/**
 * Interface copy, in both languages.
 *
 * One flat dictionary per language rather than nested namespaces: the app is a
 * single dashboard, and a flat map makes a missing key obvious at a glance when
 * the two objects are compared side by side. `Translations` is derived from the
 * English object, so TypeScript flags any key the Arabic version forgets.
 */

export const en = {
  /* Shell */
  'app.name': 'Meta Pages Publisher',
  'app.signOut': 'Sign out',
  'app.language': 'العربية',
  'app.theme.toDark': 'Dark',
  'app.theme.toLight': 'Light',

  /* Tabs */
  /* Section names for the five-destination navigation. Each is the name of a
     job to be done, not of a screen — "Publish", not "Composer". */
  /* The publish flow, whose step names are verbs: they describe what you are
     about to do, not what the screen is called. */
  'flow.chooseContent': 'Choose what to publish',
  'flow.chooseTargets': 'Choose where it goes',
  'flow.watchProgress': 'Watch it go out',
  'flow.change': 'Change',
  'flow.collapse': 'Close',
  /* The first screen after signing in. */
  'home.waitingTitle': 'Waiting for you',
  'home.waitingLabel': 'Waiting for a reply',
  'home.openInbox': 'Open the inbox',
  'home.greeting': 'Here is where things stand',
  'home.livePages': 'Pages connected',
  'home.comments': 'Comments on your posts',
  'home.viewAll': 'View all',
  'home.fromMeta': 'Counted on Facebook, not just what was sent from here',
  'home.unreadable': '{count} Page(s) did not answer — reconnect them',
  'home.publishedThisWeek': 'Published this week',
  'home.running': 'In the queue',
  'home.activityTitle': 'Posts published, last 7 days',
  'home.activityHint': 'Across all your Pages',
  'home.startTitle': 'Connect a Facebook account to begin',
  'home.startBody':
    'Nothing is connected yet. Connect the account that manages your Pages, choose which of them to work with, and everything else opens up.',
  'home.startBodyNoAccess':
    'Nothing is connected yet, and connecting an account is an administrator’s job. Ask whoever runs this account to connect it, and your work will appear here.',
  'home.startAction': 'Connect an account',
  'app.refresh': 'Reload',
  /* The analytics screen. */
  'insights.title': 'Analytics',
  'insights.refresh': 'Refresh',
  'insights.refreshing': 'Reading Meta…',
  'insights.refreshFailed': 'Could not refresh right now',
  'insights.lastDays': 'Last {count} days',
  'insights.custom': 'Custom',
  'insights.from': 'From',
  'insights.to': 'To',
  'insights.export.daily': 'Export days',
  'insights.export.pages': 'Export Pages',
  'insights.export.posts': 'Export posts',
  'insights.allPages': 'All Pages',
  'insights.posts': 'Posts',
  'insights.comments': 'Comments',
  'insights.reactions': 'Reactions',
  'insights.shares': 'Shares',
  'insights.reach': 'People reached',
  'insights.postsPerDay': 'Posts per day',
  'insights.commentsPerDay': 'Comments per day',
  'insights.reactionsPerDay': 'Reactions per day',
  'insights.otherPages': 'Other Pages',
  'insights.noData': 'Nothing recorded in this period',
  'insights.byPage': 'By Page',
  'insights.page': 'Page',
  'insights.topPosts': 'Most talked about',
  'insights.open': 'Open',
  'insights.collectedAt': 'Last collected from Facebook: {when}',
  'insights.backfilling': 'Reading history from Facebook',
  'insights.progressCount': '{done} of {total} Pages read',
  'insights.refreshHint': 'Reading {count} Pages from Facebook — this takes a few seconds',
  /* Editing a Page's own profile. */
  /* Mentions of a Page and reviews left on it. */
  /* Choosing which part of a picture Facebook keeps. */
  'cropper.zoom': 'Zoom',
  'cropper.cancel': 'Cancel',
  'cropper.use': 'Use this crop',
  'cropper.working': 'Preparing…',
  'cropper.hint.picture': 'Facebook shows profile pictures as a circle inside this square.',
  'cropper.hint.cover':
    'Facebook crops covers narrower on a phone, so keep anything important near the middle.',
  /* The inbox: everything waiting for a person, across every Page. */
  'publish.sameToOthers': 'Publish this to other Pages',
  'publish.somethingElse': 'Publish something else',
  'comments.noneStored':
    'No comments have been collected for this Page yet. Use "Check now" in the Inbox.',
  'comments.countOnPost': '{count} comments',
  'inbox.title': 'Inbox',
  'inbox.waiting': 'Waiting',
  'inbox.done': 'Handled',
  'inbox.ignored': 'No reply needed',
  'inbox.nothingIgnored': 'Nothing has been set aside.',
  'inbox.handledElsewhere': 'Answered by someone else',
  'inbox.allPages': 'All Pages',
  'inbox.allChannels': 'All channels',
  'conversations.title': 'Conversations',
  'conversations.empty': 'No conversations yet.',
  'conversations.pick': 'Pick somebody on the left.',
  'conversations.someone': 'Someone',
  'conversations.back': '← Conversations',
  'conversations.expand': 'Full screen',
  'conversations.restore': 'Exit full screen',
  'conversations.you': 'You:',
  'conversations.waitingOnly': 'Waiting ({count})',
  'conversations.waitingSince': 'Waiting since {when}',
  'conversations.thePost': 'The post',
  'conversations.more': 'More',
  'conversations.openOnFacebook': 'Open on Facebook',
  'conversations.placeholder': 'Write a reply…',
  'conversations.send': 'Send',
  'conversations.sending': 'Sending…',
  'conversations.askAssistant': 'Ask the assistant',
  'conversations.asking': 'Asking…',
  'conversations.askAnother': 'Suggest another',
  'conversations.assistantDeclined': 'The assistant did not offer a draft ({outcome}).',
  'conversations.assistantFailed': 'The assistant could not be reached: {reason}',
  'tab.conversations': 'Conversations',
  'inbox.check': 'Check now',
  'inbox.checking': 'Checking…',
  'saved.manage': 'Saved replies',
  'saved.title': 'Saved replies',
  'saved.hint':
    'The sentences you send every day, one click from the reply box. Kept for the whole account, not per Page — your opening hours do not change because the question arrived somewhere else.',
  'saved.name': 'Name (what you will see in the picker)',
  'saved.body': 'The reply',
  'saved.add': 'Save this reply',
  'saved.used': 'used {count} time(s)',
  'inbox.searchPlaceholder': 'Search messages and names',
  'inbox.search': 'Search',
  'inbox.clearSearch': 'Clear',
  'inbox.noMatches': 'Nothing matches “{query}”.',
  'inbox.showMore': 'Show older',
  'inbox.allClear': 'Nothing is waiting. Every comment has been answered or set aside.',
  'inbox.nothingDone': 'Nothing handled yet.',
  'inbox.someone': 'Someone',
  'inbox.openConversation': 'Open the whole conversation',
  'inbox.replyPlaceholder': 'Write a reply…',
  'inbox.send': 'Reply',
  'inbox.sending': 'Sending…',
  'inbox.like': 'Like',
  'inbox.react': 'React',
  'inbox.removeReaction': 'Remove reaction',
  'inbox.dismiss': 'Set aside',
  'inbox.channel.PAGE_COMMENT': 'Facebook',
  'inbox.channel.MESSENGER': 'Messenger',
  'inbox.channel.INSTAGRAM': 'Instagram',
  'inbox.channel.WHATSAPP': 'WhatsApp',
  'inbox.askAgent': 'Ask the assistant',
  'inbox.askAgain': 'Another suggestion',
  'inbox.asking': 'Asking…',
  'inbox.askAgentHint': 'The assistant drafts a reply; you decide whether to send it.',
  'inbox.fromAssistant': 'This Page’s assistant · {name}',
  'inbox.fromAgent': 'Connected agent · {name}',
  'inbox.useDraft': 'Use this',
  'inbox.declined': 'The assistant did not answer this one ({outcome}).',
  'automation.agentId': 'Agent address or id',
  'automation.agentEdit': 'Edit',
  'automation.agentEditing': 'Editing {name} — leave a secret blank to keep the stored one',
  'automation.agentSave': 'Save changes',
  'automation.agentCancel': 'Cancel',
  'automation.agentIdHint':
    'Paste the whole agent address from the platform — the id is taken out of it here, so nothing has to be cut by hand. The id on its own works too. The long secret shown once when the token was created is not an id: it goes in the token field below.',
  'inbox.unreachable': 'The assistant could not be reached: {reason}',
  'admin.renamePrompt': 'New name for this account:',
  'admin.tenantRenamed': 'Account renamed.',
  'admin.confirmDeleteTenant':
    'This deletes the account "{name}" and everything under it, permanently. Type the account name to confirm:',
  'admin.tenantDeleted': 'Account deleted.',
  'admin.confirmDeleteUser': 'Delete {email} permanently?',
  'admin.noUsersWarning': 'This account has no users, so nobody can sign into it. Add one below.',
  'admin.user': 'User',
  'admin.manageUsers': 'Users…',
  'admin.reviewerHint':
    'Sees the services under review on the Pages you assign, and nothing else — no automation, no agents, no keys, no colleagues, no plan. Assign their Pages after creating them.',
  'admin.usersOf': 'Users of',
  'admin.setPassword': 'Set password',
  'admin.newPassword': 'New password',
  'admin.passwordSet': 'Password set',
  'admin.passwordHint':
    'A password is stored hashed and is never shown again. Write it down before you leave this screen.',
  'common.close': 'Close',
  'emoji.title': 'Emoji',
  'common.active': 'Active',
  'common.inactive': 'Inactive',
  'utility.title': 'Service messages',
  'utility.intro':
    'Messenger allows free text for 24 hours after a customer writes. After that only a pre-approved service template may be sent — order and account updates, appointment reminders. No promotional content. Meta usually approves a template within seconds.',
  'utility.choosePage': 'Choose a Page',
  'utility.noTemplates': 'No templates on this Page yet.',
  'utility.newTemplate': 'New template',
  'utility.name': 'Template name',
  'utility.nameHint': 'Lowercase English letters, digits and underscores only.',
  'utility.language': 'Language',
  'utility.header': 'Heading (optional, no variables)',
  'utility.body': 'Message text',
  'utility.bodyHint': 'Write {{1}}, {{2}} where the customer details go.',
  'utility.examplePlaceholder': 'Example for {{n}}',
  'utility.create': 'Create and submit',
  'utility.creating': 'Submitting…',
  'utility.status.APPROVED': 'Approved',
  'utility.status.PENDING': 'Under review',
  'utility.status.REJECTED': 'Rejected',
  'utility.windowClosed':
    'More than 24 hours have passed since this person wrote. Messenger allows only a service template now.',
  'utility.noneApproved': 'No approved template yet — create one on the Service messages screen.',
  'utility.chooseTemplate': 'Choose a template',
  'utility.send': 'Send service message',
  'whatsapp.title': 'WhatsApp numbers',
  'whatsapp.remove': 'Disconnect',
  'whatsapp.removeConfirm':
    'Disconnect this number? Messages already in the inbox are kept. Importing the number again brings it back.',
  'whatsapp.credentialsDead': 'Token refused',
  'whatsapp.credentialsDeadHint':
    'Meta refused this number\u2019s stored token \u2014 usually because the Facebook password changed, or Facebook ended the session for security. Nothing here can revive it: import the number again with a fresh token.',
  'whatsapp.configToggle': 'Signup configuration',
  'whatsapp.configLabel': 'Embedded Signup configuration id',
  'whatsapp.configHint':
    'From developers.facebook.com \u2192 your app \u2192 Facebook Login for Business \u2192 Configurations. It must be a business access token configuration \u2014 a user access token one opens the dialog and returns no accounts. The type cannot be changed after a configuration is created; make a new one and paste its id here.',
  'whatsapp.configFromEnv':
    'Currently coming from the server environment. Saving here overrides it.',
  'whatsapp.configShared':
    'This is the shared platform app. Register your own Meta app to set your own configuration, or ask the platform administrator.',
  'whatsapp.configMissing': 'No signup configuration is set',
  'whatsapp.configMissingHint':
    'Connecting a number needs one. Open the configuration below and paste the id from Meta.',
  'whatsapp.intro':
    'A WhatsApp number belongs to a business, not to a Page — Meta records no link between them, so say which Page each number answers for. Assigning a number is also what switches delivery on.',
  'whatsapp.import': 'Add numbers',
  'whatsapp.importAction': 'Read numbers',
  'whatsapp.importing': 'Reading…',
  'whatsapp.tokenLabel': 'Access token from Meta',
  'whatsapp.tokenHint':
    'It is exchanged for a 60-day token and stored encrypted; the pasted value is never kept. Every number the token reaches will be listed.',
  'whatsapp.imported': 'Found {numbers} number(s) across {accounts} account(s).',
  'whatsapp.none': 'No WhatsApp numbers yet.',
  'whatsapp.answersFor': 'Answers for',
  'whatsapp.unassigned': 'Not assigned',
  'whatsapp.receiving': 'Receiving messages',
  'whatsapp.enable': 'Switch delivery on',
  'whatsapp.needsPage': 'Assign a Page to start receiving',
  'mentions.title': 'Mentions and reviews',
  'mentions.subtitle': 'What other people said about your Page. Read-only.',
  'mentions.selectPage': 'Page',
  'mentions.selectPagePlaceholder': 'Choose a Page…',
  'mentions.mentions': 'Posts that tagged this Page',
  'mentions.reviews': 'Recommendations',
  'mentions.noMentions': 'Nobody has tagged this Page recently.',
  'mentions.noReviews': 'No recommendations yet.',
  'mentions.someone': 'Someone',
  'mentions.open': 'Open',
  'mentions.recommends': 'Recommends',
  'mentions.doesNot': 'Does not recommend',
  'profile.location': 'Address',
  'profile.locationHint': 'Facebook uses this to place your Page on a map and in local search.',
  'profile.saveLocation': 'Save address',
  'profile.locationSaved': 'Address saved.',
  'profile.place.street': 'Street',
  'profile.place.city': 'City',
  'profile.place.state': 'Region',
  'profile.place.zip': 'Postcode',
  'profile.place.country': 'Country',
  'profile.images': 'Photos',
  'profile.picture': 'Profile picture',
  'profile.cover': 'Cover photo',
  'profile.replace': 'Replace',
  'profile.noImage': 'None set',
  'profile.hours': 'Opening hours',
  'profile.hoursHint': 'Leave a day empty to show it as closed.',
  'profile.closed': 'Mark closed',
  'profile.saveHours': 'Save hours',
  'profile.hoursSaved': 'Opening hours saved.',
  'profile.day.mon': 'Monday',
  'profile.day.tue': 'Tuesday',
  'profile.day.wed': 'Wednesday',
  'profile.day.thu': 'Thursday',
  'profile.day.fri': 'Friday',
  'profile.day.sat': 'Saturday',
  'profile.day.sun': 'Sunday',
  'profile.title': 'Page profile',
  'profile.subtitle': 'The details people see on your Page. Changes go straight to Facebook.',
  'profile.selectPage': 'Page',
  'profile.selectPagePlaceholder': 'Choose a Page…',
  'profile.noEditablePages': 'None of your connected Pages gives you permission to change it.',
  'profile.followers': '{count} followers',
  'profile.openOnFacebook': 'Open on Facebook',
  'profile.nameNote': 'The Page name is not editable here — Facebook reviews name changes itself.',
  'profile.about': 'Short description',
  'profile.description': 'Full description',
  'profile.phone': 'Phone',
  'profile.email': 'Email',
  'profile.website': 'Website',
  'profile.save': 'Save to Facebook',
  'profile.saving': 'Saving…',
  'profile.saved': 'Saved. The values below are what Facebook kept.',
  'profile.saveFailed': 'Facebook refused the change',
  'profile.readBackNote':
    'After saving, the fields are re-read from Facebook — some values are tidied up or refused, and this shows what is actually stored.',
  'nav.home': 'Home',
  'nav.pages': 'Pages',
  'nav.publish': 'Publish',
  'nav.studio': 'Studio',
  'nav.engage': 'Engage',
  'nav.activity': 'Activity',
  'nav.account': 'Account',
  'nav.settings': 'Settings',
  'tab.connections': 'Connections',
  'tab.pages': 'Pages',
  'tab.source': 'Source',
  'tab.publish': 'Publish',
  'tab.comments': 'Comments',
  'tab.automation': 'Automation',
  'tab.published': 'Published',
  'tab.jobs': 'Operations',
  'tab.logs': 'Logs',

  /* Selection bar */
  'selection.label': 'Selected',
  'selection.clear': 'Clear',

  /* Login */
  'login.title': 'Sign in',
  'login.subtitle':
    'This is the dashboard account, not your Facebook account. You connect Meta after signing in.',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.showPassword': 'Show',
  'login.hidePassword': 'Hide',
  'login.submit': 'Sign in',
  'login.submitting': 'Signing in…',
  'mail.tab': 'Email',
  'mail.hint':
    'How this system sends verification codes and account notices. Settings here override the environment.',
  'mail.settings': 'SMTP settings',
  'mail.host': 'Server',
  'mail.port': 'Port',
  'mail.username': 'Username',
  'mail.password': 'Password',
  'mail.passwordStored': 'one is stored; leave empty to keep it',
  'mail.appPasswordHint':
    'For Gmail this is an app password, not your account password: turn on 2-Step Verification, then create one at myaccount.google.com/apppasswords. It can send mail and nothing else, and revoking it does not affect your sign-in.',
  'mail.fromAddress': 'From address',
  'mail.fromName': 'From name',
  'mail.active': 'Send email (off means codes are only written to the log)',
  'mail.saved': 'Settings saved. Send a test message to prove they work.',
  'mail.test': 'Send a test message',
  'mail.testHint':
    'Saved settings are unproven settings. A wrong password fails silently until somebody is locked out.',
  'mail.sendTest': 'Send test',
  'mail.sending': 'Sending…',
  'mail.testOk': 'Delivered. Check that inbox.',
  'mail.testFailed': 'Not delivered:',
  'mail.lastTestOk': 'last test delivered',
  'mail.unprovenTitle': 'No test message has been delivered yet',
  'mail.unprovenBody':
    'Password resets and sign-up confirmations both depend on email. Until a test message arrives, assume none of them work.',
  'bindings.tab': 'Pages',
  'bindings.hint':
    'One agent per Page. A Page about a single product has a single body of knowledge, and an agent asked to answer for every Page at once answers each of them worse.',
  'bindings.agent': 'Answering agent',
  'bindings.chooseAgent': 'Choose an agent…',
  'bindings.bind': 'Bind',
  'bindings.rebind': 'Change agent',
  'bindings.unbind': 'Unbind',
  'bindings.bound': 'Bound',
  'bindings.unbound': 'No agent',
  'bindings.saved': 'Saved.',
  'bindings.removed': 'Agent unbound from this Page.',
  'bindings.noAgents': 'Add an agent first, then bind it to a Page.',
  'bindings.noPages': 'No Pages yet. Connect a Meta account first.',
  'bindings.cannotComment':
    'Meta did not grant MODERATE on this Page, so replies cannot be published here.',
  'bindings.notSubscribed': 'Not receiving comments',
  'bindings.autoReply': 'Send incoming comments to this agent',
  'bindings.autoPublish': 'Publish its replies without review',
  'bindings.autoPublishHint':
    'With this off, every reply waits in the review queue for a person. Leave it off until you have read enough of this agent\u2019s answers to trust them in public.',
  'bindings.autoDraft': 'Draft an answer for everything that arrives',
  'bindings.autoDraftHint':
    'Writes a suggestion for every new comment and message, for a person to read and send. Nothing goes out on its own, and this switch does not depend on the two above. It uses your monthly agent allowance.',
  'menu.account': 'Account',
  'menu.plan': 'Plan',
  'menu.environment': 'Environment',
  'menu.build': 'Build',
  'menu.role': 'Your role',
  'login.forgot': 'Forgot your password?',
  'login.newAccount': 'Request an account',

  'forgot.title': 'Reset your password',
  'forgot.subtitle': 'We will email a six-digit code to the address on your account.',
  'forgot.sendCode': 'Send code',
  'forgot.resend': 'Resend',
  'forgot.code': 'Verification code',
  'forgot.codeSent': 'Code sent. Check your inbox — it is valid for a few minutes.',
  'forgot.newPassword': 'New password',
  'forgot.confirmPassword': 'Confirm new password',
  'forgot.mismatch': 'The two passwords do not match.',
  'forgot.submit': 'Set new password',
  'forgot.done': 'Password changed. Signing you back in…',
  'forgot.backToLogin': 'Back to sign in',

  'signup.title': 'Request an account',
  'signup.subtitle': 'Tell us who you are. An administrator reviews every request.',
  'signup.organisation': 'Organisation name',
  'signup.contactName': 'Your name',
  'signup.phone': 'Mobile number',
  'signup.sendCode': 'Send confirmation code',
  'signup.code': 'Confirmation code',
  'signup.codeSent': 'Code sent to your email.',
  'signup.submit': 'Submit request',
  'signup.pendingTitle': 'Request received',
  'signup.pendingBody':
    'Your email is confirmed and your request is with an administrator. We will write to you at that address with the decision.',

  'account.changePassword': 'Change password',
  'account.currentPassword': 'Current password',
  'account.newPassword': 'New password',
  'account.passwordChanged': 'Password changed.',
  'account.resetUserPassword': 'Set a new password',
  'account.emailHint':
    'Reset codes go to this address. An address nobody can read locks the account out for good.',
  'account.leaveBlank': 'Leave blank to keep the current password',
  'common.edit': 'Edit',

  'admin.signups': 'Requests',
  'admin.signupsHint':
    'People who asked for an account and confirmed their email. Approving one creates the tenant and its first administrator, and emails them a temporary password.',
  'admin.signup.organisation': 'Organisation',
  'admin.signup.contact': 'Contact',
  'admin.signup.phone': 'Phone',
  'admin.signup.requested': 'Requested',
  'admin.signup.approve': 'Approve',
  'admin.signup.reject': 'Reject',
  'admin.signup.note': 'Internal note (not shown to the applicant)',
  'admin.signup.none': 'No requests waiting.',
  'admin.signup.approved': 'Approved. The account details were emailed.',
  'admin.signup.rejected': 'Rejected.',
  'admin.signup.choosePlan': 'Plan for this account',
  'admin.signup.history': 'Decided',
  'login.failed': 'Sign-in failed',

  /* Common */
  'common.loading': 'Loading…',
  'common.none': 'None',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.open': 'Open',
  'common.delete': 'Delete',
  'common.cancel': 'Cancel',
  'common.dismiss': 'Dismiss',
  'common.retry': 'Retry',
  'common.save': 'Save',
  'common.saving': 'Saving…',
  'common.remove': 'Remove',
  'common.select': 'Select',
  'common.selected': 'Selected',
  'common.noText': '(no text)',
  'common.never': 'Never',
  'common.requestFailed': 'Request failed',
  'common.page': 'Page',
  'common.status': 'Status',
  'common.date': 'Date',
  'common.type': 'Type',
  'common.media': 'Media',
  'common.link': 'Link',
  'common.actions': 'Actions',
  'common.all': 'All',

  /* Connections */
  'connections.title': 'Connections',
  'connections.connect': 'Connect a Meta account',
  'connections.connectAnother': 'Connect another account',
  'connections.allowance': '{used} of {limit} Meta accounts connected. You can add more.',
  'connections.atLimit':
    'This plan allows {limit} connected Meta accounts. Disconnect one, or upgrade, to add another.',
  'connections.empty': 'No Meta account is connected yet.',
  'connections.graphVersion': 'Graph API',
  'connections.publicSource': 'Public-Page source',
  'connections.enabled': 'enabled',
  'connections.disabled': 'disabled',
  'connections.requestedScopes': 'Requested permissions',
  'connections.pages': 'Pages',
  'connections.tokenStored': 'Token stored',
  'connections.tokenEncrypted': 'yes (encrypted)',
  'connections.tokenExpires': 'Token expires',
  'connections.noExpiry': 'no expiry recorded',
  'connections.lastVerified': 'Last verified',
  'connections.verify': 'Verify token',
  'connections.disconnect': 'Disconnect',
  'connections.declined': 'Declined permissions',
  'connections.notGranted': 'Not granted',
  'connections.pendingApproval': 'Awaiting Meta’s approval (reconnecting will not change it)',
  'connections.confirmDisconnect': 'Disconnect and destroy all stored tokens for this account?',
  'connections.disconnected': 'Disconnected and all stored tokens destroyed.',
  'connections.connected': 'Connected. {count} Page(s) discovered.',
  'connections.connectedDeclined':
    'Connected, but these permissions were declined: {list}. Publishing will fail until they are granted.',
  'connections.tokenValid': 'Token is valid',
  'connections.tokenValidUntil': 'Token is valid until {date}',
  'connections.publicSourceNotice':
    'Reading posts from public Pages you do not manage requires Meta’s Page Public Content Access feature (App Review + Business Verification). Until it is approved, only Pages you manage can be used as a source.',

  /* Pages */
  'pages.title': 'Pages',
  'pages.sync': 'Re-sync from Meta',
  'pages.syncing': 'Syncing…',
  'pages.subtitle':
    'The Pages the connected account manages, exactly as Meta reports them. Publishing requires the CREATE_CONTENT task.',
  'pages.empty': 'No Pages found. Connect a Meta account, then re-sync.',
  'pages.pageId': 'Page ID',
  'pages.category': 'Category',
  'health.title': 'What is connected',
  'health.summary': '{working} of {total} channels working',
  'health.working': 'Working',
  'health.blocker.NONE': 'Working',
  'health.blocker.DISABLED': 'Not enabled on this installation',
  'health.blocker.NOT_GRANTED':
    'Permission not granted — reconnect and accept it. If it stays this way, Meta has not approved it for this app yet',
  'health.blocker.PENDING_APPROVAL':
    'Awaiting Meta’s approval — nothing to do, reconnecting will not change it',
  'health.blocker.SETUP': 'Setup not finished',
  'health.notLive':
    '{count} Page(s) are not receiving live updates. Comments on them appear only when you press Check.',
  'health.subscribeAll': 'Switch live updates on for all Pages',
  'health.subscribing': 'Subscribing…',
  'health.subscribed': 'Switched on for {count} Page(s); {already} were already on.',
  'health.skipped': '{count} Page(s) skipped — the permission for webhooks was not granted.',
  'health.unverified':
    '{count} WhatsApp number(s) have not been checked against Meta since they were set up.',
  /* Named while it can still be renewed. Reconnecting keeps the Pages, the
     ledger and the inbox — only the credentials change — so the warning says
     what it costs to act, not just that something is wrong. */
  'health.tokenExpiring':
    '{account}: the connection expires in {days} day(s). {pages} Page(s) stop when it does.',
  'health.tokenExpired':
    '{account}: the connection expired {days} day(s) ago. {pages} Page(s) have stopped.',
  'health.reconnect': 'Reconnect the account',
  'whatsapp.connectNumber': 'Connect a number',
  'whatsapp.connecting': 'Opening Meta…',
  'whatsapp.signedUp': 'Connected',
  'whatsapp.signedUpCoexist':
    'Connected, and still live in the WhatsApp Business app on your phone —',
  'whatsapp.coexistence': 'Shared with the WhatsApp Business app',
  'whatsapp.check': 'Check',
  'whatsapp.checking': 'Checking…',
  'whatsapp.checkOk': 'Meta confirms this number is subscribed.',
  'whatsapp.checkBad': 'Meta does not confirm this number',
  'channel.FACEBOOK': 'Facebook Page',
  'channel.INSTAGRAM': 'Instagram',
  'channel.WHATSAPP': 'WhatsApp',
  'channel.MESSENGER': 'Messenger',
  'channel.INSTAGRAM_DM': 'Instagram messages',
  'channel.WEBHOOKS': 'Live updates',
  'channel.state.ACTIVE': 'Working',
  'channel.state.NOT_LINKED': 'Nothing connected yet',
  'channel.state.NOT_GRANTED':
    'Permission not granted — reconnect and accept it. If it stays this way, Meta has not approved it for this app yet',
  'channel.state.PENDING_APPROVAL':
    'Awaiting Meta’s approval — nothing to do, reconnecting will not change it',
  'channel.state.OFF': 'Not enabled on this installation',
  'pages.instagram': 'Instagram',
  'pages.noInstagram': 'No Instagram account linked',
  'pages.tasks': 'Tasks',
  'pages.canPublish': 'Can publish',
  'pages.canComment': 'Can comment',
  'pages.token': 'Token',
  'pages.tokenMissing': 'missing',
  'pages.tokenInvalidated': 'missing (invalidated)',

  /* Source */
  'source.title': 'Source',
  'source.mode.owned': 'My Pages',
  'source.mode.public': 'Public Page',
  'source.mode.compose': 'Compose new',
  'source.selectPage': 'Source Page',
  'source.selectPagePlaceholder': 'Select a Page you manage…',
  'source.loadingPosts': 'Loading posts…',
  'source.postCount': '{count} post(s)',
  'source.addPublic': 'Add source',
  'source.publicPlaceholder': 'facebook.com/SomePage, @SomePage, or a Page ID',
  'source.registeredPublic': 'Registered public sources',
  'source.selectPublicPlaceholder': 'Select a public Page…',
  'source.openInFacebook': 'Open in Facebook',
  'source.notReproducible':
    'Contains {types} — the Graph API does not expose this in a re-publishable form.',
  'source.notReproducibleHint':
    'The Graph API does not expose this media in a re-publishable form. Use “Compose new” instead.',

  /* Composer */
  'composer.postType': 'Post type',
  'composer.kind.TEXT': 'Text post',
  'composer.kind.LINK': 'Link post',
  'composer.kind.PHOTO': 'Photo',
  'composer.kind.MULTI_PHOTO': 'Multiple photos',
  'composer.kind.VIDEO': 'Video',
  'composer.kind.REEL': 'Reel (vertical video, 3-90s)',
  'composer.reelHint': 'One vertical video (9:16, at least 540x960), 3 to 90 seconds. Meta decides if the file fits and says so if not.',
  'composer.alsoAs': 'Also publish as',
  'composer.alsoAs.story': 'Story',
  'composer.alsoAs.reel': 'Reel',
  'composer.alsoAs.hint': 'Story needs exactly one photo or video and no schedule; Reel needs exactly one video. Each goes out separately, so one failing does not stop the post.',
  'jobs.variant.STORY': 'Story',
  'jobs.variant.REEL': 'Reel',
  'composer.kind.PHOTO_STORY': 'Photo story',
  'composer.kind.VIDEO_STORY': 'Video story (max 60s)',
  'composer.text': 'Text',
  'composer.textPlaceholder': 'What do you want to publish?',
  'composer.link': 'Link',
  'composer.photos': 'Photos (2–10)',
  'composer.photo': 'Photo',
  'composer.videoFile': 'Video file',
  'composer.firstComment': 'First comment (optional)',
  'composer.firstCommentPlaceholder': 'Posted as a comment under the new post',
  'composer.publishNow': 'Publish now',
  'composer.scheduleLater': 'Schedule for later',
  'composer.scheduleIn': 'Will publish in {when}',
  'composer.scheduleTooSoon': 'Must be at least 10 minutes from now (Meta requirement)',
  'composer.scheduleTooLate': 'Must be within 75 days from now (Meta requirement)',
  'composer.scheduleInvalid': 'Invalid date',
  'composer.storyNotice':
    'The Page Stories API supports no caption, no scheduling and no comments. Meta also rejects media already used in a published post, so a fresh upload is required.',
  'composer.submit': 'Use this content',
  'composer.saving': 'Saving…',
  'composer.uploadFailed': 'Upload failed',
  'composer.saveFailed': 'Could not save the composition',
  'composer.attached': 'Attached',

  /* Publish */
  'publish.postTitle': 'Post',
  'publish.targetsTitle': 'Targets',
  'publish.nothingSelected':
    'Nothing is selected. Go to Source, pick a post or compose new content, then come back here.',
  'publish.targetsHint': 'Only Pages where Meta reports the CREATE_CONTENT task can be selected.',
  'publish.source': 'Source',
  'publish.reference': 'Reference',
  'publish.preview': 'Preview',
  'publish.allowRepublish': 'Publish again even if this content already went to a selected Page',
  'publish.allowRepublishHint': '(overrides the duplicate guard; audit-logged)',
  'publish.button': 'Publish to {count} Page(s)',
  'publish.queueing': 'Queueing…',
  'publish.queued': 'Queued {count} target(s).',
  'publish.scheduled': 'Scheduled {count} target(s) for {when}.',
  /* Empty means now, which is what Publish has always done. The hint says so
     rather than leaving somebody wondering whether the field is required. */
  'publish.scheduleLabel': 'Publish later (optional)',
  'jobs.edit.message': 'Post text',
  'jobs.edit.link': 'Link (optional)',
  'jobs.edit.firstComment': 'First comment (optional)',
  'jobs.edit.media': 'Images and video',
  'jobs.edit.uploading': 'Uploading…',
  'jobs.edit.at': 'Goes out at',
  'jobs.edit.noText': 'This republishes an existing post, so there is no text of ours to change.',
  'jobs.edit.thenRetry':
    'Save your changes, then press Retry — the corrected post goes out straight away.',
  'jobs.edit.partialWarning':
    'This post already reached some of its pages. Those keep the version they received; only the pages that failed get the corrected one.',
  'jobs.edit.targetsFixed': 'To change which pages this goes to, cancel it and schedule it again.',
  'publish.scheduleHint': 'Leave empty to publish immediately.',
  'publish.scheduleSet': 'Will go out at {when}, your local time.',
  'publish.cancelScheduled': 'Cancel',
  'publish.cancelled': 'Cancelled before publishing.',
  'publish.alsoInstagram': 'Also publish to Instagram',
  'publish.instagramPickFirst': 'Choose Pages first.',
  'publish.instagramNone': 'None of the chosen Pages has a linked Instagram account.',
  'publish.instagramReach': '{count} of {total} chosen Page(s) have one.',
  'jobs.goesOut': 'goes out {date}',
  'publish.skipped': 'Skipped {count}: {reasons}',
  'publish.failed': 'Publishing failed',
  'publish.blockedNoToken': 'no Page token stored',
  'publish.blockedNoTask': 'missing CREATE_CONTENT task',

  /* Jobs */
  'jobs.title': 'Jobs',
  'jobs.empty': 'No jobs yet.',
  'jobs.view.scheduled': 'Scheduled',
  'jobs.view.publishing': 'Going out now',
  'jobs.view.published': 'Published',
  'jobs.view.failed': 'Needs attention',
  'jobs.view.cancelled': 'Cancelled',
  'jobs.empty.scheduled': 'Nothing is waiting to go out.',
  'jobs.empty.publishing': 'Nothing is going out at this moment.',
  'jobs.empty.published': 'Nothing has been published from here yet.',
  'jobs.empty.failed': 'Nothing needs your attention.',
  'jobs.empty.cancelled': 'Nothing was called back.',
  'jobs.hide': 'Hide',
  'jobs.reveal': 'Show again',
  'jobs.hidden': 'Hidden',
  'jobs.filter.goesOutFrom': 'Goes out from',
  'jobs.filter.madeFrom': 'Made from',
  'jobs.filter.to': 'To',
  'jobs.filter.sortDefault': 'Usual order',
  'jobs.filter.sortAsc': 'Oldest first',
  'jobs.filter.sortDesc': 'Newest first',
  'jobs.filter.clear': 'Clear filters',
  'jobs.showHidden': 'Show hidden',
  'jobs.showAttempts': 'Show attempts',
  'jobs.hideAttempts': 'Hide attempts',
  'jobs.retryFailed': 'Retry failed targets',
  'jobs.created': 'created {date}',
  'jobs.targetPage': 'Target Page',
  'jobs.channel.FACEBOOK': 'Facebook',
  'jobs.channel.INSTAGRAM': 'Instagram',
  'jobs.channel.LINKEDIN': 'LinkedIn',
  'social.apps.network': 'Network',
  'social.setting.LINKEDIN.organizations': 'Company pages are approved for this app (Community Management API)',
  'networks.title': 'Network definitions',
  'networks.hint': 'Every network other than Meta is a definition. Edit one, or add a network from an existing one or from scratch: no code change and no deploy. The server refuses a definition that reaches an address it does not declare.',
  'networks.source.PRESET': 'Built in',
  'networks.source.OVERRIDE': 'Built in, edited here',
  'networks.source.CUSTOM': 'Added here',
  'networks.verifiedLive': 'tested against the real network',
  'networks.notVerified': 'not yet tested against the real network',
  'networks.edit': 'Edit definition',
  'networks.add': 'Add a network…',
  'networks.fromPreset': 'Add a network',
  'networks.blank': 'An empty definition',
  'networks.key': 'Key (capital letters, e.g. X or TIKTOK)',
  'networks.name': 'Name',
  'networks.contract': 'Definition (JSON)',
  'networks.validate': 'Check',
  'networks.valid': 'The definition is valid.',
  'networks.save': 'Save',
  'networks.saved': 'Saved. It is in use now.',
  'networks.disable': 'Disable',
  'networks.enable': 'Enable',
  'networks.reset': 'Back to the built-in one',
  'networks.delete': 'Delete',
  'networks.badJson': 'That is not valid JSON.',
  'networks.confirmDelete': 'Remove this stored definition?',
  'source.origin.PLATFORM': 'Published from here',
  'source.origin.NETWORK': 'On the account',
  'source.profileLimit': "LinkedIn does not let apps read a personal profile's posts, so only what was published from here is listed. A company page can show all of its posts once its app is approved for company pages.",
  'source.refresh': 'Refresh',
  'source.confirmDeleteSocial': 'Delete this post from the account? This cannot be undone.',
  'source.deleted': 'Post deleted.',
  'source.saved': 'Post updated.',
  'source.socialNote': 'Posts published to this account from here. A LinkedIn post cannot be reused as a source.',
  'source.socialEmpty': 'Nothing has been published to this account from here yet.',
  'home.revokedTitle': '{count} Page(s) need to be reconnected',
  'home.revokedBody': 'Meta withdrew access, so these cannot be read or posted to until the account is connected again: {names}',
  'home.revokedAction': 'Reconnect',
  'social.account.ACCOUNT': 'Account',
  'publish.destinationsLocked': 'Choose the content first. These are the places it can go.',
  'conversations.allChannels': 'All channels',
  'dest.search': 'Search…',
  'dest.none': 'Nothing to choose from.',
  'dest.close': 'Close',
  'dest.selectAll': 'Select all',
  'dest.clearAll': 'Clear',
  'dest.selected': '{count} selected',
  'dest.unavailable': 'Not available ({count})',
  'dest.unsupported': '{networks}: not offered here.',
  'dest.reason.INACTIVE': 'No longer connected',
  'dest.reason.NO_TOKEN': 'Access token missing or revoked',
  'dest.reason.NO_TASK': 'Your role on this Page does not allow this',
  'dest.reason.RECONNECT': 'Reconnect it first',
  'channelName.FACEBOOK': 'Facebook',
  'channelName.INSTAGRAM': 'Instagram',
  'channelName.LINKEDIN': 'LinkedIn',
  'channelName.WHATSAPP': 'WhatsApp',
  'social.title': 'Social accounts',
  'social.hint': 'LinkedIn today; more networks use the same screen as they are added.',
  'social.provider.LINKEDIN': 'LinkedIn',
  'social.connect': 'Connect {provider}',
  'social.connectAnother': 'Connect another {provider} account',
  'social.reconnect': 'Reconnect',
  'social.notSetUp': '{provider} is not set up yet. Add the app credentials below first.',
  'social.direct.hint': 'Separate several with commas or new lines. Add the bot to each one as an administrator first.',
  'social.direct.submit': 'Connect',
  'social.empty': 'No social account is connected yet.',
  'social.connected': '{provider} connected: {count} account(s) found.',
  'social.accounts': 'Publishing accounts',
  'social.account.PERSON': 'Personal profile',
  'social.account.ORGANIZATION': 'Company page',
  'social.inactive': 'Not available',
  'social.renews': 'Renews automatically',
  'social.reconnectWhenExpired': 'Reconnect when it expires',
  'social.apps.title': 'App credentials',
  'social.apps.hint':
    'Create a developer app at the provider, then paste its Client ID and secret here. They are stored encrypted, and the secret is never shown again.',
  'social.apps.redirect': 'Redirect URL to register at the provider',
  'social.apps.add': 'Register a {provider} app',
  'social.apps.label': 'Name',
  'social.apps.clientId': 'Client ID',
  'social.apps.clientSecret': 'Client secret',
  'social.apps.keepSecret': 'Leave empty to keep the stored secret',
  'social.apps.secretStored': 'Secret stored ({fingerprint})',
  'social.apps.apiVersion': 'API version (optional, e.g. 202603)',
  'social.apps.organizations': 'Company pages are approved for this app (Community Management API)',
  'social.apps.shared': 'Shared platform app',
  'social.apps.registerShared': 'Register as the shared app for every tenant',
  'social.apps.own': 'This tenant',
  'social.apps.save': 'Save',
  'social.apps.saved': 'Saved.',
  'social.apps.delete': 'Delete',
  'social.apps.confirmDelete': 'Delete this app registration?',
  'social.apps.disable': 'Disable',
  'social.apps.enable': 'Enable',
  'social.apps.connections': '{count} connection(s)',
  'publish.socialTitle': 'Social accounts',
  'publish.socialHint': 'LinkedIn profiles and pages you have connected.',
  'publish.socialUnavailable': 'Reconnect it first',
  'jobs.attempts': 'Attempts',
  'jobs.http': 'HTTP',
  'jobs.metaCode': 'Meta code',
  'jobs.message': 'Message',
  'jobs.postId': 'Post ID',
  'jobs.resolve': 'Resolve',
  'jobs.resolvePrompt':
    'Check the Page. If the post is there, paste its post ID. If nothing was published, leave this empty.',
  'jobs.attemptNumber': '#',
  'jobs.result': 'Result',
  'jobs.endpoint': 'Endpoint',
  'jobs.retryable': 'Retryable',
  'jobs.when': 'When',

  /* Published */
  'published.title': 'Published posts',
  'published.subtitle':
    'What this system published. Deletion works on posts the system created — posts written on Facebook directly must be deleted there.',
  'published.empty': 'No posts yet.',
  'published.post': 'Post',
  'published.why': 'Why this reach?',
  'published.detailsFailed': 'Could not read this post from Meta',
  'published.onFacebook': 'On Facebook since',
  'published.audience': 'Audience',
  'published.publishedBy': 'Published by',
  'published.stillOnPage': 'On the Page timeline',
  'published.hiddenFromTimeline': 'Hidden',
  'published.nothingRestricting':
    'Meta reports nothing restricting this post. Everyone who follows the Page may see it — but Facebook does not show every post to every follower, so a person who has not seen it is usually distribution, not a setting.',
  'published.finding.NOT_PUBLISHED':
    'Not published yet — Meta still holds this post as unpublished, so it is not on the Page.',
  'published.finding.HIDDEN_FROM_TIMELINE':
    'Hidden from the Page timeline. It stays reachable by its direct link, which is why some people see it and others do not. Someone hid it on Facebook, or a moderation rule did — the app never does.',
  'published.finding.EXPIRED': 'This post carried an expiry, and it has passed.',
  'published.finding.AUDIENCE_LIMITED':
    'Meta reports an audience limit on this post. Anyone outside it cannot see the post at all. Nothing in this app sets one.',
  'published.finding.RESTRICTED_PRIVACY': 'The audience is not public.',
  'published.finding.TARGETING_UNREADABLE':
    'Meta would not tell us whether an audience limit is set on this post, so that one question is unanswered here.',
  'published.publishedAt': 'Published',
  'published.confirmDelete':
    'Permanently delete this post from “{page}”?\n\n{id}\n\nThis cannot be undone.',
  'published.deleteFailed': 'Could not delete',
  'published.like': 'Like',
  'published.unlike': 'Unlike',
  'published.likeHint':
    'Places a plain Like as the Page. Meta gives no way to choose a reaction (love, wow, …) through the API.',
  'published.likeFailed': 'Could not like this post',
  'published.unlikeFailed': 'Could not remove the Like',

  /* Comments */
  'comments.title': 'Comments',
  'comments.subtitle':
    'Publish comments as the Page, on posts belonging to Pages you manage. Requires the MODERATE task.',
  'comments.selectPage': 'Page',
  'comments.selectPagePlaceholder': 'Select a Page…',
  'comments.noModerate': 'None of your Pages grant the MODERATE task, which Meta requires.',
  'comments.selectPost': 'Post to comment on',
  'comments.selectPostPlaceholder': 'Select a post…',
  'comments.drafts': 'Comments to publish',
  'comments.placeholder': 'Your comment…',
  'comments.followUp': 'Follow-up comment {n}',
  'comments.addAnother': 'Add another comment',
  'comments.pacing':
    '{count} comments will publish in order, about {seconds}s apart. Posting them at once looks like automated activity to Meta.',
  'comments.publish': 'Publish {count} comment(s)',
  'comments.queued':
    'Queued {count} comment(s) on “{page}”. Spaced {seconds}s apart, finishing in about {minutes} minute(s).',
  'comments.replyTo': 'Reply to an existing comment (optional)',
  'comments.replyToPost': 'Comment directly on the post',
  'comments.yourPage': 'your Page',
  'comments.repliesDisabled':
    'Replying to comments left by other people needs the pages_read_user_content permission and its own App Review approval. Until then you can add your own comments to the post.',

  /* Automation */
  'automation.title': 'Automation',
  'automation.tab.queue': 'Review queue',
  'automation.tab.agents': 'Agents',
  'automation.tab.performance': 'Performance',
  'automation.queueEmpty': 'Nothing waiting for review.',
  'automation.queueHint':
    'Nothing here has been published. Approve to publish the reply as the Page, or dismiss to leave the comment unanswered.',
  'automation.proposedReply': 'Proposed reply',
  'automation.confidence': 'confidence',
  'automation.noReply': 'The agent produced no reply. Dismiss, or answer on Facebook directly.',
  'automation.approve': 'Approve and publish',
  'automation.approveEdited': 'Publish edited reply',
  'automation.dismiss': 'Dismiss',
  'automation.disabledNotice':
    'Comment automation is off. Enable it in configuration and reconnect each Meta account — subscribing a Page to webhooks needs the pages_manage_metadata permission.',
  'automation.agentName': 'Name',
  'automation.agentMode': 'Mode',
  'automation.agentEndpoint': 'Endpoint',
  'automation.agentCredential': 'Credential',
  'automation.agentCallbackSecret': 'Callback secret',
  'automation.agentStored': 'stored (encrypted)',
  'automation.addAgent': 'Add an agent',
  'automation.modeSyncLabel': 'SYNC — answer in the same response',
  'automation.modeAsyncLabel': 'ASYNC — call us back when ready',
  'automation.modeHint':
    'Both are supported so you can compare them on the Performance tab and keep whichever suits your agent.',
  'automation.bearerToken': 'Bearer token (optional)',
  'automation.bearerHint': 'Encrypted at rest and never shown again.',
  'automation.agentKey': 'Agent / knowledge-base key (optional)',
  'automation.callbackSecret': 'Callback secret (required for ASYNC)',
  'automation.callbackSecretHint':
    'Your agent signs its callback body with this using HMAC-SHA256 and sends it as X-Signature-256.',
  'automation.callbackUrl': 'Callback URL',
  'automation.saveAgent': 'Save agent',
  'automation.perfWindow':
    'Last {days} days. A decline counts as a correct outcome — an agent knowing it should not answer is not a failure.',
  'automation.perfEmpty': 'No agent calls in the last 30 days yet.',
  'automation.calls': 'Calls',
  'automation.answered': 'Answered',
  'automation.declined': 'Declined',
  'automation.empty': 'Empty',
  'automation.timedOut': 'Timed out',
  'automation.failedCount': 'Failed',
  'automation.successRate': 'Success rate',
  'automation.avgMs': 'Avg ms',
  'automation.p95Ms': 'p95 ms',
  'automation.avgReply': 'Avg reply',

  /* Logs */
  'logs.title': 'Logs',
  'logs.tab.calls': 'Graph API calls',
  'logs.tab.audit': 'Audit trail',
  'logs.redactionNotice':
    'Access tokens are never written to these tables. Endpoints are recorded with secret parameters stripped.',
  'logs.rateUsage': 'Meta rate-limit usage — calls {calls}%, CPU {cpu}%, time {time}%',
  'logs.ratePaused': ' — publishing is paused until this falls',
  'logs.failuresOnly': 'Show failures only',
  'logs.method': 'Method',
  'logs.endpoint': 'Endpoint',
  'logs.duration': 'ms',
  'logs.actor': 'Actor',
  'logs.action': 'Action',
  'logs.entity': 'Entity',
  'logs.detail': 'Detail',

  /* Admin console */
  'admin.title': 'Platform administration',
  'admin.tab.permissions': 'Meta permissions',
  'admin.permissions.intro':
    'Only a published permission is asked of a customer. The rest are offered to platform staff and the Meta reviewer, because asking a customer for one Meta has not approved can make Meta refuse the whole login dialog. Change a status once you have acted on the verdict from Meta — nothing here updates itself.',
  'admin.permissions.scope': 'Permission',
  'admin.permissions.channel': 'Channel',
  'admin.permissions.status': 'Status',
  'admin.permissions.lastCustomer': 'Last granted to a customer',
  'admin.permissions.neverCustomer': 'Never',
  'admin.permissions.note': 'Note',
  'admin.permissions.IN_DEVELOPMENT': 'In development',
  'admin.permissions.PENDING_APPROVAL': 'Awaiting Meta',
  'admin.permissions.PUBLISHED': 'Approved and published',
  'admin.tab.dashboard': 'Dashboard',
  'admin.tab.tenants': 'Accounts',
  'admin.tab.plans': 'Plans',
  'admin.superAdminOnly': 'This area requires a super-administrator.',
  'admin.active': 'active',
  'admin.suspended': 'suspended',
  'admin.stat.tenants': 'Accounts',
  'admin.stat.users': 'Users',
  'admin.stat.connections': 'Meta connections',
  'admin.stat.pages': 'Pages',
  'admin.stat.posts': 'Live posts',
  'admin.stat.agentCalls': 'Agent calls (month)',
  'admin.stat.replies': 'Auto replies (month)',
  'admin.stat.jobs': 'Jobs',
  'admin.planDistribution': 'Accounts per plan',
  'admin.plan': 'Plan',
  'admin.tenant': 'Account',
  'admin.tenantsOnPlan': 'accounts',
  'admin.newTenant': 'Create an account',
  'admin.tenantName': 'Account name',
  'admin.tenantSlug': 'Identifier',
  'admin.slugHint': 'Lowercase letters, digits and hyphens. Cannot be changed later.',
  'admin.selectPlan': 'Select a plan…',
  'admin.createTenant': 'Create account',
  'admin.tenantCreated': 'Account created.',
  'admin.planChanged': 'Plan changed.',
  'admin.statusChanged': 'Status changed.',
  'admin.suspend': 'Suspend',
  'admin.reactivate': 'Reactivate',
  'admin.suspendPrompt': 'Why is this account being suspended? Shown to its users.',
  'admin.plansHint':
    'Limits are numeric quotas; entitlements decide which features the plan sells. A feature also needs the installation to support it — see the Account page.',
  'admin.planSaved': 'Plan saved.',
  'admin.entitlements': 'Included features',
  'admin.price': 'Price / month',
  'admin.limit.maxMetaAccounts': 'Meta accounts',
  'admin.limit.maxPages': 'Pages',
  'admin.limit.maxUsers': 'Users',
  'admin.limit.maxAgentCallsPerMonth': 'Agent calls / month',
  'admin.limit.maxAutoRepliesPerMonth': 'Auto replies / month',
  'admin.feature.featurePublishing': 'Publishing',
  'admin.feature.featureComments': 'Comments',
  'admin.feature.featureCommentReplies': 'Replying to visitors',
  'admin.feature.featureAutomation': 'AI automation',
  'admin.feature.featurePublicSource': 'Public-Page sources',
  'admin.feature.featureApiAccess': 'API access',

  /* Account */
  'account.title': 'Account',
  'account.tab.overview': 'Overview',
  'account.tab.me': 'My account',
  'account.tab.users': 'Users',
  'account.tab.keys': 'API keys',
  'account.currentPlan': 'Current plan',
  'account.usage': 'Usage',
  'account.usage.metaAccounts': 'Meta accounts',
  'account.usage.pages': 'Pages',
  'account.usage.users': 'Users',
  'account.usage.agentCalls': 'Agent calls this month',
  'account.usage.autoReplies': 'Auto replies this month',
  'account.usage.metaApps': 'Your own Meta apps',
  'admin.unlimited': 'Unlimited',
  'account.usage.postsPerDay': 'Posts today',
  'account.usage.commentsPerDay': 'Comments today',
  'account.period.DAY': '(per day)',
  'account.period.MONTH': '(per month)',
  'account.quotaWarning': 'Approaching a limit: {list}',
  'account.features': 'Features',
  'account.featuresHint':
    'A feature works only when all three layers allow it: the installation can do it, your plan includes it, and you have switched it on.',
  'account.feature': 'Feature',
  'account.layer.capability': 'Installation',
  'account.layer.entitlement': 'Plan',
  'account.layer.setting': 'Your setting',
  'account.blockedBy.CAPABILITY': 'Not available',
  'account.blockedBy.ENTITLEMENT': 'Not in your plan',
  'account.blockedBy.SETTING': 'Switched off',
  'account.blockedBy.UNRELEASED': 'Coming soon',
  'account.featureName.PUBLISHING': 'Publishing posts',
  'account.featureName.COMMENTS': 'Publishing comments',
  'account.featureName.COMMENT_REPLIES': 'Replying to visitors',
  'account.featureName.AUTOMATION': 'AI automation',
  'account.featureName.PUBLIC_SOURCE': 'Public-Page sources',
  'account.featureName.API_ACCESS': 'API access',
  'account.featureName.OWN_META_APP': 'Your own Meta app',
  'account.user': 'User',
  'account.role': 'Role',
  'account.role.ADMIN': 'Administrator',
  'account.role.SUPERADMIN': 'Platform administrator',
  'account.role.OPERATOR': 'Operator',
  'account.role.PUBLISHER': 'Publisher',
  'account.role.REVIEWER': 'Meta reviewer',
  'account.role.VIEWER': 'Viewer',
  'account.pages.assign': 'Pages',
  'account.pages.hint':
    'This person sees only the pages ticked here — their inbox, comments and conversations.',
  'account.pages.saved': 'Pages updated.',
  'account.pages.empty': 'No page ticked: this person will see nothing.',
  'account.pages.none': 'No page is connected to this account yet.',
  'account.activeUser': 'Active',
  'account.inactiveUser': 'Inactive',
  'account.activate': 'Activate',
  'account.deactivate': 'Deactivate',
  'account.addUser': 'Add a user',
  'account.displayName': 'Name',
  'account.passwordHint': 'At least 12 characters.',
  'account.userCreated': 'User created.',
  'account.userUpdated': 'User updated.',
  'account.adminOnly': 'This area requires an administrator.',
  'account.keysHint':
    'For calling this system from your own software. The key is shown once and stored only as a hash.',
  'account.keyName': 'Key name',
  'account.createKey': 'Create key',
  'account.keyIssued': 'Your new API key',
  'account.keyMatrix': 'What this key may do',
  'account.keyMatrixHint':
    'A key can do nothing until you say so here. Tick a capability on a Page and the key gets it on that Page only — nothing else on the account is reachable with it.',
  'account.keyAllPages': 'Every Page',
  'account.keyAllPagesHint':
    'Including Pages you connect later. Choose this for an agent you set up once and leave running.',
  'account.keyCap.PAGES_READ': 'List Pages',
  'account.keyCap.PUBLISH': 'Publish posts',
  'account.keyCap.INBOX_READ': 'Read the inbox',
  'account.keyCap.INBOX_REPLY': 'Answer the inbox',
  'account.keyCap.INSIGHTS_READ': 'Read insights',
  'account.keyCap.POSTS_READ': 'List published posts',
  'account.keyCap.GENERATION_WRITE': 'Generate content (studio)',
  'account.keyAutoSend': 'Send replies without review',
  'account.keyAutoSendHint':
    'Off means an answer is saved as a draft in the inbox and a person presses send. On means the agent’s words reach the customer unread.',
  'account.keyExpiry': 'Expires after (days)',
  'account.keyExpiryNever': 'Never',
  'account.keyNoGrants': 'Tick at least one capability. A key with none can do nothing.',
  'account.keyNoPages': 'Connect a Page first. There is nothing to grant a key yet.',
  'account.keyGrantsColumn': 'Access',
  'account.keyGrantCount': '{count} on {pages}',
  'account.keyExpired': 'Expired',
  'account.keyDocs': 'How an outside agent uses this',
  'account.keyExpiresColumn': 'Expires',
  'account.keyNoExpiry': 'Never',
  'account.keyShow': 'Show key',
  'account.keyShown': 'This key, and the connector address built from it',
  'account.keyValue': 'The key — send it as: Authorization: Bearer …',
  'account.keyConnectorUrl':
    'Connector URL — paste into Claude: Customize \u2192 Connectors \u2192 Add custom connector',
  'account.keyConnectorHint':
    'Leave the OAuth fields empty. The address is the credential, so it grants exactly this key\u2019s matrix and nothing more \u2014 revoking the key stops it at once.',
  'account.tab.apps': 'Meta app',
  'account.appsHint':
    'By default every connection goes through the platform\u2019s Meta app. Registering your own gives you your own App Review, your own rate limit, and your own Business Verification.',
  'account.appsUsingPlatform':
    'Connections currently go through the platform\u2019s Meta app. Nothing needs doing unless you want your own.',
  'account.addApp': 'Register an app',
  'account.addAppHint':
    'From the Meta app dashboard: Settings \u2192 Basic. The secret is stored encrypted and never shown again. Paste the callback URL shown above into the app\u2019s webhook configuration.',
  'account.appLabel': 'A name you will recognise',
  'account.appId': 'App ID',
  'account.appSecret': 'App secret',
  'account.appLoginConfig': 'Login configuration id (optional)',
  'account.appVerified': 'Verified with Meta',
  'account.appInvalid': 'Meta refused these credentials',
  'account.appVerifying': 'Checking with Meta\u2026',
  'account.appDefault': 'Default',
  'account.appConnections': 'Connections',
  'account.appInUse':
    'Disconnect its Meta accounts first \u2014 their tokens stop working when the app goes.',
  'account.keyOnce': 'Copy it now — it cannot be shown again.',
  'account.keyPrefix': 'Prefix',
  'account.lastUsed': 'Last used',
  'account.revoke': 'Revoke',
  'account.revoked': 'Revoked',

  /* Extra tabs */
  'tab.account': 'Account',
  'tab.admin': 'Admin',
  'tab.studio': 'Studio',

  /* Content generation studio */
  'studio.title': 'Content Studio',
  'studio.tab.compose': 'Compose',
  'studio.tab.library': 'Archive',
  /* Usage and cost statistics. */
  'studio.tab.usage': 'Usage & cost',
  'usage.title': 'Usage & cost',
  'usage.estimateNote':
    'Every figure here is an estimate from the prices set on each connector, not an invoice.',
  'usage.totalCost': 'Estimated cost',
  'usage.jobsDone': 'Jobs finished',
  'usage.jobsFailed': 'Failed (no cost recorded)',
  'usage.unpriced': 'Not priced',
  'usage.unpricedHint':
    '{count} finished job(s) have no price, so they are missing from the cost. Set a price on their connector under Automation, Studio connectors, Advanced; later jobs will then be counted.',
  'usage.allPriced': 'Every finished job has a price.',
  'usage.testCost': 'Connector tests (billed to you, kept apart): {cost} across {count} run(s)',
  'usage.notPriced': 'Not priced',
  'usage.partial': '+{count} not priced',
  'usage.chartTitle.day': 'Cost per day',
  'usage.chartTitle.week': 'Cost per week',
  'usage.chartTitle.month': 'Cost per month',
  'usage.chartEmpty': 'Nothing was spent in this period.',
  'usage.chartAria': 'Bar chart of estimated cost over time, highest bar {max}',
  'usage.bar': '{day}: {cost}, {count} job(s)',
  'usage.byConnector': 'By connector',
  'usage.byProvider': 'By provider',
  'usage.byDestination': 'By page or network',
  'usage.byProject': 'Most expensive projects',
  'usage.col.name': 'Name',
  'usage.col.provider': 'Provider',
  'usage.col.kind': 'Type',
  'usage.col.jobs': 'Jobs',
  'usage.col.failed': 'Failed',
  'usage.col.cost': 'Cost',
  'usage.col.used': 'Consumed',
  'usage.col.projects': 'Projects',
  'usage.col.network': 'Network',
  'usage.unit.seconds': '{value} s',
  'usage.unit.images': '{value} image(s)',
  'usage.unit.chars': '{value} chars',
  'usage.unit.tokens': '{input} in / {output} out tokens',
  'usage.attribution':
    'A project published to several pages or networks is counted under each of them, so these rows add up to more than the total. Spend on projects not published anywhere: {cost}.',
  'usage.destinationsEmpty': 'No project made in this period has been published yet.',
  'usage.empty': 'Nothing was produced in this period.',
  'studio.cost.estimated': 'Cost {cost} (estimated)',
  'studio.cost.partial': 'Cost {cost}+ (estimated; some steps are not priced)',
  'studio.cost.unpriced': 'Cost: not priced',
  'studioConnectors.pricing.title': 'Price, for the cost statistics',
  'studioConnectors.pricing.hint':
    'What the provider charges, so the studio can estimate the cost of each result. Leave it empty if you do not know: the statistics then say "not priced" instead of guessing. Changing it never needs a new test.',
  'studioConnectors.pricing.unit': 'Charged per',
  'studioConnectors.pricing.unit.none': 'No price',
  'studioConnectors.pricing.unit.SECOND': 'Second of video',
  'studioConnectors.pricing.unit.IMAGE': 'Image',
  'studioConnectors.pricing.unit.CHARS_10K': 'Ten thousand characters',
  'studioConnectors.pricing.unit.TOKENS_1M': 'Million tokens',
  'studioConnectors.pricing.unit.REQUEST': 'Request',
  'studioConnectors.pricing.price': 'Price in USD',
  'studioConnectors.pricing.inputPrice': 'Input, USD per million tokens',
  'studioConnectors.pricing.outputPrice': 'Output, USD per million tokens',
  'studioConnectors.pricing.soundPrice': 'With sound, USD per second (optional)',
  'studioConnectors.pricing.byResolution': 'Per second by resolution (optional; overrides the price above)',
  'studioConnectors.pricing.invalid': 'Prices must be numbers, zero or more.',
  'studioConnectors.pricing.usageTitle': 'Where the reply reports what was used',
  'studioConnectors.pricing.usageHint':
    'Optional paths into the provider’s reply, written like the response path. When present, real token counts or seconds are used instead of estimates. Leave them empty if unsure.',
  'studioConnectors.pricing.usageInput': 'Input tokens path',
  'studioConnectors.pricing.usageOutput': 'Output tokens path',
  'studioConnectors.pricing.usageSeconds': 'Seconds path',
  'studioConnectors.pricing.tag.SECOND': '/s',
  'studioConnectors.pricing.tag.IMAGE': '/image',
  'studioConnectors.pricing.tag.CHARS_10K': '/10k chars',
  'studioConnectors.pricing.tag.TOKENS_1M': ' per 1M tokens',
  'studioConnectors.pricing.tag.REQUEST': '/request',
  'studio.idea': 'Your idea',
  'studio.ideaPlaceholder': 'Describe what you want made — the more specific, the better the script.',
  'studio.attachments': 'Reference files',
  'studio.uploading': 'Uploading…',
  'studio.attachmentsCapped': 'Your plan limits how many reference files one request may carry.',
  'studio.uploadFailed': 'Could not upload that file',
  'studio.pasteScriptToggle': 'I already have a script — let me paste it in',
  'studio.pastedScript': 'Your script',
  'studio.pastedScriptPlaceholder': 'Paste the script here',
  'studio.generateScript': 'Generate script',
  'studio.scriptResult': 'Script',
  'studio.audioSection': 'Audio',
  'studio.generateAudio': 'Generate audio',
  'studio.audioResult': 'Audio',
  'studio.videoSection': 'Video or image',
  'studio.includeAudio': 'Include the generated audio in the video',
  'studio.generateVideo': 'Generate video',
  'studio.generateImage': 'Generate image',
  'studio.videoResult': 'Video',
  'studio.imageResult': 'Image',
  'studio.jobRunning': 'Working on it…',
  'studio.jobFailed': 'The connector could not produce this',
  'studio.previousFeedback': 'Requested change: {text}',
  'studio.approve': 'Approve',
  'studio.reviseWithFeedback': 'Revise with feedback',
  'studio.sendToPublish': 'Send to Publish',
  'studio.copyLink': 'Copy link',
  'studio.download': 'Download',
  'studio.linkReady': 'Link copied — valid for 24 hours:',
  'studio.feedbackPlaceholder': 'What should change?',
  'studio.submitRevision': 'Submit',
  'studio.libraryEmpty': 'Nothing generated yet — start from the Compose tab.',
  'studio.capability.SCRIPT': 'Script',
  'studio.capability.AUDIO': 'Audio',
  'studio.capability.VIDEO': 'Video',
  'studio.capability.IMAGE': 'Image',
  'studio.revisionEntry': '{date} — {feedback}',
  /* One request and everything made from it. */
  'studio.project.savedNote': 'This request is saved as a project in the Archive.',
  'studio.project.newRequest': 'New request',
  'studio.project.loadMore': 'Load more',
  'studio.project.inProgress': 'In progress',
  'studio.project.published': 'Published',
  'studio.project.open': 'Open',
  'studio.project.count.text': '{count} text',
  'studio.project.count.image': '{count} image',
  'studio.project.count.audio': '{count} audio',
  'studio.project.count.video': '{count} video',
  'studio.project.back': 'Back to the archive',
  'studio.project.archive': 'Archive',
  'studio.project.archiveConfirm': 'Archive this project? It leaves the list; the files themselves are not deleted.',
  'studio.project.request': 'The request',
  'studio.project.outputs': 'Results',
  'studio.project.noOutputs': 'Nothing has been made for this request yet.',
  'studio.project.textHeading': 'Text',
  'studio.project.imageHeading': 'Images',
  'studio.project.audioHeading': 'Audio',
  'studio.project.videoHeading': 'Videos',
  'studio.project.plansHeading': 'Longer videos',
  'studio.project.useText': 'Use this text in the post',
  'studio.project.useMedia': 'Include this in the post',
  'studio.project.noText': 'No text',
  'studio.project.audioNotSent': 'Audio cannot be posted on its own; it is used inside a video.',
  'studio.project.earlierVersions': 'Earlier versions ({count})',
  'studio.project.readyTitle': 'What will be sent',
  'studio.project.sendText': 'Text',
  'studio.project.sendMedia': 'Files',
  'studio.project.noMedia': 'No files',
  'studio.project.kind.image': 'Image',
  'studio.project.kind.video': 'Video',
  'studio.project.kind.audio': 'Audio',
  'studio.project.postRule': 'A post carries photos or a single video, never both. Tick what should go out.',
  'studio.project.publishing': 'Publishing',
  'studio.project.notPublished': 'Not published yet.',
  'studio.project.mode.NEW_POST': 'New post',
  'studio.project.mode.ATTACHED': 'Attached to a scheduled post',
  'studio.project.seeInPosts': 'See scheduled posts',
  /* Goal-first: start from the end product, then tune the choices. */
  'studio.mode.goal': 'Guided',
  'studio.mode.free': 'Free mode',
  'studio.mode.freeHint': 'Free mode: write, voice and film one step at a time, with no goal.',
  'studio.goal.title': 'What do you want to make?',
  'studio.goal.POST': 'Post',
  'studio.goal.POST.desc': 'Text ready to publish on your networks. It can come with a picture or a video.',
  'studio.goal.IMAGE_PROMPT': 'Image prompt',
  'studio.goal.IMAGE_PROMPT.desc': 'A precise description to hand to an image model.',
  'studio.goal.VIDEO_FROM_TEXT': 'Video script and prompt from text',
  'studio.goal.VIDEO_FROM_TEXT.desc': 'A script and scene prompts for a video made from your words alone.',
  'studio.goal.VIDEO_FROM_IMAGE': 'Video script and prompt from image(s)',
  'studio.goal.VIDEO_FROM_IMAGE.desc': 'A script that brings the picture or pictures you attach to life.',
  'studio.goal.AUDIO_TEXT': 'Text to turn into speech',
  'studio.goal.AUDIO_TEXT.desc': 'Text written to be read aloud, with diacritics and a dialect.',
  'studio.followUp.label': 'Make it come with',
  'studio.followUp.hint': 'The picture or video is planned in the same request, from the same idea.',
  'studio.followUp.NONE': 'Nothing',
  'studio.followUp.IMAGE': 'Image',
  'studio.followUp.VIDEO_FROM_TEXT': 'Video from text',
  'studio.followUp.VIDEO_FROM_IMAGE': 'Video from image',
  'studio.goal.requestLabel': 'Your request',
  'studio.goal.requestPlaceholder': 'Say what you want and who it is for — the more specific, the better.',
  'studio.att.title': 'References',
  'studio.att.hint': 'Upload a file or paste a public https link, and say what each one is for.',
  'studio.att.upload': 'Upload a file',
  'studio.att.urlPlaceholder': 'https://…',
  'studio.att.addUrl': 'Add link',
  'studio.att.urlInvalid': 'The link must be a public https address.',
  'studio.att.role': 'What is it for?',
  'studio.att.link': 'Link',
  'studio.role.LOGO': 'Logo',
  'studio.role.CHARACTER': 'Character',
  'studio.role.PRODUCT': 'Product',
  'studio.role.FIRST_FRAME': 'First frame',
  'studio.role.LAST_FRAME': 'Last frame',
  'studio.role.STYLE': 'Style',
  'studio.role.KNOWLEDGE': 'Background knowledge',
  'studio.role.REFERENCE': 'General reference',
  'studio.opt.title': 'Choices',
  'studio.opt.hint': 'Each choice is already set to a sensible default. Change only what matters.',
  'studio.opt.group.language': 'Language, dialect and instructions',
  'studio.opt.group.textOnMedia': 'Text on the image or video',
  'studio.opt.group.post': 'The post',
  'studio.opt.group.image': 'The image',
  'studio.opt.group.video': 'The video',
  'studio.opt.group.audio': 'The voice',
  'studio.opt.language': 'Language',
  'studio.opt.language.hint': 'The language of the finished text.',
  'studio.opt.language.ar': 'Arabic',
  'studio.opt.language.en': 'English',
  'studio.opt.dialect': 'Dialect',
  'studio.opt.dialect.hint': 'Applies to Arabic. Modern Standard is formal; the others sound like that region.',
  'studio.opt.dialect.MSA': 'Modern Standard Arabic',
  'studio.opt.dialect.SAUDI_NAJDI': 'Saudi (Najdi)',
  'studio.opt.dialect.SAUDI_HIJAZI': 'Saudi (Hijazi)',
  'studio.opt.dialect.GULF': 'Gulf',
  'studio.opt.dialect.EGYPTIAN': 'Egyptian',
  'studio.opt.dialect.LEVANTINE': 'Levantine',
  'studio.opt.dialect.MAGHREBI': 'Maghrebi',
  'studio.opt.diacritics': 'Arabic diacritics (tashkeel)',
  'studio.opt.diacritics.hint': 'On words that will be spoken, so the voice reads them correctly.',
  'studio.opt.diacritics.NONE': 'None',
  'studio.opt.diacritics.PARTIAL': 'Partial — only ambiguous words',
  'studio.opt.diacritics.FULL': 'Full',
  'studio.opt.extra': 'Extra instructions',
  'studio.opt.extra.hint': 'Anything else the writer should respect: names to avoid, a slogan, a rule.',
  'studio.opt.textOnMedia': 'Words in the picture',
  'studio.opt.textOnMedia.hint':
    'Whether words appear inside the image or video itself. Models often misspell Arabic, so say it explicitly.',
  'studio.opt.textOnMedia.NONE': 'No text in the picture',
  'studio.opt.textOnMedia.EXACT': 'These exact words',
  'studio.opt.textOnMedia.TITLE': 'A short title only',
  'studio.opt.textOnMedia.text': 'The words',
  'studio.opt.textOnMedia.text.hint': 'Written into the picture exactly as typed.',
  'studio.opt.post.length': 'Length',
  'studio.opt.post.length.hint': 'How long the post should be.',
  'studio.opt.post.length.SHORT': 'Short',
  'studio.opt.post.length.MEDIUM': 'Medium',
  'studio.opt.post.length.LONG': 'Long',
  'studio.opt.post.tone': 'Tone',
  'studio.opt.post.tone.hint': 'How the post sounds to the reader.',
  'studio.opt.post.tone.FORMAL': 'Formal',
  'studio.opt.post.tone.FRIENDLY': 'Friendly',
  'studio.opt.post.tone.EXCITED': 'Excited',
  'studio.opt.post.tone.PERSUASIVE': 'Persuasive',
  'studio.opt.post.tone.INFORMATIVE': 'Informative',
  'studio.opt.post.hashtags': 'Add hashtags',
  'studio.opt.post.hashtags.hint': 'A few relevant tags at the end.',
  'studio.opt.post.emojis': 'Use emojis',
  'studio.opt.post.emojis.hint': 'A light touch, not in every line.',
  'studio.opt.post.callToAction': 'End with a call to action',
  'studio.opt.post.callToAction.hint': 'Tell the reader what to do next.',
  'studio.opt.aspect': 'Shape (aspect ratio)',
  'studio.opt.aspect.hint': 'Pick the shape of the network you will post to.',
  'studio.opt.aspect.9:16': '9:16 — vertical (reels, stories)',
  'studio.opt.aspect.16:9': '16:9 — wide (YouTube, web)',
  'studio.opt.aspect.1:1': '1:1 — square',
  'studio.opt.aspect.4:5': '4:5 — portrait feed',
  'studio.opt.aspect.3:4': '3:4 — portrait',
  'studio.opt.aspect.4:3': '4:3 — landscape',
  'studio.opt.quality': 'Quality',
  'studio.opt.quality.hint': 'Higher quality takes longer and costs more.',
  'studio.opt.quality.DRAFT': 'Draft — fast and cheap',
  'studio.opt.quality.STANDARD': 'Standard',
  'studio.opt.quality.HIGH': 'High',
  'studio.opt.image.style': 'Style',
  'studio.opt.image.style.hint': 'For example: photo-realistic, flat illustration, warm and bright.',
  'studio.opt.video.duration': 'Duration (seconds)',
  'studio.opt.video.duration.hint': 'The total length. A longer video is built from several clips.',
  'studio.opt.video.scenes': 'Number of scenes',
  'studio.opt.video.scenes.hint': 'Leave empty to let the writer decide.',
  'studio.opt.video.characterConsistency': 'Keep characters consistent',
  'studio.opt.video.characterConsistency.hint': 'Each character is described once and repeated in every scene.',
  'studio.opt.video.sceneContinuity': 'Scenes continue one another',
  'studio.opt.video.sceneContinuity.hint': 'Each scene starts where the previous one ended.',
  'studio.opt.video.transitions': 'Transitions',
  'studio.opt.video.transitions.hint': 'How one scene gives way to the next.',
  'studio.opt.video.transitions.AUTO': 'Automatic',
  'studio.opt.video.transitions.CUT': 'Straight cut',
  'studio.opt.video.transitions.FADE': 'Fade',
  'studio.opt.video.transitions.DISSOLVE': 'Dissolve',
  'studio.opt.video.pace': 'Pace',
  'studio.opt.video.pace.hint': 'How quickly the scenes move.',
  'studio.opt.video.pace.SLOW': 'Slow',
  'studio.opt.video.pace.NORMAL': 'Normal',
  'studio.opt.video.pace.FAST': 'Fast',
  'studio.opt.video.keepScriptOrder': 'Keep the script in order',
  'studio.opt.video.keepScriptOrder.hint': 'Spoken lines and on-screen text follow your order, scene by scene.',
  'studio.opt.video.sound': 'Sound',
  'studio.opt.video.sound.hint': 'The background sound, apart from the voice.',
  'studio.opt.video.sound.NONE': 'None',
  'studio.opt.video.sound.AMBIENT': 'Ambient sound',
  'studio.opt.video.sound.MUSIC': 'Music',
  'studio.opt.video.sound.AMBIENT_MUSIC': 'Ambient sound and music',
  'studio.opt.audio.gender': 'Voice',
  'studio.opt.audio.gender.hint': 'A male or female voice, or either.',
  'studio.opt.audio.gender.ANY': 'Either',
  'studio.opt.audio.gender.MALE': 'Male',
  'studio.opt.audio.gender.FEMALE': 'Female',
  'studio.opt.audio.voice': 'Voice id (optional)',
  'studio.opt.audio.voice.hint': 'The provider’s own voice name. When filled, it wins over the choice above.',
  'studio.opt.audio.speed': 'Speed',
  'studio.opt.audio.speed.hint': '1 is normal speech; lower is slower.',
  'studio.opt.audio.tone': 'Tone of voice',
  'studio.opt.audio.tone.hint': 'The feeling of the delivery.',
  'studio.opt.audio.tone.NEUTRAL': 'Neutral',
  'studio.opt.audio.tone.WARM': 'Warm',
  'studio.opt.audio.tone.ENERGETIC': 'Energetic',
  'studio.opt.audio.tone.CALM': 'Calm',
  'studio.opt.audio.tone.SERIOUS': 'Serious',
  'studio.preset.label': 'Saved choices',
  'studio.preset.none': 'Start from the defaults',
  'studio.preset.save': 'Save these choices',
  'studio.preset.namePrompt': 'Name for this set of choices',
  'studio.preset.delete': 'Delete',
  'studio.preset.deleteConfirm': 'Delete this saved set of choices?',
  'studio.preset.saved': 'Saved.',
  'studio.route.title': 'What will run',
  'studio.route.checking': 'Checking which connectors would be used…',
  'studio.route.uses': 'Will use: {name}',
  'studio.route.missing': 'No connector for this step — {reason}',
  'studio.route.missingNoReason': 'No connector is set up for this step.',
  'studio.route.fix': 'Open the connector settings',
  'studio.route.failed': 'Could not check the connectors. You can still start.',
  'studio.step.POST_TEXT': 'Post text',
  'studio.step.IMAGE_PROMPT': 'Image prompt',
  'studio.step.VIDEO_SCRIPT': 'Video script and prompt',
  'studio.step.AUDIO_TEXT': 'Text for speech',
  'studio.step.IMAGE': 'Image generation',
  'studio.step.VIDEO': 'Video generation',
  'studio.step.AUDIO': 'Voice generation',
  'studio.create.start': 'Start',
  'studio.create.starting': 'Starting…',
  'studio.problem.REQUEST_EMPTY': 'Write your request first.',
  'studio.problem.UPLOADING': 'Wait for the upload to finish.',
  'studio.problem.BAD_URL': 'One of the links is not a valid https address.',
  'studio.problem.NEEDS_IMAGE': 'This goal needs at least one image: upload one or paste a link.',
  'studio.problem.EXACT_TEXT_MISSING': 'Type the exact words that should appear in the picture.',
  'studio.project.goal': 'Goal',
  'studio.project.postTextHeading': 'Post text',
  'studio.project.promptHeading': 'Prompt for the media',
  'studio.produce.title': 'Next step',
  'studio.produce.hint': 'Built from the prompt above. Approve or revise the prompt first if it is not right.',
  'studio.produce.IMAGE': 'Create the image',
  'studio.produce.VIDEO': 'Create the video',
  'studio.produce.AUDIO': 'Create the audio',
  'studio.produce.connector': 'Connector',
  'studio.produce.auto': 'Automatic — the platform chooses',
  'studio.produce.working': 'Starting…',
  'studio.produce.videoCost': 'Video is billed by the provider, per second of footage.',
  'studio.produce.started': 'Started — the result will appear in the results below.',
  /* Where a connector is used (slots). */
  'studioConnectors.slot.title': 'Where this connector is used',
  'studioConnectors.slot.hint':
    'Several connectors of the same kind can each be the default for a different use. Leave a list empty to mean any.',
  'studioConnectors.slot.purposes': 'Writing purposes',
  'studioConnectors.slot.purpose.POST_TEXT': 'Posts',
  'studioConnectors.slot.purpose.IMAGE_PROMPT': 'Image prompts',
  'studioConnectors.slot.purpose.VIDEO_SCRIPT': 'Video scripts',
  'studioConnectors.slot.purpose.AUDIO_TEXT': 'Text for speech',
  'studioConnectors.slot.videoInputs': 'Video input',
  'studioConnectors.slot.videoInput.TEXT': 'From text',
  'studioConnectors.slot.videoInput.IMAGE': 'From image',
  'studioConnectors.slot.aspects': 'Aspect ratios',
  'studioConnectors.slot.quality': 'Quality tier',
  'studioConnectors.slot.qualityAny': 'Any',
  'studioConnectors.slot.maxClip': 'Longest single clip (seconds)',
  'studioConnectors.slot.maxClipHint': 'Leave empty if unknown. A longer video is split into clips of this length.',
  'studioConnectors.slot.extension': 'Can extend a previous clip, so a longer film is a chain',
  'studioConnectors.tag.maxClip': 'up to {seconds}s',
  'studioConnectors.tag.extension': 'extends clips',
  'studioConnectors.slot.none': 'Any use',
  /* Publish from a result or a project: a new post, or add to a scheduled one. */
  'studio.publish.button': 'Publish',
  'studio.publish.newPost': 'New post',
  'studio.publish.newPostHint': 'Open the composer with the text and files filled in.',
  'studio.publish.attach': 'Attach to a scheduled post',
  'studio.publish.attachHint': 'Add the files to a post that is already scheduled.',
  'studio.publish.attachNeedsMedia': 'Needs an image or video.',
  'studio.attach.title': 'Choose a scheduled post',
  'studio.attach.videoRule': 'A video can be attached only to a post that has no other files, and it must be the only file.',
  'studio.attach.none': 'No posts are scheduled.',
  'studio.attach.mediaCount': '{count} file(s)',
  'studio.attach.notEditable': 'This post republishes someone else’s and cannot be changed.',
  'studio.attach.block.NOTHING_SELECTED': 'Nothing to attach.',
  'studio.attach.block.POST_HAS_VIDEO': 'This post already has a video; nothing can be added.',
  'studio.attach.block.VIDEO_NEEDS_EMPTY_POST': 'A video needs a post with no other files.',
  'studio.attach.block.TOO_MANY': 'A post holds at most 10 files.',
  'studio.attach.confirm': 'Add {count} file(s) to this scheduled post?',
  'studio.attach.here': 'Attach here',
  'studio.attach.done': 'Added to the scheduled post.',
  'studio.attach.notRecorded': 'The files were added, but the project could not be told. It will not list this post.',
  'studio.attach.openScheduled': 'Open scheduled posts',
  'flow.projectRecordFailed': 'The post was queued, but the studio project could not be updated with it.',
  /* A longer video: a script cut into shots, run one paid call at a time. */
  'videoPlan.open': 'Plan a longer video',
  'videoPlan.intro':
    'Video models make clips of a few seconds. A longer video is a plan: the script is cut into shots, you review every shot’s description, and then each shot is generated in turn — each one a paid call.',
  'videoPlan.targetSeconds': 'Target length (seconds)',
  'videoPlan.shotEstimate': '≈ {count} shot(s)',
  'videoPlan.pauseBetweenShots': 'Pause after each shot so I can review it before continuing',
  'videoPlan.referenceNote': 'The first shot uses the attached reference images ({count} of up to 3).',
  'videoPlan.referenceNoteNone': 'Attach reference images above if you want the first shot to start from them.',
  'videoPlan.create': 'Write the shot list',
  'videoPlan.creating': 'Writing the shot list… this takes up to half a minute.',
  'videoPlan.cancel': 'Cancel',
  'videoPlan.title': 'Video plan',
  'videoPlan.summary': '{seconds} s · {count} shot(s)',
  'videoPlan.status.DRAFT': 'Draft',
  'videoPlan.status.RUNNING': 'Running',
  'videoPlan.status.PAUSED': 'Paused',
  'videoPlan.status.DONE': 'Done',
  'videoPlan.status.FAILED': 'Failed',
  'videoPlan.shotStatus.PENDING': 'Waiting',
  'videoPlan.shotStatus.RUNNING': 'Generating',
  'videoPlan.shotStatus.DONE': 'Done',
  'videoPlan.shotStatus.FAILED': 'Failed',
  'videoPlan.shotStatus.APPROVED': 'Done',
  'videoPlan.shot': 'Shot {number}',
  'videoPlan.timeRange': '{from}–{to} s',
  'videoPlan.scriptExcerpt': 'Script lines this shot covers',
  'videoPlan.visualPrompt': 'What the camera sees',
  'videoPlan.visualPromptHint':
    'In English, by design — video models read English best. Setting, subject, action, camera, lighting, mood.',
  'videoPlan.editingLocked': 'The plan is running; editing opens again when it pauses or finishes.',
  'videoPlan.unsavedChanges': 'You have unsaved changes to the shots.',
  'videoPlan.saveShots': 'Save shots',
  'videoPlan.saveNote':
    'Changing a shot resets it and every shot after it: each later shot extends the one before.',
  'videoPlan.discardChanges': 'Discard changes',
  'videoPlan.addShot': 'Add a shot at the end',
  'videoPlan.removeShot': 'Remove this shot',
  'videoPlan.approveAndStart': 'Approve and start',
  'videoPlan.confirmStart':
    'Start generating? Each shot is a separate paid call to your video connector — {count} shot(s) in this plan. By default it pauses after each finished shot so you can review it.',
  'videoPlan.continue': 'Continue with the next shot',
  'videoPlan.confirmContinue': 'Generate the next shot? This is a paid call.',
  'videoPlan.runningNow': 'Generating shot {number} of {count}… this can take a few minutes.',
  'videoPlan.pausedHint': 'Watch the video so far. Continue, or redo the last shot with a new description.',
  'videoPlan.doneHint': 'The whole video is ready.',
  'videoPlan.videoSoFar': 'Video so far',
  'videoPlan.finalVideo': 'Final video',
  'videoPlan.redoShot': 'Redo this shot',
  'videoPlan.redoHint': 'Describe the shot again; it will be generated afresh, along with every shot after it.',
  'videoPlan.redoAndRun': 'Redo and generate',
  'videoPlan.fixAndRetry': 'Fix and retry',
  'videoPlan.fixHint': 'Edit the description of the failed shot, then try again. This is a paid call.',
  'videoPlan.retry': 'Try again',
  'videoPlan.noVideoYet': 'No video yet — approve the plan to start.',
  'videoPlan.copyLink': 'Copy link',
  'videoPlan.libraryTitle': 'Video plans',
  'videoPlan.libraryEmpty': 'No video plans yet — plan one from the Compose tab.',
  'videoPlan.openPlan': 'Open',
  'videoPlan.backToList': 'Back to the list',
  'automation.tab.studioConnectors': 'Studio connectors',
  'studioConnectors.hint':
    'A connector is saved as a draft and is only used once a test run has produced a result. The Studio picks among the tested connectors of a kind by what each one says it is for (see “Where this connector is used”), falling back to the one marked default.',
  'studioConnectors.name': 'Name',
  'studioConnectors.capability': 'Capability',
  'studioConnectors.provider': 'Provider',
  'studioConnectors.endpoint': 'Endpoint URL',
  'studioConnectors.token': 'Auth token',
  'studioConnectors.default': 'Default',
  'studioConnectors.active': 'Active',
  'studioConnectors.inactive': 'Disabled',
  'studioConnectors.add': 'Add a connector',
  'studioConnectors.tokenHint':
    'Leave blank when editing to keep the token already stored — it is shown once and never read back.',
  'studioConnectors.setDefault': 'Use as the default for this capability',
  'studioConnectors.saveConnector': 'Save connector',
  'studioConnectors.capability.SCRIPT': 'Script',
  'studioConnectors.capability.AUDIO': 'Audio',
  'studioConnectors.capability.VIDEO': 'Video',
  'studioConnectors.capability.IMAGE': 'Image',
  'studioConnectors.model': 'Model',
  'studioConnectors.modelHint':
    'The model id exactly as the provider writes it (for ElevenLabs, the voice id). Providers retire model ids often — if generation suddenly fails with "not found", put the current id here.',
  'studioConnectors.preset': 'Start from',
  'studioConnectors.presetCustom': 'Custom — start empty',
  'studioConnectors.presetHint':
    'Pick a provider from the list and every field is filled in for you. Choose "Custom" only for a provider that is not listed.',
  'studioConnectors.advanced': 'Advanced — request & response',
  'studioConnectors.advancedHint':
    'How the request is built and where the result is read from. A preset fills all of this; change it only if the provider’s documentation says otherwise.',
  'studioConnectors.authStyle': 'Where the token goes',
  'studioConnectors.authStyle.BEARER': 'Authorization: Bearer',
  'studioConnectors.authStyle.HEADER': 'Custom header',
  'studioConnectors.authStyle.QUERY': 'Query parameter',
  'studioConnectors.authStyle.NONE': 'None',
  'studioConnectors.headerName': 'Header name',
  'studioConnectors.queryName': 'Query parameter name',
  'studioConnectors.template': 'Request body (JSON)',
  'studioConnectors.templateHint':
    'Leave empty to use this platform’s own contract. Otherwise, the JSON sent to the provider, with the placeholders below.',
  'studioConnectors.templateInvalid': 'This is not valid JSON yet: {error}',
  'studioConnectors.templateNotObject': 'The request body must be a JSON object: { … }',
  'studioConnectors.placeholders': 'Placeholders you can use in the body and in the endpoint URL:',
  'studioConnectors.placeholder.prompt': 'the idea, the script, or the image/video description',
  'studioConnectors.placeholder.model': 'the Model field above',
  'studioConnectors.placeholder.feedback': 'your revision notes (empty on a first attempt)',
  'studioConnectors.placeholder.audioUrl':
    'link to the generated audio a video should use (may be empty)',
  'studioConnectors.placeholder.references': 'links to the attached reference files, as a list',
  'studioConnectors.placeholder.item': 'the current reference, inside a $each block only',
  'studioConnectors.placeholderEach':
    'To send one entry per attached reference, use $each — for example:',
  'studioConnectors.placeholderEmpty':
    'A field whose value comes out empty is left out of the request entirely.',
  'studioConnectors.responseKind': 'What the provider returns',
  'studioConnectors.responseKind.TEXT': 'Text',
  'studioConnectors.responseKind.URL': 'A link to the file',
  'studioConnectors.responseKind.BASE64': 'The file inside the JSON (base64)',
  'studioConnectors.responseKind.BINARY': 'The file itself (no JSON)',
  'studioConnectors.responseKind.legacy': 'This platform’s own contract',
  'studioConnectors.responsePath': 'Where the result is in the response',
  'studioConnectors.responsePathHint':
    'A path into the JSON reply, e.g. candidates[0].content.parts[*].text, data[0].b64_json or [0].generated_text.',
  'studioConnectors.mimePath': 'Where the file type is (optional)',
  'studioConnectors.mimePathHint':
    'A path to the MIME type in the reply, e.g. data[0].media_type. Leave empty if the provider does not send one.',
  'studioConnectors.polling': 'Long-running result (polling)',
  'studioConnectors.pollingHint':
    'Leave these empty unless the provider only answers "started" and has to be asked again until the result is ready (Veo, most video models).',
  'studioConnectors.pollPath': 'Where the operation id is in the first reply',
  'studioConnectors.pollUrl': 'URL to poll, with {{operation}} in it',
  'studioConnectors.pollDonePath': 'Where the "done" flag is in the polled reply',
  'studioConnectors.authOnResultFetch': 'Send the key when downloading the result',
  'studioConnectors.pollDoneValue': 'Status word that means finished (optional)',
  'studioConnectors.pollFailedValues': 'Status words that mean it failed, comma-separated',
  'studioConnectors.pollDoneValueHint':
    'Leave empty when the done path turns true (Google). For a status field such as Alibaba\'s output.task_status, write SUCCEEDED here and FAILED,CANCELED,UNKNOWN below.',
  'studioConnectors.extraHeaders': 'Extra headers',
  'studioConnectors.extraHeadersHint':
    'Fixed headers sent with every call, as JSON. Never put the API key here — it has its own encrypted field.',
  'studioConnectors.extraHeadersInvalid': 'Write a JSON object whose values are text, or leave it empty.',
  'studioConnectors.edit': 'Edit connector',
  'studioConnectors.editing': 'Editing {name} — leave the auth token blank to keep the stored one',
  'studioConnectors.verification': 'Test',
  'studioConnectors.verification.UNTESTED': 'Not tested',
  'studioConnectors.verification.PASSED': 'Passed',
  'studioConnectors.verification.FAILED': 'Failed',
  'studioConnectors.defaultNeedsTest': 'A connector can only be made the default after its test has passed.',
  'studioConnectors.draftHint':
    'It is saved as a draft: it cannot be used, or made the default, until a test run has produced a result. Test it from its row after saving.',
  'studioConnectors.test.button': 'Test',
  'studioConnectors.test.title': 'Test this connector',
  'studioConnectors.test.hint':
    'Runs one real generation through this connector with a small prompt and shows what the provider answered, so you can see exactly what to put in the response path. It passes only if a result (text, image, video or audio) came back.',
  'studioConnectors.test.savedOnly': 'The test uses the connector as last saved — save your changes first.',
  'studioConnectors.test.prompt': 'Test prompt (optional)',
  'studioConnectors.test.promptPlaceholder': 'Leave empty to use a short built-in prompt',
  'studioConnectors.test.reference': 'Reference image (optional, for image-to-video or image editing connectors)',
  'studioConnectors.test.videoCost':
    'Video is billed per second of output by the provider. This test generates a short clip (a few seconds) and will be charged to your provider account. Run it?',
  'studioConnectors.test.videoCostNote':
    'Video connectors are billed per second. The test asks for a very short clip, but it is still a real, charged call.',
  'studioConnectors.test.run': 'Run test',
  'studioConnectors.test.running': 'Testing…',
  'studioConnectors.test.runningShort': 'Calling the provider… this usually takes a few seconds.',
  'studioConnectors.test.runningVideo': 'Waiting for the provider to render the video — this can take several minutes. You can leave this page; the result will be here when it finishes.',
  'studioConnectors.test.lastTested': 'Last tested {when}',
  'studioConnectors.test.failed': 'The test failed: {error}',
  'studioConnectors.test.passed': 'The test passed — the connector is now usable. This is what it produced:',
  'studioConnectors.test.resultImage': 'Test result',
  'studioConnectors.test.providerResponse': 'Provider response',
  'studioConnectors.test.providerResponseHint':
    'The provider\'s reply to the last test, with keys removed and long values shortened. Write the response path from what you see here.',
  'studioConnectors.test.providerRequest': 'Request sent',
  'studioConnectors.test.providerRequestHint':
    'What the last test sent: the address without any key, the header names and the body. Compare it with the provider\'s documentation.',
  'studioConnectors.repair.title': 'Auto-fix',
  'studioConnectors.repair.hint':
    'A text model reads what the test sent and what the provider answered, and proposes corrections. Nothing changes until you apply them. The key is never shown to it.',
  'studioConnectors.repair.target': 'Connector being repaired: {name}',
  'studioConnectors.repair.usedModel': 'Proposed by the text connector "{name}"',
  'studioConnectors.repair.videoWarn':
    'This is a video connector: every retest is a real call billed per second. Automatic retries are limited to 2.',
  'studioConnectors.repair.videoConfirm':
    'Each retest of a video connector generates a clip and is charged to your provider account. Continue?',
  'studioConnectors.repair.note': 'Note for the model (optional)',
  'studioConnectors.repair.notePlaceholder':
    'For example: the reply has an "images" list, or the docs say the header is x-api-key',
  'studioConnectors.repair.auto': 'Apply automatically and retry, up to',
  'studioConnectors.repair.autoCount': 'Number of automatic tries',
  'studioConnectors.repair.times': 'times',
  'studioConnectors.repair.run': 'Auto-fix',
  'studioConnectors.repair.again': 'Try another fix',
  'studioConnectors.repair.round': 'Try {n} of {max}',
  'studioConnectors.repair.asking': 'Asking the text model…',
  'studioConnectors.repair.applying': 'Saving the changes…',
  'studioConnectors.repair.retesting': 'Testing again…',
  'studioConnectors.repair.wrong': 'What was wrong:',
  'studioConnectors.repair.confidence': 'Confidence: {level}',
  'studioConnectors.repair.confidence.high': 'high',
  'studioConnectors.repair.confidence.medium': 'medium',
  'studioConnectors.repair.confidence.low': 'low',
  'studioConnectors.repair.changes': 'Proposed changes',
  'studioConnectors.repair.dropped': 'Left out (not applied)',
  'studioConnectors.repair.noChanges':
    'The model found nothing it is allowed to change here. Edit the settings by hand, or add a note and try again.',
  'studioConnectors.repair.apply': 'Apply and retest',
  'studioConnectors.repair.passed':
    'Fixed: the test passed after {n} change(s). The connector is usable now.',
  'studioConnectors.repair.stopped': 'Stopped after {n} try(ies): the test still fails. {error}',
  'studioConnectors.repair.failedAgain':
    'The test still fails: {error} You can ask for another fix.',
  'studioConnectors.repair.history': 'Earlier tries ({n})',
  'studioConnectors.repair.attemptPassed': 'The test passed after this change.',
  'studioConnectors.repair.attemptFailed': 'The test still failed: {error}',
  'studioConnectors.tree.title': 'Point at the field in the reply',
  'studioConnectors.tree.hint': 'Tap a value in the reply to use its path.',
  'studioConnectors.tree.anyItem': 'Any item (write [*] instead of a position)',
  'studioConnectors.tree.selected': 'Path:',
  'studioConnectors.tree.useAs': 'Use as:',
  'studioConnectors.tree.use.result': 'Result path',
  'studioConnectors.tree.use.operation': 'Operation id path',
  'studioConnectors.tree.use.done': 'Done-flag path',
  'studioConnectors.tree.use.mime': 'MIME type path',
  'studioConnectors.tree.used': 'Set in the form: {path}. Press Save to keep it.',
  'studioConnectors.tree.readOnly': 'Open Edit on this connector to put a path into its form.',
  'studioConnectors.tree.cannotAddress':
    'This name cannot be written as a path. Pick a value inside it, or type the path by hand.',
  'studioConnectors.tree.noPosition':
    'This path has no position in it, so "any item" changes nothing.',
  'studioConnectors.tree.notJson':
    'The reply is not JSON (or was cut short), so there is nothing to point at.',
  'studioConnectors.tree.items': '{n} items',
  'studioConnectors.tree.fields': '{n} fields',
  'studioConnectors.tree.expand': 'Expand',
  'studioConnectors.tree.collapse': 'Collapse',
  'studioConnectors.tree.mark.result': 'result',
  'studioConnectors.tree.mark.operation': 'operation id',
  'studioConnectors.tree.mark.done': 'done flag',
  'studioConnectors.tree.mark.mime': 'MIME',
  'studioConnectors.catalogue.button': 'Update from catalogue',
  'studioConnectors.catalogue.hint':
    'Compares this connector with the catalogue entry "{label}". Tick what to take; it goes into the form below and is saved only when you press Save (which also resets the test).',
  'studioConnectors.catalogue.same': 'This connector already matches its catalogue entry.',
  'studioConnectors.catalogue.customised': 'Often changed on purpose, so it is unticked.',
  'studioConnectors.catalogue.apply': 'Apply {n} selected to the form',
  'studioConnectors.catalogue.applied': 'Applied to the form. Review it, then press Save.',
  'studioConnectors.field.requestTemplate': 'Request template',
  'studioConnectors.field.responsePath': 'Response path',
  'studioConnectors.field.responseKind': 'Response type',
  'studioConnectors.field.responseMimePath': 'MIME type path',
  'studioConnectors.field.pollPath': 'Operation-id path',
  'studioConnectors.field.pollUrlTemplate': 'Poll URL',
  'studioConnectors.field.pollDonePath': 'Done path',
  'studioConnectors.field.pollDoneValue': 'Done status word',
  'studioConnectors.field.pollFailedValues': 'Failed status words',
  'studioConnectors.field.authOnResultFetch': 'Send the key when fetching the result',
  'studioConnectors.field.extraHeaders': 'Extra headers',
  'studioConnectors.field.authStyle': 'Where the key goes',
  'studioConnectors.field.authParamName': 'Header or parameter name',
  'studioConnectors.field.endpointUrl': 'Endpoint URL',
  'studioConnectors.field.model': 'Model',
} as const;

export type TranslationKey = keyof typeof en;
export type Translations = Record<TranslationKey, string>;

export const ar: Translations = {
  /* Shell */
  'app.name': 'ناشر صفحات ميتا',
  'app.signOut': 'تسجيل الخروج',
  'app.language': 'English',
  'app.theme.toDark': 'داكن',
  'app.theme.toLight': 'فاتح',

  /* Tabs */
  'flow.chooseContent': 'اختر ما تنشره',
  'flow.chooseTargets': 'اختر أين يُنشر',
  'flow.watchProgress': 'تابع خروجه',
  'flow.change': 'تغيير',
  'flow.collapse': 'إغلاق',
  'home.waitingTitle': 'ينتظرك',
  'home.waitingLabel': 'ينتظر ردًّا',
  'home.openInbox': 'افتح الوارد',
  'home.greeting': 'هذه حال حسابك الآن',
  'home.livePages': 'صفحة متصلة',
  'home.comments': 'تعليق على منشوراتك',
  'home.viewAll': 'عرض الكل',
  'home.fromMeta': 'محسوبة من فيسبوك نفسه، لا مما أُرسل من هنا فقط',
  'home.unreadable': '{count} صفحة لم تستجب — أعد ربطها',
  'home.publishedThisWeek': 'نُشر هذا الأسبوع',
  'home.running': 'في الطابور',
  'home.activityTitle': 'ما نُشر خلال ٧ أيام',
  'home.activityHint': 'عبر كل صفحاتك',
  'home.startTitle': 'اربط حساب فيسبوك لتبدأ',
  'home.startBody':
    'لا يوجد اتصال بعد. اربط الحساب الذي يدير صفحاتك، واختر أيّها تريد العمل عليه، وينفتح الباقي.',
  'home.startBodyNoAccess':
    'لا يوجد اتصال بعد، وربط الحساب من عمل المدير. اطلب ممّن يدير هذا الحساب أن يربطه، وسيظهر عملك هنا.',
  'home.startAction': 'اربط حسابًا',
  'app.refresh': 'تحديث',
  'insights.title': 'الإحصائيات',
  'insights.refresh': 'تحديث',
  'insights.refreshing': 'يقرأ من ميتا…',
  'insights.refreshFailed': 'تعذّر التحديث الآن',
  'insights.lastDays': 'آخر {count} يومًا',
  'insights.custom': 'مدى مخصّص',
  'insights.from': 'من',
  'insights.to': 'إلى',
  'insights.export.daily': 'صدّر الأيام',
  'insights.export.pages': 'صدّر الصفحات',
  'insights.export.posts': 'صدّر المنشورات',
  'insights.allPages': 'كل الصفحات',
  'insights.posts': 'منشور',
  'insights.comments': 'تعليق',
  'insights.reactions': 'تفاعل',
  'insights.shares': 'مشاركة',
  'insights.reach': 'وصلت إلى',
  'insights.postsPerDay': 'المنشورات يوميًا',
  'insights.commentsPerDay': 'التعليقات يوميًا',
  'insights.reactionsPerDay': 'التفاعلات يوميًا',
  'insights.otherPages': 'صفحات أخرى',
  'insights.noData': 'لا شيء مسجّل في هذه الفترة',
  'insights.byPage': 'حسب الصفحة',
  'insights.page': 'الصفحة',
  'insights.topPosts': 'الأكثر تفاعلًا',
  'insights.open': 'فتح',
  'insights.collectedAt': 'آخر قراءة من فيسبوك: {when}',
  'insights.backfilling': 'يقرأ التاريخ من فيسبوك',
  'insights.progressCount': 'قُرئت {done} من {total} صفحة',
  'insights.refreshHint': 'يقرأ {count} صفحة من فيسبوك — تستغرق ثوانيَ قليلة',
  'cropper.zoom': 'التكبير',
  'cropper.cancel': 'إلغاء',
  'cropper.use': 'استخدم هذا القصّ',
  'cropper.working': 'يُجهّز…',
  'cropper.hint.picture': 'فيسبوك يعرض صورة الملف دائرةً داخل هذا المربّع.',
  'cropper.hint.cover': 'فيسبوك يقصّ الغلاف أضيق على الجوال، فاجعل المهمّ قرب الوسط.',
  'publish.sameToOthers': 'انشره في صفحات أخرى',
  'publish.somethingElse': 'انشر شيئًا آخر',
  'comments.noneStored': 'لم تُجمع تعليقات لهذه الصفحة بعد. استخدم «تحقّق الآن» في الوارد.',
  'comments.countOnPost': '{count} تعليقًا',
  'inbox.title': 'الوارد',
  'inbox.waiting': 'ينتظر',
  'inbox.done': 'تمّت معالجته',
  'inbox.ignored': 'لا يتطلب ردًّا',
  'inbox.nothingIgnored': 'لم يُنحَّ أي عنصر جانبًا.',
  'inbox.handledElsewhere': 'أجاب عنه طرف آخر',
  'inbox.allPages': 'كل الصفحات',
  'inbox.allChannels': 'كل القنوات',
  'conversations.title': 'المحادثات',
  'conversations.empty': 'لا توجد محادثات بعد.',
  'conversations.pick': 'اختر أحدًا من القائمة.',
  'conversations.someone': 'شخص',
  'conversations.back': '→ المحادثات',
  'conversations.expand': 'ملء الشاشة',
  'conversations.restore': 'إنهاء ملء الشاشة',
  'conversations.you': 'أنت:',
  'conversations.waitingOnly': 'المنتظِرة ({count})',
  'conversations.waitingSince': 'ينتظر منذ {when}',
  'conversations.thePost': 'المنشور',
  'conversations.more': 'المزيد',
  'conversations.openOnFacebook': 'افتحه في فيسبوك',
  'conversations.placeholder': 'اكتب ردًّا…',
  'conversations.send': 'إرسال',
  'conversations.sending': 'جارٍ الإرسال…',
  'conversations.askAssistant': 'اسأل المساعد',
  'conversations.asking': 'جارٍ السؤال…',
  'conversations.askAnother': 'اقتراح آخر',
  'conversations.assistantDeclined': 'لم يقترح المساعد ردًّا ({outcome}).',
  'conversations.assistantFailed': 'تعذّر الوصول إلى المساعد: {reason}',
  'tab.conversations': 'المحادثات',
  'inbox.check': 'تحقّق الآن',
  'inbox.checking': 'يتحقّق…',
  'saved.manage': 'الردود المحفوظة',
  'saved.title': 'الردود المحفوظة',
  'saved.hint':
    'الجمل التي ترسلها كل يوم، على بُعد نقرة من صندوق الردّ. تُحفظ للحساب كله لا لكل صفحة — ساعات عملك لا تتغيّر لأن السؤال وصل من مكان آخر.',
  'saved.name': 'الاسم (كما سيظهر في القائمة)',
  'saved.body': 'نصّ الردّ',
  'saved.add': 'احفظ هذا الردّ',
  'saved.used': 'استُخدم {count} مرة',
  'inbox.searchPlaceholder': 'ابحث في الرسائل والأسماء',
  'inbox.search': 'ابحث',
  'inbox.clearSearch': 'امسح',
  'inbox.noMatches': 'لا شيء يطابق «{query}».',
  'inbox.showMore': 'أظهر الأقدم',
  'inbox.allClear': 'لا شيء ينتظر. كل تعليق أُجيب أو نُحّي جانبًا.',
  'inbox.nothingDone': 'لم تُعالَج أي عناصر بعد.',
  'inbox.someone': 'شخص ما',
  'inbox.openConversation': 'افتح المحادثة كاملة',
  'inbox.replyPlaceholder': 'اكتب ردًّا…',
  'inbox.send': 'ردّ',
  'inbox.sending': 'يُرسل…',
  'inbox.like': 'إعجاب',
  'inbox.react': 'تفاعل',
  'inbox.removeReaction': 'إزالة التفاعل',
  'inbox.dismiss': 'نحِّه جانبًا',
  'inbox.channel.PAGE_COMMENT': 'فيسبوك',
  'inbox.channel.MESSENGER': 'ماسنجر',
  'inbox.channel.INSTAGRAM': 'إنستقرام',
  'inbox.channel.WHATSAPP': 'واتساب',
  'inbox.askAgent': 'اسأل المساعد',
  'inbox.askAgain': 'اقتراح آخر',
  'inbox.asking': 'يسأل…',
  'inbox.askAgentHint': 'المساعد يكتب مسودّة، وأنت تقرّر إرسالها.',
  'inbox.fromAssistant': 'مساعد هذه الصفحة · {name}',
  'inbox.fromAgent': 'وكيل موصول · {name}',
  'inbox.useDraft': 'استخدمه',
  'inbox.declined': 'لم يُجب المساعد عن هذا ({outcome}).',
  'automation.agentId': 'عنوان الوكيل أو معرّفه',
  'automation.agentEdit': 'تعديل',
  'automation.agentEditing': 'تعديل {name} — اترك السرّ فارغًا ليبقى المحفوظ',
  'automation.agentSave': 'حفظ التعديل',
  'automation.agentCancel': 'إلغاء',
  'automation.agentIdHint':
    'الصق عنوان الوكيل كاملًا كما تعطيه المنصّة — يُستخرج المُعرّف منه هنا، فلا تقصّ شيئًا بيدك. والمُعرّف وحده يعمل أيضًا. أمّا السلسلة الطويلة التي عُرضت مرّة عند إنشاء الرمز فليست مُعرّفًا: مكانها خانة الرمز السرّي أدناه.',
  'inbox.unreachable': 'تعذّر الوصول إلى المساعد: {reason}',
  'admin.renamePrompt': 'الاسم الجديد لهذا الحساب:',
  'admin.tenantRenamed': 'تم تغيير الاسم.',
  'admin.confirmDeleteTenant':
    'سيُحذف الحساب «{name}» وكل ما تحته نهائيًّا. اكتب اسم الحساب للتأكيد:',
  'admin.tenantDeleted': 'حُذف الحساب.',
  'admin.confirmDeleteUser': 'حذف {email} نهائيًّا؟',
  'admin.noUsersWarning':
    'لا يوجد مستخدمون في هذا الحساب، فلا يستطيع أحد الدخول إليه. أضف واحدًا بالأسفل.',
  'admin.user': 'المستخدم',
  'admin.manageUsers': 'المستخدمون…',
  'admin.reviewerHint':
    'يرى الخدمات قيد المراجعة على الصفحات التي تُسنِدها إليه، ولا شيء سواها — لا أتمتة ولا وكلاء ولا مفاتيح ولا زملاء ولا باقة. أسنِد له الصفحات بعد إنشائه.',
  'admin.usersOf': 'مستخدمو',
  'admin.setPassword': 'تعيين كلمة مرور',
  'admin.newPassword': 'كلمة المرور الجديدة',
  'admin.passwordSet': 'تم التعيين',
  'admin.passwordHint':
    'تُخزَّن كلمة المرور مشفَّرة ولا تظهر مرة أخرى. دوِّنها قبل مغادرة هذه الشاشة.',
  'common.close': 'إغلاق',
  'emoji.title': 'الرموز التعبيرية',
  'common.active': 'نشط',
  'common.inactive': 'غير نشط',
  'utility.title': 'الرسائل الخدمية',
  'utility.intro':
    'ماسنجر يسمح بالنص الحر ٢٤ ساعة بعد أن يراسلك العميل. بعدها لا يُقبل إلا قالب خدمي معتمد — تحديثات الطلبات والحسابات وتذكير المواعيد. ولا يُسمح بأي محتوى تسويقي. وميتا تعتمد القالب عادةً خلال ثوانٍ.',
  'utility.choosePage': 'اختر صفحة',
  'utility.noTemplates': 'لا توجد قوالب على هذه الصفحة بعد.',
  'utility.newTemplate': 'قالب جديد',
  'utility.name': 'اسم القالب',
  'utility.nameHint': 'حروف إنجليزية صغيرة وأرقام وشرطة سفلية فقط.',
  'utility.language': 'اللغة',
  'utility.header': 'العنوان (اختياري، بلا حقول متغيرة)',
  'utility.body': 'نص الرسالة',
  'utility.bodyHint': 'اكتب {{1}} و{{2}} في مواضع بيانات العميل.',
  'utility.examplePlaceholder': 'مثال للحقل {{n}}',
  'utility.create': 'إنشاء وإرسال للاعتماد',
  'utility.creating': 'جارٍ الإرسال…',
  'utility.status.APPROVED': 'معتمد',
  'utility.status.PENDING': 'قيد المراجعة',
  'utility.status.REJECTED': 'مرفوض',
  'utility.windowClosed':
    'مرّت أكثر من ٢٤ ساعة على رسالة هذا الشخص، وماسنجر لا يقبل الآن إلا قالبًا خدميًّا.',
  'utility.noneApproved': 'لا يوجد قالب معتمد بعد — أنشئ واحدًا من شاشة الرسائل الخدمية.',
  'utility.chooseTemplate': 'اختر قالبًا',
  'utility.send': 'إرسال رسالة خدمية',
  'whatsapp.title': 'أرقام واتساب',
  'whatsapp.remove': 'فكّ الربط',
  'whatsapp.removeConfirm':
    '\u0641\u0643\u0651 \u0631\u0628\u0637 \u0647\u0630\u0627 \u0627\u0644\u0631\u0642\u0645\u061f \u0627\u0644\u0631\u0633\u0627\u0626\u0644 \u0627\u0644\u062a\u064a \u0648\u0635\u0644\u062a \u0625\u0644\u0649 \u0627\u0644\u0628\u0631\u064a\u062f \u062a\u0628\u0642\u0649. \u0648\u0625\u0639\u0627\u062f\u0629 \u0627\u0633\u062a\u064a\u0631\u0627\u062f\u0647 \u062a\u0639\u064a\u062f\u0647 \u0643\u0645\u0627 \u0643\u0627\u0646.',
  'whatsapp.credentialsDead': 'الرمز مرفوض',
  'whatsapp.credentialsDeadHint':
    '\u0645\u064a\u062a\u0627 \u0631\u0641\u0636 \u0631\u0645\u0632 \u0647\u0630\u0627 \u0627\u0644\u0631\u0642\u0645 \u2014 \u063a\u0627\u0644\u0628\u064b\u0627 \u0644\u0623\u0646\u0651 \u0643\u0644\u0645\u0629 \u0645\u0631\u0648\u0631 \u0641\u064a\u0633\u0628\u0648\u0643 \u062a\u063a\u064a\u0651\u0631\u062a\u060c \u0623\u0648 \u0644\u0623\u0646\u0651 \u0641\u064a\u0633\u0628\u0648\u0643 \u0623\u0646\u0647\u0649 \u0627\u0644\u062c\u0644\u0633\u0629 \u0644\u0623\u0633\u0628\u0627\u0628 \u0623\u0645\u0646\u064a\u0629. \u0644\u0627 \u0634\u064a\u0621 \u0647\u0646\u0627 \u064a\u064f\u062d\u064a\u064a\u0647: \u0623\u0639\u0650\u062f \u0627\u0633\u062a\u064a\u0631\u0627\u062f \u0627\u0644\u0631\u0642\u0645 \u0628\u0631\u0645\u0632 \u062c\u062f\u064a\u062f.',
  'whatsapp.configToggle': 'تكوين التسجيل',
  'whatsapp.configLabel': 'معرّف تكوين Embedded Signup',
  'whatsapp.configHint':
    'من developers.facebook.com \u2190 تطبيقك \u2190 Facebook Login for Business \u2190 التكوينات. يجب أن يكون من نوع \u00ab\u0631\u0645\u0632 \u0648\u0635\u0648\u0644 \u0627\u0644\u0646\u0634\u0627\u0637 \u0627\u0644\u062a\u062c\u0627\u0631\u064a\u00bb \u2014 وتكوين \u00ab\u0631\u0645\u0632 \u0648\u0635\u0648\u0644 \u0627\u0644\u0645\u0633\u062a\u062e\u062f\u0645\u00bb يفتح النافذة ولا يُرجع حسابات. والنوع لا يُعدَّل بعد الإنشاء، فأنشئ تكوينًا جديدًا والصق معرّفه هنا.',
  'whatsapp.configFromEnv': 'القيمة موروثة من إعداد المنصّة. الحفظ هنا يجعلها خاصّة بك.',
  'whatsapp.configShared':
    'هذا تطبيق المنصّة المشترك. سجّل تطبيق ميتا خاصًّا بك لتضبط تكوينك، أو اطلب من مدير المنصّة.',
  'whatsapp.configMissing': 'لا يوجد تكوين تسجيل مضبوط',
  'whatsapp.configMissingHint': 'ربط رقم يحتاجه. افتح «تكوين التسجيل» أدناه والصق المعرّف من ميتا.',
  'whatsapp.intro':
    'رقم واتساب يتبع النشاط التجاري لا الصفحة — وميتا لا تسجّل أي رابط بينهما، لذلك حدِّد الصفحة التي يردّ عنها كل رقم. وتحديد الصفحة هو نفسه ما يشغّل استقبال الرسائل.',
  'whatsapp.import': 'إضافة أرقام',
  'whatsapp.importAction': 'قراءة الأرقام',
  'whatsapp.importing': 'جارٍ القراءة…',
  'whatsapp.tokenLabel': 'رمز الوصول من ميتا',
  'whatsapp.tokenHint':
    'يُستبدل برمز صلاحيته ٦٠ يومًا ويُخزَّن مشفَّرًا؛ ولا يُحفظ ما تلصقه هنا. ستظهر كل الأرقام التي يصل إليها الرمز.',
  'whatsapp.imported': 'وجدنا {numbers} رقمًا في {accounts} حسابًا.',
  'whatsapp.none': 'لا توجد أرقام واتساب بعد.',
  'whatsapp.answersFor': 'يردّ عن',
  'whatsapp.unassigned': 'غير محدَّدة',
  'whatsapp.receiving': 'يستقبل الرسائل',
  'whatsapp.enable': 'تشغيل الاستقبال',
  'whatsapp.needsPage': 'حدِّد صفحة ليبدأ الاستقبال',
  'mentions.title': 'الإشارات والتقييمات',
  'mentions.subtitle': 'ما قاله الآخرون عن صفحتك. للقراءة فقط.',
  'mentions.selectPage': 'الصفحة',
  'mentions.selectPagePlaceholder': 'اختر صفحة…',
  'mentions.mentions': 'منشورات أشارت إلى صفحتك',
  'mentions.reviews': 'التوصيات',
  'mentions.noMentions': 'لم يُشِر أحد إلى هذه الصفحة مؤخرًا.',
  'mentions.noReviews': 'لا توصيات بعد.',
  'mentions.someone': 'شخص ما',
  'mentions.open': 'فتح',
  'mentions.recommends': 'يوصي بها',
  'mentions.doesNot': 'لا يوصي بها',
  'profile.location': 'العنوان',
  'profile.locationHint': 'يستخدمه فيسبوك لوضع صفحتك على الخريطة وفي البحث المحلّي.',
  'profile.saveLocation': 'احفظ العنوان',
  'profile.locationSaved': 'حُفظ العنوان.',
  'profile.place.street': 'الشارع',
  'profile.place.city': 'المدينة',
  'profile.place.state': 'المحافظة',
  'profile.place.zip': 'الرمز البريدي',
  'profile.place.country': 'الدولة',
  'profile.images': 'الصور',
  'profile.picture': 'صورة الملف',
  'profile.cover': 'صورة الغلاف',
  'profile.replace': 'استبدال',
  'profile.noImage': 'غير مضبوطة',
  'profile.hours': 'ساعات العمل',
  'profile.hoursHint': 'اترك اليوم فارغًا ليظهر مغلقًا.',
  'profile.closed': 'اجعله مغلقًا',
  'profile.saveHours': 'احفظ الساعات',
  'profile.hoursSaved': 'حُفظت ساعات العمل.',
  'profile.day.mon': 'الإثنين',
  'profile.day.tue': 'الثلاثاء',
  'profile.day.wed': 'الأربعاء',
  'profile.day.thu': 'الخميس',
  'profile.day.fri': 'الجمعة',
  'profile.day.sat': 'السبت',
  'profile.day.sun': 'الأحد',
  'profile.title': 'ملفّ الصفحة',
  'profile.subtitle': 'البيانات التي يراها الناس على صفحتك. التعديل يذهب إلى فيسبوك مباشرة.',
  'profile.selectPage': 'الصفحة',
  'profile.selectPagePlaceholder': 'اختر صفحة…',
  'profile.noEditablePages': 'لا تملك صلاحية التعديل على أيٍّ من صفحاتك المتصلة.',
  'profile.followers': '{count} متابعًا',
  'profile.openOnFacebook': 'افتحها في فيسبوك',
  'profile.nameNote': 'اسم الصفحة غير قابل للتعديل هنا — فيسبوك يراجع تغيير الاسم بنفسه.',
  'profile.about': 'نبذة مختصرة',
  'profile.description': 'الوصف الكامل',
  'profile.phone': 'الهاتف',
  'profile.email': 'البريد',
  'profile.website': 'الموقع',
  'profile.save': 'احفظ في فيسبوك',
  'profile.saving': 'يحفظ…',
  'profile.saved': 'حُفظ. القيم أدناه هي ما أبقاه فيسبوك.',
  'profile.saveFailed': 'رفض فيسبوك التعديل',
  'profile.readBackNote':
    'بعد الحفظ تُقرأ الحقول من فيسبوك مجددًا — بعض القيم يُهذّبها أو يرفضها، وهذا يُظهر ما هو مخزَّن فعلًا.',
  'nav.home': 'الرئيسية',
  'nav.pages': 'الصفحات',
  'nav.publish': 'النشر',
  'nav.studio': 'استوديو المحتوى',
  'nav.engage': 'التفاعل',
  'nav.activity': 'السجلّ',
  'nav.account': 'الحساب',
  'nav.settings': 'الإعدادات',
  'tab.connections': 'الاتصالات',
  'tab.pages': 'الصفحات',
  'tab.source': 'المصدر',
  'tab.publish': 'النشر',
  'tab.comments': 'التعليقات',
  'tab.automation': 'الأتمتة',
  'tab.published': 'المنشور',
  'tab.jobs': 'العمليات',
  'tab.logs': 'السجلات',

  /* Selection bar */
  'selection.label': 'المُختار',
  'selection.clear': 'إلغاء',

  /* Login */
  'login.title': 'تسجيل الدخول',
  'login.subtitle': 'هذا حساب لوحة التحكم، لا حساب فيسبوك. تربط ميتا بعد تسجيل الدخول.',
  'login.email': 'البريد الإلكتروني',
  'login.password': 'كلمة المرور',
  'login.showPassword': 'إظهار',
  'login.hidePassword': 'إخفاء',
  'login.submit': 'دخول',
  'login.submitting': 'جارٍ الدخول…',
  'mail.tab': 'البريد',
  'mail.hint':
    'كيف يرسل النظام رموز التحقق وإشعارات الحسابات. ما يُضبط هنا يتقدّم على متغيّرات البيئة.',
  'mail.settings': 'إعدادات SMTP',
  'mail.host': 'الخادم',
  'mail.port': 'المنفذ',
  'mail.username': 'اسم المستخدم',
  'mail.password': 'كلمة المرور',
  'mail.passwordStored': 'محفوظة — اتركه فارغًا للإبقاء عليها',
  'mail.appPasswordHint':
    'في Gmail هذه «كلمة مرور تطبيق» لا كلمة مرور حسابك: فعّل التحقق بخطوتين، ثم أنشئها من myaccount.google.com/apppasswords. ترسل البريد فقط ولا تفتح حسابك، وإلغاؤها لا يؤثر على دخولك.',
  'mail.fromAddress': 'عنوان المرسِل',
  'mail.fromName': 'اسم المرسِل',
  'mail.active': 'تفعيل الإرسال (إن أُطفئ تُكتب الرموز في السجلّ فقط)',
  'mail.saved': 'حُفظت الإعدادات. أرسل رسالة اختبار لإثبات أنها تعمل.',
  'mail.test': 'إرسال رسالة اختبار',
  'mail.testHint':
    'الإعدادات المحفوظة ليست إعدادات مُثبتة. كلمة مرور خاطئة تفشل بصمت حتى يُقفل أحدهم خارج حسابه.',
  'mail.sendTest': 'إرسال',
  'mail.sending': 'جارٍ الإرسال…',
  'mail.testOk': 'وصلت. تفقّد ذلك البريد.',
  'mail.testFailed': 'لم تصل:',
  'mail.lastTestOk': 'وصلت رسالة الاختبار الأخيرة',
  'mail.unprovenTitle': 'لم تصل أي رسالة اختبار بعد',
  'mail.unprovenBody':
    'إعادة تعيين كلمات المرور وتأكيد التسجيل كلاهما يعتمد على البريد. حتى تصل رسالة اختبار، افترض أن أيًّا منهما لا يعمل.',
  'bindings.tab': 'الصفحات',
  'bindings.hint':
    'وكيل واحد لكل صفحة. الصفحة التي تخصّ منتجًا واحدًا لها معرفة واحدة، والوكيل الذي يُطلب منه الرد عن كل الصفحات يردّ عن كلٍّ منها أسوأ مما يفعل المتخصّص.',
  'bindings.agent': 'الوكيل المسؤول',
  'bindings.chooseAgent': 'اختر وكيلًا…',
  'bindings.bind': 'ربط',
  'bindings.rebind': 'تغيير الوكيل',
  'bindings.unbind': 'فكّ الربط',
  'bindings.bound': 'مرتبطة',
  'bindings.unbound': 'بلا وكيل',
  'bindings.saved': 'حُفظ.',
  'bindings.removed': 'فُكّ ربط الوكيل بهذه الصفحة.',
  'bindings.noAgents': 'أضف وكيلًا أولًا، ثم اربطه بصفحة.',
  'bindings.noPages': 'لا صفحات بعد. اربط حساب Meta أولًا.',
  'bindings.cannotComment': 'لم تمنح Meta صلاحية MODERATE على هذه الصفحة، فلا يمكن نشر ردود فيها.',
  'bindings.notSubscribed': 'لا تصلها التعليقات',
  'bindings.autoReply': 'إرسال التعليقات الواردة إلى هذا الوكيل',
  'bindings.autoPublish': 'نشر ردوده بلا مراجعة',
  'bindings.autoPublishHint':
    'إن كان مُطفأً، ينتظر كل ردٍّ في قائمة المراجعة حتى يعتمده إنسان. اتركه مُطفأً حتى تقرأ من ردود هذا الوكيل ما يكفي للوثوق به أمام الناس.',
  'bindings.autoDraft': 'اكتب مسوّدة ردٍّ لكلّ ما يصل',
  'bindings.autoDraftHint':
    'يكتب اقتراحًا لكلّ تعليق ورسالة جديدة، ليقرأه إنسان ويرسله. لا يخرج شيء وحده، وهذا الخيار لا يعتمد على الخيارين فوقه. ويستهلك حصّتك الشهرية من نداءات الوكيل.',
  'menu.account': 'الحساب',
  'menu.plan': 'الباقة',
  'menu.environment': 'البيئة',
  'menu.build': 'الإصدار',
  'menu.role': 'دورك',
  'login.forgot': 'نسيت كلمة المرور؟',
  'login.newAccount': 'طلب حساب جديد',

  'forgot.title': 'إعادة تعيين كلمة المرور',
  'forgot.subtitle': 'سنرسل رمزًا من ستة أرقام إلى بريد حسابك.',
  'forgot.sendCode': 'إرسال الرمز',
  'forgot.resend': 'إعادة الإرسال',
  'forgot.code': 'رمز التحقق',
  'forgot.codeSent': 'أُرسل الرمز. تفقّد بريدك — صالح لبضع دقائق.',
  'forgot.newPassword': 'كلمة المرور الجديدة',
  'forgot.confirmPassword': 'تأكيد كلمة المرور',
  'forgot.mismatch': 'كلمتا المرور غير متطابقتين.',
  'forgot.submit': 'حفظ كلمة المرور',
  'forgot.done': 'تم التغيير. نعيدك إلى تسجيل الدخول…',
  'forgot.backToLogin': 'العودة لتسجيل الدخول',

  'signup.title': 'طلب حساب جديد',
  'signup.subtitle': 'عرّفنا بنفسك. كل طلب يراجعه مدير النظام.',
  'signup.organisation': 'اسم المنشأة',
  'signup.contactName': 'اسمك',
  'signup.phone': 'رقم الجوال',
  'signup.sendCode': 'إرسال رمز التأكيد',
  'signup.code': 'رمز التأكيد',
  'signup.codeSent': 'أُرسل الرمز إلى بريدك.',
  'signup.submit': 'إرسال الطلب',
  'signup.pendingTitle': 'استلمنا طلبك',
  'signup.pendingBody':
    'تم تأكيد بريدك، وطلبك الآن لدى مدير النظام. سنكتب إليك على البريد نفسه بالنتيجة.',

  'account.changePassword': 'تغيير كلمة المرور',
  'account.currentPassword': 'كلمة المرور الحالية',
  'account.newPassword': 'كلمة المرور الجديدة',
  'account.passwordChanged': 'تم تغيير كلمة المرور.',
  'account.resetUserPassword': 'تعيين كلمة مرور جديدة',
  'account.emailHint':
    'رموز الاستعادة تُرسل إلى هذا العنوان. عنوان لا يقرؤه أحد يعني إقفال الحساب نهائيًا.',
  'account.leaveBlank': 'اتركه فارغًا للإبقاء على كلمة المرور الحالية',
  'common.edit': 'تعديل',

  'admin.signups': 'الطلبات',
  'admin.signupsHint':
    'من طلبوا حسابًا وأكّدوا بريدهم. الموافقة تُنشئ المستأجر ومديره الأول، وتُرسل إليه كلمة مرور مؤقتة.',
  'admin.signup.organisation': 'المنشأة',
  'admin.signup.contact': 'جهة الاتصال',
  'admin.signup.phone': 'الجوال',
  'admin.signup.requested': 'تاريخ الطلب',
  'admin.signup.approve': 'موافقة',
  'admin.signup.reject': 'رفض',
  'admin.signup.note': 'ملاحظة داخلية (لا تظهر لمقدّم الطلب)',
  'admin.signup.none': 'لا توجد طلبات معلّقة.',
  'admin.signup.approved': 'تمت الموافقة، وأُرسلت البيانات بالبريد.',
  'admin.signup.rejected': 'رُفض الطلب.',
  'admin.signup.choosePlan': 'باقة هذا الحساب',
  'admin.signup.history': 'طلبات سابقة',
  'login.failed': 'فشل تسجيل الدخول',

  /* Common */
  'common.loading': 'جارٍ التحميل…',
  'common.none': 'لا شيء',
  'common.yes': 'نعم',
  'common.no': 'لا',
  'common.open': 'فتح',
  'common.delete': 'حذف',
  'common.cancel': 'إلغاء',
  'common.dismiss': 'إلغاء',
  'common.retry': 'إعادة المحاولة',
  'common.save': 'حفظ',
  'common.saving': 'جارٍ الحفظ…',
  'common.remove': 'إزالة',
  'common.select': 'اختيار',
  'common.selected': 'مُختار',
  'common.noText': '(بدون نص)',
  'common.never': 'لم يحدث',
  'common.requestFailed': 'فشل الطلب',
  'common.page': 'الصفحة',
  'common.status': 'الحالة',
  'common.date': 'التاريخ',
  'common.type': 'النوع',
  'common.media': 'الوسائط',
  'common.link': 'رابط',
  'common.actions': 'إجراءات',
  'common.all': 'الكل',

  /* Connections */
  'connections.title': 'الاتصالات',
  'connections.connect': 'ربط حساب ميتا',
  'connections.connectAnother': 'اربط حسابًا آخر',
  'connections.allowance': 'مربوط {used} من {limit} حساب ميتا. يمكنك إضافة المزيد.',
  'connections.atLimit':
    'تسمح هذه الباقة بـ{limit} حساب ميتا. افصل واحدًا أو ارفع الباقة لإضافة غيره.',
  'connections.empty': 'لا يوجد حساب ميتا مربوط بعد.',
  'connections.graphVersion': 'إصدار Graph API',
  'connections.publicSource': 'مصدر الصفحات العامة',
  'connections.enabled': 'مُفعّل',
  'connections.disabled': 'معطّل',
  'connections.requestedScopes': 'الصلاحيات المطلوبة',
  'connections.pages': 'الصفحات',
  'connections.tokenStored': 'التوكن مخزَّن',
  'connections.tokenEncrypted': 'نعم (مشفّر)',
  'connections.tokenExpires': 'انتهاء التوكن',
  'connections.noExpiry': 'بلا تاريخ انتهاء',
  'connections.lastVerified': 'آخر تحقق',
  'connections.verify': 'تحقق من التوكن',
  'connections.disconnect': 'فصل الاتصال',
  'connections.declined': 'صلاحيات مرفوضة',
  'connections.notGranted': 'غير ممنوحة',
  'connections.pendingApproval': 'بانتظار موافقة ميتا (إعادة الربط لن تغيّر ذلك)',
  'connections.confirmDisconnect': 'فصل الحساب وحذف كل التوكنات المخزَّنة؟',
  'connections.disconnected': 'تم الفصل وحُذفت كل التوكنات المخزَّنة.',
  'connections.connected': 'تم الربط. اكتُشفت {count} صفحة.',
  'connections.connectedDeclined':
    'تم الربط، لكن رُفضت هذه الصلاحيات: {list}. سيفشل النشر حتى تُمنح.',
  'connections.tokenValid': 'التوكن صالح',
  'connections.tokenValidUntil': 'التوكن صالح حتى {date}',
  'connections.publicSourceNotice':
    'قراءة منشورات صفحات عامة لا تديرها تتطلب ميزة Page Public Content Access من ميتا (مراجعة تطبيق + توثيق نشاط تجاري). حتى الموافقة، يمكن استخدام الصفحات التي تديرها فقط كمصدر.',

  /* Pages */
  'pages.title': 'الصفحات',
  'pages.sync': 'إعادة المزامنة من ميتا',
  'pages.syncing': 'جارٍ المزامنة…',
  'pages.subtitle':
    'الصفحات التي يديرها الحساب المربوط، كما تعرضها ميتا تمامًا. النشر يتطلب مهمة CREATE_CONTENT.',
  'pages.empty': 'لا توجد صفحات. اربط حساب ميتا ثم أعد المزامنة.',
  'pages.pageId': 'معرّف الصفحة',
  'pages.category': 'التصنيف',
  'health.title': 'ما المتصل فعلًا',
  'health.summary': '{working} من {total} قنوات تعمل',
  'health.working': 'يعمل',
  'health.blocker.NONE': 'يعمل',
  'health.blocker.DISABLED': 'غير مفعّلة في هذا التثبيت',
  'health.blocker.NOT_GRANTED':
    'الصلاحية غير ممنوحة — أعِد الربط واقبلها. وإن بقيت كذلك فميتا لم تعتمدها لهذا التطبيق بعد',
  'health.blocker.PENDING_APPROVAL':
    'بانتظار موافقة ميتا — لا شيء يلزمك، وإعادة الربط لن تغيّر ذلك',
  'health.blocker.SETUP': 'الإعداد لم يكتمل',
  'health.notLive':
    '{count} صفحة لا تستقبل التحديث الفوري. تعليقاتها لا تظهر إلا عند الضغط على «تحقّق».',
  'health.subscribeAll': 'شغّل التحديث الفوري لكل الصفحات',
  'health.subscribing': 'جارٍ الاشتراك…',
  'health.subscribed': 'شُغّل على {count} صفحة، و{already} كانت مشتركة أصلًا.',
  'health.skipped': 'تُخطّيت {count} صفحة — صلاحية الويب هوك غير ممنوحة.',
  'health.unverified': '{count} رقم واتساب لم يُراجَع لدى ميتا منذ إعداده.',
  'health.tokenExpiring': '{account}: ينتهي الربط خلال {days} يومًا، وتتوقّف {pages} صفحة معه.',
  'health.tokenExpired': '{account}: انتهى الربط قبل {days} يومًا، وتوقّفت {pages} صفحة.',
  'health.reconnect': 'أعِد ربط الحساب',
  'whatsapp.connectNumber': 'ربط رقم',
  'whatsapp.connecting': 'جارٍ فتح نافذة ميتا…',
  'whatsapp.signedUp': 'تم الربط',
  'whatsapp.signedUpCoexist': 'تم الربط، والرقم ما زال يعمل في تطبيق واتساب على جوالك —',
  'whatsapp.coexistence': 'مشترك مع تطبيق واتساب للأعمال',
  'whatsapp.check': 'تحقّق',
  'whatsapp.checking': 'جارٍ التحقّق…',
  'whatsapp.checkOk': 'ميتا تؤكّد أن هذا الرقم مشترك.',
  'whatsapp.checkBad': 'ميتا لا تؤكّد اشتراك هذا الرقم',
  'channel.FACEBOOK': 'صفحة فيسبوك',
  'channel.INSTAGRAM': 'إنستقرام',
  'channel.WHATSAPP': 'واتساب',
  'channel.MESSENGER': 'ماسنجر',
  'channel.INSTAGRAM_DM': 'رسائل إنستقرام',
  'channel.WEBHOOKS': 'التحديث الفوري',
  'channel.state.ACTIVE': 'يعمل',
  'channel.state.NOT_LINKED': 'لا يوجد شيء مرتبط بعد',
  'channel.state.NOT_GRANTED':
    'الصلاحية غير ممنوحة — أعِد الربط واقبلها. وإن بقيت كذلك فميتا لم تعتمدها لهذا التطبيق بعد',
  'channel.state.PENDING_APPROVAL': 'بانتظار موافقة ميتا — لا شيء يلزمك، وإعادة الربط لن تغيّر ذلك',
  'channel.state.OFF': 'غير مفعّلة في هذا التثبيت',
  'pages.instagram': 'إنستقرام',
  'pages.noInstagram': 'لا يوجد حساب إنستقرام مرتبط',
  'pages.tasks': 'المهام',
  'pages.canPublish': 'النشر',
  'pages.canComment': 'التعليق',
  'pages.token': 'التوكن',
  'pages.tokenMissing': 'مفقود',
  'pages.tokenInvalidated': 'مفقود (أُبطل)',

  /* Source */
  'source.title': 'المصدر',
  'source.mode.owned': 'صفحاتي',
  'source.mode.public': 'صفحة عامة',
  'source.mode.compose': 'إنشاء منشور',
  'source.selectPage': 'صفحة المصدر',
  'source.selectPagePlaceholder': 'اختر صفحة تديرها…',
  'source.loadingPosts': 'جارٍ تحميل المنشورات…',
  'source.postCount': '{count} منشور',
  'source.addPublic': 'إضافة مصدر',
  'source.publicPlaceholder': 'facebook.com/SomePage أو @SomePage أو معرّف الصفحة',
  'source.registeredPublic': 'المصادر العامة المسجّلة',
  'source.selectPublicPlaceholder': 'اختر صفحة عامة…',
  'source.openInFacebook': 'فتح في فيسبوك',
  'source.notReproducible': 'يحتوي على {types} — لا يوفّر Graph API نسخة قابلة لإعادة النشر.',
  'source.notReproducibleHint':
    'لا يوفّر Graph API نسخة قابلة لإعادة الرفع من هذه الوسائط. استخدم «إنشاء منشور» بدلًا من ذلك.',

  /* Composer */
  'composer.postType': 'نوع المنشور',
  'composer.kind.TEXT': 'منشور نصي',
  'composer.kind.LINK': 'منشور برابط',
  'composer.kind.PHOTO': 'صورة',
  'composer.kind.MULTI_PHOTO': 'عدة صور',
  'composer.kind.VIDEO': 'فيديو',
  'composer.kind.REEL': 'ريلز (فيديو عمودي، ٣-٩٠ ثانية)',
  'composer.reelHint': 'فيديو عمودي واحد (٩:١٦ وبدقة ٥٤٠×٩٦٠ على الأقل) مدته من ٣ إلى ٩٠ ثانية. تقرر ميتا إن كان الملف مناسبًا وتخبرك إن لم يكن.',
  'composer.alsoAs': 'انشر أيضًا كـ',
  'composer.alsoAs.story': 'قصة',
  'composer.alsoAs.reel': 'ريلز',
  'composer.alsoAs.hint': 'القصة تحتاج صورة أو فيديو واحدًا بالضبط وبلا جدولة، والريلز يحتاج فيديو واحدًا بالضبط. كلٌّ منهما يُنشر على حدة، فلا يوقف فشل أحدهما المنشور.',
  'jobs.variant.STORY': 'قصة',
  'jobs.variant.REEL': 'ريلز',
  'composer.kind.PHOTO_STORY': 'قصة بصورة',
  'composer.kind.VIDEO_STORY': 'قصة بفيديو (٦٠ ثانية كحد أقصى)',
  'composer.text': 'النص',
  'composer.textPlaceholder': 'ما الذي تريد نشره؟',
  'composer.link': 'الرابط',
  'composer.photos': 'الصور (٢–١٠)',
  'composer.photo': 'الصورة',
  'composer.videoFile': 'ملف الفيديو',
  'composer.firstComment': 'التعليق الأول (اختياري)',
  'composer.firstCommentPlaceholder': 'يُنشر كتعليق تحت المنشور الجديد',
  'composer.publishNow': 'انشر الآن',
  'composer.scheduleLater': 'جدولة لوقت لاحق',
  'composer.scheduleIn': 'سيُنشر بعد {when}',
  'composer.scheduleTooSoon': 'يجب أن يكون بعد ١٠ دقائق على الأقل من الآن (شرط ميتا)',
  'composer.scheduleTooLate': 'يجب ألا يتجاوز ٧٥ يومًا من الآن (شرط ميتا)',
  'composer.scheduleInvalid': 'تاريخ غير صالح',
  'composer.storyNotice':
    'واجهة القصص لا تدعم التعليق التوضيحي ولا الجدولة ولا التعليقات. كما ترفض ميتا وسائط استُخدمت في منشور سابق، فيلزم رفع نسخة جديدة.',
  'composer.submit': 'استخدام هذا المحتوى',
  'composer.saving': 'جارٍ الحفظ…',
  'composer.uploadFailed': 'فشل الرفع',
  'composer.saveFailed': 'تعذّر حفظ المحتوى',
  'composer.attached': 'مرفق',

  /* Publish */
  'publish.postTitle': 'المنشور',
  'publish.targetsTitle': 'الصفحات المستهدفة',
  'publish.nothingSelected':
    'لم يُختر شيء. اذهب إلى «المصدر»، واختر منشورًا أو أنشئ محتوى جديدًا، ثم عُد إلى هنا.',
  'publish.targetsHint': 'يمكن اختيار الصفحات التي تُبلغ ميتا بمهمة CREATE_CONTENT عليها فقط.',
  'publish.source': 'المصدر',
  'publish.reference': 'المرجع',
  'publish.preview': 'معاينة',
  'publish.allowRepublish': 'انشر مجددًا حتى لو سبق نشر هذا المحتوى على صفحة مختارة',
  'publish.allowRepublishHint': '(يتجاوز حارس التكرار؛ ويُسجَّل في سجل التدقيق)',
  'publish.button': 'انشر على {count} صفحة',
  'publish.queueing': 'جارٍ الإدراج…',
  'publish.queued': 'أُدرج {count} هدف.',
  'publish.scheduled': 'جُدول {count} هدفًا في {when}.',
  'publish.scheduleLabel': 'انشر لاحقًا (اختياري)',
  'jobs.edit.message': 'نصّ المنشور',
  'jobs.edit.link': 'رابط (اختياري)',
  'jobs.edit.firstComment': 'أول تعليق (اختياري)',
  'jobs.edit.media': 'الصور والفيديو',
  'jobs.edit.uploading': 'جارٍ الرفع…',
  'jobs.edit.at': 'يخرج في',
  'jobs.edit.noText': 'هذا إعادة نشر لمنشور قائم، فلا نصّ لنا فيه ليُعدَّل.',
  'jobs.edit.thenRetry': 'احفظ التعديل ثمّ اضغط «إعادة المحاولة» — يخرج المنشور المصحَّح فورًا.',
  'jobs.edit.partialWarning':
    'وصل هذا المنشور إلى بعض صفحاته. تلك تحتفظ بالنسخة التي وصلتها، والتصحيح يذهب إلى الصفحات التي فشلت وحدها.',
  'jobs.edit.targetsFixed': 'لتغيير الصفحات التي يخرج إليها، ألغِه ثمّ جدوِلْه من جديد.',
  'publish.scheduleHint': 'اتركه فارغًا لينشر فورًا.',
  'publish.scheduleSet': 'سيخرج في {when} بتوقيتك المحلي.',
  'publish.cancelScheduled': 'ألغِ',
  'publish.cancelled': 'أُلغي قبل النشر.',
  'publish.alsoInstagram': 'انشر على إنستقرام أيضًا',
  'publish.instagramPickFirst': 'اختر الصفحات أولًا.',
  'publish.instagramNone': 'لا صفحة من المختارة لها حساب إنستقرام مرتبط.',
  'publish.instagramReach': '{count} من {total} صفحة مختارة لها حساب.',
  'jobs.goesOut': 'يخرج {date}',
  'publish.skipped': 'تُخطّي {count}: {reasons}',
  'publish.failed': 'فشل النشر',
  'publish.blockedNoToken': 'لا يوجد توكن مخزَّن',
  'publish.blockedNoTask': 'مهمة CREATE_CONTENT غير متوفرة',

  /* Jobs */
  'jobs.title': 'العمليات',
  'jobs.empty': 'لا توجد عمليات بعد.',
  'jobs.view.scheduled': 'مجدولة',
  'jobs.view.publishing': 'تخرج الآن',
  'jobs.view.published': 'منشورة',
  'jobs.view.failed': 'تحتاج انتباهك',
  'jobs.view.cancelled': 'ملغاة',
  'jobs.empty.scheduled': 'لا شيء ينتظر الخروج.',
  'jobs.empty.publishing': 'لا شيء يخرج في هذه اللحظة.',
  'jobs.empty.published': 'لم يُنشر شيء من هنا بعد.',
  'jobs.empty.failed': 'لا شيء يحتاج انتباهك.',
  'jobs.empty.cancelled': 'لم يُستدعَ شيء.',
  'jobs.hide': 'إخفاء',
  'jobs.reveal': 'إظهار',
  'jobs.hidden': 'مخفيّة',
  'jobs.filter.goesOutFrom': 'يخرج من',
  'jobs.filter.madeFrom': 'أُنشئ من',
  'jobs.filter.to': 'إلى',
  'jobs.filter.sortDefault': 'الترتيب المعتاد',
  'jobs.filter.sortAsc': 'الأقدم أولًا',
  'jobs.filter.sortDesc': 'الأحدث أولًا',
  'jobs.filter.clear': 'إزالة الفلاتر',
  'jobs.showHidden': 'إظهار المخفيّة',
  'jobs.showAttempts': 'عرض المحاولات',
  'jobs.hideAttempts': 'إخفاء المحاولات',
  'jobs.retryFailed': 'إعادة المحاولة للفاشل',
  'jobs.created': 'أُنشئت {date}',
  'jobs.targetPage': 'الصفحة المستهدفة',
  'jobs.channel.FACEBOOK': 'فيسبوك',
  'jobs.channel.INSTAGRAM': 'إنستقرام',
  'jobs.channel.LINKEDIN': 'لينكدإن',
  'social.apps.network': 'الشبكة',
  'social.setting.LINKEDIN.organizations': 'صفحات الشركات معتمدة لهذا التطبيق (Community Management API)',
  'networks.title': 'تعريفات الشبكات',
  'networks.hint': 'كل شبكة غير ميتا هي تعريف. عدّل تعريفاً، أو أضف شبكة انطلاقاً من أخرى أو من الصفر، دون تعديل كود ودون نشر. ويرفض الخادم أي تعريف يتصل بعنوان لم يصرّح به.',
  'networks.source.PRESET': 'مدمجة',
  'networks.source.OVERRIDE': 'مدمجة ومعدّلة هنا',
  'networks.source.CUSTOM': 'مضافة هنا',
  'networks.verifiedLive': 'جُرّبت على الشبكة فعلياً',
  'networks.notVerified': 'لم تُجرَّب على الشبكة فعلياً بعد',
  'networks.edit': 'تعديل التعريف',
  'networks.add': 'إضافة شبكة…',
  'networks.fromPreset': 'إضافة شبكة',
  'networks.blank': 'تعريف فارغ',
  'networks.key': 'المفتاح (أحرف إنجليزية كبيرة، مثل X أو TIKTOK)',
  'networks.name': 'الاسم',
  'networks.contract': 'التعريف (JSON)',
  'networks.validate': 'تحقق',
  'networks.valid': 'التعريف سليم.',
  'networks.save': 'حفظ',
  'networks.saved': 'تم الحفظ، وهو مستخدم الآن.',
  'networks.disable': 'تعطيل',
  'networks.enable': 'تفعيل',
  'networks.reset': 'العودة للتعريف المدمج',
  'networks.delete': 'حذف',
  'networks.badJson': 'هذا ليس JSON صالحاً.',
  'networks.confirmDelete': 'إزالة هذا التعريف المحفوظ؟',
  'source.origin.PLATFORM': 'نُشر من هنا',
  'source.origin.NETWORK': 'من الحساب نفسه',
  'source.profileLimit': 'لا يتيح لينكدإن للتطبيقات قراءة منشورات الملف الشخصي، فتظهر هنا فقط المنشورات التي نُشرت من المنصة. أما صفحة الشركة فتظهر كل منشوراتها بعد اعتماد تطبيقها لصفحات الشركات.',
  'source.refresh': 'تحديث',
  'source.confirmDeleteSocial': 'حذف هذا المنشور من الحساب؟ لا يمكن التراجع عن ذلك.',
  'source.deleted': 'حُذف المنشور.',
  'source.saved': 'عُدّل المنشور.',
  'source.socialNote': 'المنشورات التي نُشرت على هذا الحساب من هنا. ولا يمكن إعادة استخدام منشور لينكدإن كمصدر.',
  'source.socialEmpty': 'لم يُنشر على هذا الحساب شيء من هنا بعد.',
  'home.revokedTitle': '{count} صفحة تحتاج إعادة ربط',
  'home.revokedBody': 'سحبت ميتا الوصول، فلا يمكن القراءة منها ولا النشر عليها حتى يُعاد ربط الحساب: {names}',
  'home.revokedAction': 'إعادة الربط',
  'social.account.ACCOUNT': 'حساب',
  'publish.destinationsLocked': 'اختر المحتوى أولًا. هذه هي الأماكن التي يمكن النشر إليها.',
  'conversations.allChannels': 'كل القنوات',
  'dest.search': 'ابحث…',
  'dest.none': 'لا يوجد ما يُختار.',
  'dest.close': 'إغلاق',
  'dest.selectAll': 'تحديد الكل',
  'dest.clearAll': 'إلغاء التحديد',
  'dest.selected': '{count} محدّدة',
  'dest.unavailable': 'غير متاحة ({count})',
  'dest.unsupported': '{networks}: غير مدعوم هنا.',
  'dest.reason.INACTIVE': 'لم تعد مربوطة',
  'dest.reason.NO_TOKEN': 'التوكن مفقود أو مُبطل',
  'dest.reason.NO_TASK': 'دورك على هذه الصفحة لا يسمح بذلك',
  'dest.reason.RECONNECT': 'أعد ربطه أولًا',
  'channelName.FACEBOOK': 'فيسبوك',
  'channelName.INSTAGRAM': 'إنستقرام',
  'channelName.LINKEDIN': 'لينكدإن',
  'channelName.WHATSAPP': 'واتساب',
  'social.title': 'الحسابات الاجتماعية',
  'social.hint': 'لينكدإن حاليًا، وستستخدم الشبكات الأخرى الشاشة نفسها عند إضافتها.',
  'social.provider.LINKEDIN': 'لينكدإن',
  'social.connect': 'ربط {provider}',
  'social.connectAnother': 'ربط حساب {provider} آخر',
  'social.reconnect': 'إعادة الربط',
  'social.notSetUp': '{provider} غير مُعدّ بعد. أضف بيانات التطبيق أدناه أولًا.',
  'social.direct.hint': 'افصل بين أكثر من واحد بفاصلة أو سطر جديد. أضف البوت مشرفًا في كل واحد منها أولًا.',
  'social.direct.submit': 'ربط',
  'social.empty': 'لا يوجد حساب اجتماعي مربوط بعد.',
  'social.connected': 'تم ربط {provider}: عُثر على {count} حساب.',
  'social.accounts': 'حسابات النشر',
  'social.account.PERSON': 'ملف شخصي',
  'social.account.ORGANIZATION': 'صفحة شركة',
  'social.inactive': 'غير متاح',
  'social.renews': 'يتجدد تلقائيًا',
  'social.reconnectWhenExpired': 'أعد الربط عند انتهائه',
  'social.apps.title': 'بيانات التطبيق',
  'social.apps.hint':
    'أنشئ تطبيق مطوّر لدى المنصة، ثم الصق معرّف العميل (Client ID) والسر هنا. تُحفظ مشفّرة ولا يُعرض السر مرة أخرى.',
  'social.apps.redirect': 'رابط إعادة التوجيه الذي يُسجَّل لدى المنصة',
  'social.apps.add': 'تسجيل تطبيق {provider}',
  'social.apps.label': 'الاسم',
  'social.apps.clientId': 'معرّف العميل (Client ID)',
  'social.apps.clientSecret': 'سر العميل (Client secret)',
  'social.apps.keepSecret': 'اتركه فارغًا للإبقاء على السر المحفوظ',
  'social.apps.secretStored': 'السر محفوظ ({fingerprint})',
  'social.apps.apiVersion': 'إصدار الواجهة (اختياري، مثل 202603)',
  'social.apps.organizations': 'صفحات الشركات معتمدة لهذا التطبيق (Community Management API)',
  'social.apps.shared': 'تطبيق المنصة المشترك',
  'social.apps.registerShared': 'سجّله كتطبيق مشترك لكل المستأجرين',
  'social.apps.own': 'هذا المستأجر',
  'social.apps.save': 'حفظ',
  'social.apps.saved': 'تم الحفظ.',
  'social.apps.delete': 'حذف',
  'social.apps.confirmDelete': 'حذف تسجيل هذا التطبيق؟',
  'social.apps.disable': 'تعطيل',
  'social.apps.enable': 'تفعيل',
  'social.apps.connections': '{count} اتصال',
  'publish.socialTitle': 'الحسابات الاجتماعية',
  'publish.socialHint': 'ملفات وصفحات لينكدإن التي ربطتها.',
  'publish.socialUnavailable': 'أعد ربطه أولًا',
  'jobs.attempts': 'المحاولات',
  'jobs.http': 'HTTP',
  'jobs.metaCode': 'كود ميتا',
  'jobs.message': 'الرسالة',
  'jobs.postId': 'معرّف المنشور',
  'jobs.resolve': 'تسوية',
  'jobs.resolvePrompt':
    'افحص الصفحة. إن وُجد المنشور، الصق معرّفه. وإن لم يُنشر شيء، اترك الحقل فارغًا.',
  'jobs.attemptNumber': '#',
  'jobs.result': 'النتيجة',
  'jobs.endpoint': 'نقطة النهاية',
  'jobs.retryable': 'قابل للإعادة',
  'jobs.when': 'الوقت',

  /* Published */
  'published.title': 'المنشورات المنشورة',
  'published.subtitle':
    'ما نشره هذا النظام. الحذف يعمل على منشورات أنشأها النظام — أما ما كُتب على فيسبوك مباشرة فيُحذف من هناك.',
  'published.empty': 'لا توجد منشورات بعد.',
  'published.post': 'المنشور',
  'published.why': 'لماذا هذا الوصول؟',
  'published.detailsFailed': 'تعذّرت قراءة هذا المنشور من ميتا',
  'published.onFacebook': 'على فيسبوك منذ',
  'published.audience': 'الجمهور',
  'published.publishedBy': 'نُشر بواسطة',
  'published.stillOnPage': 'على يوميات الصفحة',
  'published.hiddenFromTimeline': 'مخفيّ',
  'published.nothingRestricting':
    'ميتا لا تُبلغ عن أيّ قيد على هذا المنشور. كل متابعي الصفحة يمكنهم رؤيته — لكنّ فيسبوك لا يعرض كل منشور على كل متابع، فمن لم يرَه غالبًا مسألة توزيع لا إعداد.',
  'published.finding.NOT_PUBLISHED':
    'لم يُنشر بعد — ما زالت ميتا تحمله غير منشور، فهو ليس على الصفحة.',
  'published.finding.HIDDEN_FROM_TIMELINE':
    'مخفيّ عن يوميات الصفحة. يبقى الوصول إليه برابطه المباشر، ولهذا يراه بعضهم ولا يراه آخرون. أخفاه شخص على فيسبوك أو قاعدة إشراف — التطبيق لا يفعل ذلك أبدًا.',
  'published.finding.EXPIRED': 'كان لهذا المنشور وقت انتهاء وقد مضى.',
  'published.finding.AUDIENCE_LIMITED':
    'ميتا تُبلغ عن تحديد جمهور على هذا المنشور. من هو خارجه لا يستطيع رؤيته إطلاقًا. ولا شيء في هذا التطبيق يضع تحديدًا كهذا.',
  'published.finding.RESTRICTED_PRIVACY': 'الجمهور ليس عامًّا.',
  'published.finding.TARGETING_UNREADABLE':
    'لم تُخبرنا ميتا إن كان على المنشور تحديد جمهور، فهذا السؤال وحده بلا جواب هنا.',
  'published.publishedAt': 'تاريخ النشر',
  'published.confirmDelete': 'حذف هذا المنشور نهائيًا من «{page}»؟\n\n{id}\n\nلا يمكن التراجع.',
  'published.deleteFailed': 'تعذّر الحذف',
  'published.like': 'إعجاب',
  'published.unlike': 'إلغاء الإعجاب',
  'published.likeHint':
    'يضع إعجابًا عاديًا باسم الصفحة. لا توفّر ميتا طريقة لاختيار تفاعل (إعجاب قوي، إعجاب مضحك…) عبر الواجهة البرمجية.',
  'published.likeFailed': 'تعذّر الإعجاب بهذا المنشور',
  'published.unlikeFailed': 'تعذّرت إزالة الإعجاب',

  /* Comments */
  'comments.title': 'التعليقات',
  'comments.subtitle': 'انشر تعليقات باسم الصفحة، على منشورات صفحات تديرها. يتطلب مهمة MODERATE.',
  'comments.selectPage': 'الصفحة',
  'comments.selectPagePlaceholder': 'اختر صفحة…',
  'comments.noModerate': 'لا تمنح أي من صفحاتك مهمة MODERATE التي تشترطها ميتا.',
  'comments.selectPost': 'المنشور المراد التعليق عليه',
  'comments.selectPostPlaceholder': 'اختر منشورًا…',
  'comments.drafts': 'التعليقات المراد نشرها',
  'comments.placeholder': 'تعليقك…',
  'comments.followUp': 'تعليق متابع {n}',
  'comments.addAnother': 'إضافة تعليق آخر',
  'comments.pacing':
    'ستُنشر {count} تعليقات بالترتيب، بفاصل {seconds} ثانية تقريبًا. نشرها دفعة واحدة يبدو نشاطًا آليًا لميتا.',
  'comments.publish': 'انشر {count} تعليق',
  'comments.queued':
    'أُدرج {count} تعليق على «{page}». بفاصل {seconds} ثانية، وينتهي خلال {minutes} دقيقة تقريبًا.',
  'comments.replyTo': 'الرد على تعليق قائم (اختياري)',
  'comments.replyToPost': 'التعليق على المنشور مباشرة',
  'comments.yourPage': 'صفحتك',
  'comments.repliesDisabled':
    'الرد على تعليقات الآخرين يتطلب صلاحية pages_read_user_content ومراجعة تطبيق خاصة بها. حتى ذلك الحين يمكنك إضافة تعليقاتك على المنشور.',

  /* Automation */
  'automation.title': 'الأتمتة',
  'automation.tab.queue': 'طابور المراجعة',
  'automation.tab.agents': 'الوكلاء',
  'automation.tab.performance': 'الأداء',
  'automation.queueEmpty': 'لا شيء ينتظر المراجعة.',
  'automation.queueHint':
    'لم يُنشر أي شيء هنا. وافق لنشر الرد باسم الصفحة، أو تجاهل لترك التعليق بلا رد.',
  'automation.proposedReply': 'الرد المقترح',
  'automation.confidence': 'الثقة',
  'automation.noReply': 'لم ينتج الوكيل ردًا. تجاهله أو أجب من فيسبوك مباشرة.',
  'automation.approve': 'موافقة ونشر',
  'automation.approveEdited': 'نشر الرد المعدَّل',
  'automation.dismiss': 'تجاهل',
  'automation.disabledNotice':
    'أتمتة التعليقات معطّلة. فعّلها في الإعدادات وأعد ربط كل حساب ميتا — اشتراك الصفحة في Webhooks يتطلب صلاحية pages_manage_metadata.',
  'automation.agentName': 'الاسم',
  'automation.agentMode': 'النمط',
  'automation.agentEndpoint': 'نقطة النهاية',
  'automation.agentCredential': 'بيانات الاعتماد',
  'automation.agentCallbackSecret': 'سر الاستدعاء',
  'automation.agentStored': 'مخزَّن (مشفّر)',
  'automation.addAgent': 'إضافة وكيل',
  'automation.modeSyncLabel': 'SYNC — الرد في نفس الاستجابة',
  'automation.modeAsyncLabel': 'ASYNC — يستدعينا عند الجاهزية',
  'automation.modeHint': 'النمطان مدعومان لتقارنهما في تبويب الأداء وتبقي الأنسب لوكيلك.',
  'automation.bearerToken': 'توكن Bearer (اختياري)',
  'automation.bearerHint': 'مشفّر عند التخزين ولا يُعرض مجددًا.',
  'automation.agentKey': 'مفتاح الوكيل / قاعدة المعرفة (اختياري)',
  'automation.callbackSecret': 'سر الاستدعاء (مطلوب لنمط ASYNC)',
  'automation.callbackSecretHint':
    'يوقّع وكيلك جسم الاستدعاء بهذا المفتاح عبر HMAC-SHA256 ويرسله في ترويسة X-Signature-256.',
  'automation.callbackUrl': 'عنوان الاستدعاء',
  'automation.saveAgent': 'حفظ الوكيل',
  'automation.perfWindow':
    'آخر {days} يومًا. «الرفض» يُحتسب نتيجة صحيحة — معرفة الوكيل أنه لا ينبغي أن يجيب ليست فشلًا.',
  'automation.perfEmpty': 'لا توجد استدعاءات للوكيل في آخر ٣٠ يومًا.',
  'automation.calls': 'الاستدعاءات',
  'automation.answered': 'أجاب',
  'automation.declined': 'رفض',
  'automation.empty': 'فارغ',
  'automation.timedOut': 'انتهت المهلة',
  'automation.failedCount': 'فشل',
  'automation.successRate': 'معدل النجاح',
  'automation.avgMs': 'متوسط (مللي)',
  'automation.p95Ms': 'p95 (مللي)',
  'automation.avgReply': 'متوسط الرد',

  /* Logs */
  'logs.title': 'السجلات',
  'logs.tab.calls': 'نداءات Graph API',
  'logs.tab.audit': 'سجل التدقيق',
  'logs.redactionNotice':
    'لا تُكتب التوكنات في هذه الجداول إطلاقًا. تُسجَّل نقاط النهاية بعد إزالة المعاملات السرية.',
  'logs.rateUsage': 'استهلاك حد ميتا — النداءات {calls}٪، المعالج {cpu}٪، الوقت {time}٪',
  'logs.ratePaused': ' — النشر متوقف حتى ينخفض',
  'logs.failuresOnly': 'عرض الإخفاقات فقط',
  'logs.method': 'الطريقة',
  'logs.endpoint': 'نقطة النهاية',
  'logs.duration': 'مللي',
  'logs.actor': 'المنفِّذ',
  'logs.action': 'الإجراء',
  'logs.entity': 'الكيان',
  'logs.detail': 'التفاصيل',

  /* Admin console */
  'admin.title': 'إدارة المنصة',
  'admin.tab.permissions': 'صلاحيات ميتا',
  'admin.permissions.intro':
    'لا يُطلب من العميل إلا ما كان موافقًا ومنشورًا. والباقي يُعرَض على إدارة المنصّة ومراجع ميتا وحدهما، لأنّ طلب صلاحية لم تعتمدها ميتا من عميل قد يجعل ميتا ترفض نافذة الربط كلّها. غيِّر الحالة بعد أن تعمل بقرار ميتا — لا شيء هنا يتغيّر من تلقاء نفسه.',
  'admin.permissions.scope': 'الصلاحية',
  'admin.permissions.channel': 'القناة',
  'admin.permissions.status': 'الحالة',
  'admin.permissions.lastCustomer': 'آخر منح لعميل',
  'admin.permissions.neverCustomer': 'لم يحدث',
  'admin.permissions.note': 'ملاحظة',
  'admin.permissions.IN_DEVELOPMENT': 'تحت التطوير',
  'admin.permissions.PENDING_APPROVAL': 'بانتظار ميتا',
  'admin.permissions.PUBLISHED': 'موافق ومنشور',
  'admin.tab.dashboard': 'لوحة المعلومات',
  'admin.tab.tenants': 'الحسابات',
  'admin.tab.plans': 'الباقات',
  'admin.superAdminOnly': 'هذا القسم يتطلب صلاحية مدير المنصة.',
  'admin.active': 'نشط',
  'admin.suspended': 'موقوف',
  'admin.stat.tenants': 'الحسابات',
  'admin.stat.users': 'المستخدمون',
  'admin.stat.connections': 'اتصالات ميتا',
  'admin.stat.pages': 'الصفحات',
  'admin.stat.posts': 'منشورات قائمة',
  'admin.stat.agentCalls': 'استدعاءات الوكيل (الشهر)',
  'admin.stat.replies': 'ردود آلية (الشهر)',
  'admin.stat.jobs': 'العمليات',
  'admin.planDistribution': 'توزيع الحسابات على الباقات',
  'admin.plan': 'الباقة',
  'admin.tenant': 'الحساب',
  'admin.tenantsOnPlan': 'حساب',
  'admin.newTenant': 'إنشاء حساب',
  'admin.tenantName': 'اسم الحساب',
  'admin.tenantSlug': 'المعرّف',
  'admin.slugHint': 'حروف إنجليزية صغيرة وأرقام وشرطات. لا يمكن تغييره لاحقًا.',
  'admin.selectPlan': 'اختر باقة…',
  'admin.createTenant': 'إنشاء الحساب',
  'admin.tenantCreated': 'أُنشئ الحساب.',
  'admin.planChanged': 'تغيّرت الباقة.',
  'admin.statusChanged': 'تغيّرت الحالة.',
  'admin.suspend': 'إيقاف',
  'admin.reactivate': 'إعادة التفعيل',
  'admin.suspendPrompt': 'ما سبب إيقاف هذا الحساب؟ سيظهر لمستخدميه.',
  'admin.plansHint':
    'الحدود أرقام، والمزايا تحدد ما تبيعه الباقة. الميزة تحتاج أيضًا أن يدعمها التثبيت نفسه — انظر صفحة الحساب.',
  'admin.planSaved': 'حُفظت الباقة.',
  'admin.entitlements': 'المزايا المشمولة',
  'admin.price': 'السعر / شهريًا',
  'admin.limit.maxMetaAccounts': 'حسابات ميتا',
  'admin.limit.maxPages': 'الصفحات',
  'admin.limit.maxUsers': 'المستخدمون',
  'admin.limit.maxAgentCallsPerMonth': 'استدعاءات الوكيل / شهر',
  'admin.limit.maxAutoRepliesPerMonth': 'ردود آلية / شهر',
  'admin.feature.featurePublishing': 'النشر',
  'admin.feature.featureComments': 'التعليقات',
  'admin.feature.featureCommentReplies': 'الرد على الزوار',
  'admin.feature.featureAutomation': 'أتمتة الذكاء الاصطناعي',
  'admin.feature.featurePublicSource': 'مصادر الصفحات العامة',
  'admin.feature.featureApiAccess': 'الوصول عبر API',

  /* Account */
  'account.title': 'الحساب',
  'account.tab.overview': 'نظرة عامة',
  'account.tab.me': 'حسابي',
  'account.tab.users': 'المستخدمون',
  'account.tab.keys': 'مفاتيح API',
  'account.currentPlan': 'الباقة الحالية',
  'account.usage': 'الاستهلاك',
  'account.usage.metaAccounts': 'حسابات ميتا',
  'account.usage.pages': 'الصفحات',
  'account.usage.users': 'المستخدمون',
  'account.usage.agentCalls': 'استدعاءات الوكيل هذا الشهر',
  'account.usage.autoReplies': 'الردود الآلية هذا الشهر',
  'account.usage.metaApps': 'تطبيقات Meta الخاصة بك',
  'admin.unlimited': 'بلا حد',
  'account.usage.postsPerDay': 'منشورات اليوم',
  'account.usage.commentsPerDay': 'تعليقات اليوم',
  'account.period.DAY': '(يوميًا)',
  'account.period.MONTH': '(شهريًا)',
  'account.quotaWarning': 'اقتراب من الحد: {list}',
  'account.features': 'المزايا',
  'account.featuresHint':
    'الميزة تعمل فقط إذا سمحت الطبقات الثلاث: التثبيت قادر عليها، وباقتك تشملها، وأنت فعّلتها.',
  'account.feature': 'الميزة',
  'account.layer.capability': 'التثبيت',
  'account.layer.entitlement': 'الباقة',
  'account.layer.setting': 'إعدادك',
  'account.blockedBy.CAPABILITY': 'غير متاحة',
  'account.blockedBy.ENTITLEMENT': 'ليست في باقتك',
  'account.blockedBy.SETTING': 'معطّلة',
  'account.blockedBy.UNRELEASED': 'قريبًا',
  'account.featureName.PUBLISHING': 'نشر المنشورات',
  'account.featureName.COMMENTS': 'نشر التعليقات',
  'account.featureName.COMMENT_REPLIES': 'الرد على الزوار',
  'account.featureName.AUTOMATION': 'أتمتة الذكاء الاصطناعي',
  'account.featureName.PUBLIC_SOURCE': 'مصادر الصفحات العامة',
  'account.featureName.API_ACCESS': 'الوصول عبر API',
  'account.featureName.OWN_META_APP': 'تطبيق Meta الخاص بك',
  'account.user': 'المستخدم',
  'account.role': 'الدور',
  'account.role.ADMIN': 'مدير',
  'account.role.SUPERADMIN': 'مدير المنصّة',
  'account.role.OPERATOR': 'مشغّل',
  'account.role.PUBLISHER': 'ناشر',
  'account.role.REVIEWER': 'مراجع ميتا',
  'account.role.VIEWER': 'مطّلع',
  'account.pages.assign': 'الصفحات',
  'account.pages.hint':
    'لا يرى هذا الشخص إلا الصفحات المُحدَّدة هنا — رسائلها وتعليقاتها ومحادثاتها.',
  'account.pages.saved': 'حُدِّثت الصفحات.',
  'account.pages.empty': 'لم تُحدَّد أي صفحة: لن يرى هذا الشخص شيئًا.',
  'account.pages.none': 'لا توجد صفحة مرتبطة بهذا الحساب بعد.',
  'account.activeUser': 'نشط',
  'account.inactiveUser': 'غير نشط',
  'account.activate': 'تفعيل',
  'account.deactivate': 'تعطيل',
  'account.addUser': 'إضافة مستخدم',
  'account.displayName': 'الاسم',
  'account.passwordHint': '١٢ حرفًا على الأقل.',
  'account.userCreated': 'أُضيف المستخدم.',
  'account.userUpdated': 'حُدّث المستخدم.',
  'account.adminOnly': 'هذا القسم يتطلب صلاحية مدير.',
  'account.keysHint':
    'لاستدعاء هذا النظام من برمجياتك. يُعرض المفتاح مرة واحدة ويُخزَّن كبصمة فقط.',
  'account.keyName': 'اسم المفتاح',
  'account.createKey': 'إنشاء مفتاح',
  'account.keyIssued': 'مفتاح API الجديد',
  'account.keyMatrix': 'ما الذي يستطيعه هذا المفتاح',
  'account.keyMatrixHint':
    'المفتاح لا يفعل شيئًا حتّى تأذن له هنا. ضع علامة على خدمة أمام صفحة فيملكها المفتاح في تلك الصفحة وحدها — ولا يصل إلى شيء آخر في الحساب.',
  'account.keyAllPages': 'كلّ الصفحات',
  'account.keyAllPagesHint':
    'بما فيها صفحات تربطها لاحقًا. اخترها لوكيل تُعِدّه مرّة ثمّ تتركه يعمل.',
  'account.keyCap.PAGES_READ': 'عرض الصفحات',
  'account.keyCap.PUBLISH': 'نشر المنشورات',
  'account.keyCap.INBOX_READ': 'قراءة البريد الوارد',
  'account.keyCap.INBOX_REPLY': 'الردّ على البريد الوارد',
  'account.keyCap.INSIGHTS_READ': 'قراءة التحليلات',
  'account.keyCap.POSTS_READ': 'عرض المنشورات المنشورة',
  'account.keyCap.GENERATION_WRITE': 'توليد المحتوى (الاستوديو)',
  'account.keyAutoSend': 'إرسال الردود دون مراجعة',
  'account.keyAutoSendHint':
    'إن كانت مطفأة حُفظ الردّ مسوّدةً في البريد الوارد ويضغط إنسانٌ إرسال. وإن كانت مضاءة وصل كلام الوكيل إلى العميل دون أن يقرأه أحد.',
  'account.keyExpiry': 'ينتهي بعد (أيّام)',
  'account.keyExpiryNever': 'لا ينتهي',
  'account.keyNoGrants': 'ضع علامة على خدمة واحدة على الأقلّ. مفتاح بلا صلاحية لا يفعل شيئًا.',
  'account.keyNoPages': 'اربط صفحةً أوّلًا. لا يوجد بعدُ ما تمنحه لمفتاح.',
  'account.keyGrantsColumn': 'الصلاحيات',
  'account.keyGrantCount': '{count} على {pages}',
  'account.keyExpired': 'منتهٍ',
  'account.keyDocs': 'كيف يستعمله وكيل من خارج المنصّة',
  'account.keyExpiresColumn': 'ينتهي في',
  'account.keyNoExpiry': 'لا ينتهي',
  'account.keyShow': 'أظهر المفتاح',
  'account.keyShown': 'هذا المفتاح، والرابط المبنيّ عليه',
  'account.keyValue': 'المفتاح — يُرسَل في الترويسة: Authorization: Bearer …',
  'account.keyConnectorUrl':
    'رابط الموصّل — الصقه في كلود: Customize \u2190 Connectors \u2190 Add custom connector',
  'account.keyConnectorHint':
    'اترك خانات OAuth فارغة. الرابط نفسه هو بيانات الاعتماد، فيمنح مصفوفة هذا المفتاح بالضبط ولا شيء غيرها \u2014 وإلغاء المفتاح يوقفه في الحال.',
  'account.tab.apps': 'تطبيق ميتا',
  'account.appsHint':
    'كل ربط يمرّ افتراضيًّا عبر تطبيق ميتا الخاصّ بالمنصّة. تسجيل تطبيقك يمنحك مراجعتك أنت، وحدّك أنت في المعدّل، وتوثيق نشاطك التجاري أنت.',
  'account.appsUsingPlatform':
    'الروابط تمرّ حاليًّا عبر تطبيق المنصّة. لا شيء مطلوب ما لم ترد تطبيقك الخاصّ.',
  'account.addApp': 'سجّل تطبيقًا',
  'account.addAppHint':
    'من لوحة تطبيق ميتا: الإعدادات \u2190 الأساسية. يُخزَّن السرّ مشفَّرًا ولا يُعرض مرّة أخرى. والصق رابط الاستدعاء الظاهر أعلاه في إعداد الويب هوك للتطبيق.',
  'account.appLabel': 'اسم تعرفه',
  'account.appId': 'معرّف التطبيق',
  'account.appSecret': 'سرّ التطبيق',
  'account.appLoginConfig': 'معرّف تكوين الدخول (اختياري)',
  'account.appVerified': 'موثَّق لدى ميتا',
  'account.appInvalid': 'ميتا رفضت هذه البيانات',
  'account.appVerifying': 'جارٍ التحقّق لدى ميتا\u2026',
  'account.appDefault': 'الافتراضي',
  'account.appConnections': 'الروابط',
  'account.appInUse': 'افصل حسابات ميتا المرتبطة به أولًا \u2014 رموزها تتوقّف بذهاب التطبيق.',
  'account.keyOnce': 'انسخه الآن — لن يُعرض مجددًا.',
  'account.keyPrefix': 'البادئة',
  'account.lastUsed': 'آخر استخدام',
  'account.revoke': 'إبطال',
  'account.revoked': 'مُبطَل',

  /* Extra tabs */
  'tab.account': 'الحساب',
  'tab.admin': 'الإدارة',
  'tab.studio': 'الاستوديو',

  /* استوديو إنتاج المحتوى */
  'studio.title': 'استوديو المحتوى',
  'studio.tab.compose': 'الإنشاء',
  'studio.tab.library': 'الأرشيف',
  /* Usage and cost statistics. */
  'studio.tab.usage': 'الاستهلاك والتكلفة',
  'usage.title': 'الاستهلاك والتكلفة',
  'usage.estimateNote': 'كل الأرقام هنا تقديرية، محسوبة من الأسعار المحددة على كل موصّل، وليست فاتورة.',
  'usage.totalCost': 'التكلفة التقديرية',
  'usage.jobsDone': 'مهام مكتملة',
  'usage.jobsFailed': 'مهام فشلت (بلا تكلفة مسجلة)',
  'usage.unpriced': 'غير محسوبة',
  'usage.unpricedHint':
    '{count} مهمة مكتملة بلا سعر، لذلك هي غير داخلة في التكلفة. حدّد السعر على موصّلها من الأتمتة ثم موصّلات الاستوديو ثم الإعدادات المتقدمة، وستُحسب المهام اللاحقة.',
  'usage.allPriced': 'لكل مهمة مكتملة سعر محدد.',
  'usage.testCost': 'اختبارات الموصّلات (تُحتسب عليك وتُعرض منفصلة): {cost} في {count} تشغيل',
  'usage.notPriced': 'غير محسوبة',
  'usage.partial': '+{count} غير محسوبة',
  'usage.chartTitle.day': 'التكلفة اليومية',
  'usage.chartTitle.week': 'التكلفة الأسبوعية',
  'usage.chartTitle.month': 'التكلفة الشهرية',
  'usage.chartEmpty': 'لم يُصرف شيء في هذه الفترة.',
  'usage.chartAria': 'رسم أعمدة للتكلفة التقديرية عبر الزمن، أعلى عمود {max}',
  'usage.bar': '{day}: {cost}، {count} مهمة',
  'usage.byConnector': 'حسب الموصّل',
  'usage.byProvider': 'حسب المزوّد',
  'usage.byDestination': 'حسب الصفحة أو الشبكة',
  'usage.byProject': 'أغلى المشاريع',
  'usage.col.name': 'الاسم',
  'usage.col.provider': 'المزوّد',
  'usage.col.kind': 'النوع',
  'usage.col.jobs': 'المهام',
  'usage.col.failed': 'الفاشلة',
  'usage.col.cost': 'التكلفة',
  'usage.col.used': 'المستهلك',
  'usage.col.projects': 'المشاريع',
  'usage.col.network': 'الشبكة',
  'usage.unit.seconds': '{value} ث',
  'usage.unit.images': '{value} صورة',
  'usage.unit.chars': '{value} حرف',
  'usage.unit.tokens': 'دخل {input} / خرج {output} رمز',
  'usage.attribution':
    'المشروع المنشور في عدة صفحات أو شبكات يُحتسب تحت كل منها، لذلك مجموع هذه الصفوف أكبر من الإجمالي. المصروف على مشاريع لم تُنشر في أي مكان: {cost}.',
  'usage.destinationsEmpty': 'لم يُنشر بعد أي مشروع أُنجز في هذه الفترة.',
  'usage.empty': 'لم يُنتج شيء في هذه الفترة.',
  'studio.cost.estimated': 'التكلفة {cost} (تقديرية)',
  'studio.cost.partial': 'التكلفة {cost}+ (تقديرية، وبعض الخطوات غير محسوبة)',
  'studio.cost.unpriced': 'التكلفة: غير محسوبة',
  'studioConnectors.pricing.title': 'السعر، لإحصاءات التكلفة',
  'studioConnectors.pricing.hint':
    'ما يتقاضاه المزوّد، ليقدّر الاستوديو تكلفة كل نتيجة. اتركه فارغًا إن كنت لا تعرفه، فتقول الإحصاءات «غير محسوبة» بدل التخمين. تغييره لا يحتاج اختبارًا جديدًا.',
  'studioConnectors.pricing.unit': 'يُحتسب لكل',
  'studioConnectors.pricing.unit.none': 'بلا سعر',
  'studioConnectors.pricing.unit.SECOND': 'ثانية فيديو',
  'studioConnectors.pricing.unit.IMAGE': 'صورة',
  'studioConnectors.pricing.unit.CHARS_10K': 'عشرة آلاف حرف',
  'studioConnectors.pricing.unit.TOKENS_1M': 'مليون رمز',
  'studioConnectors.pricing.unit.REQUEST': 'طلب',
  'studioConnectors.pricing.price': 'السعر بالدولار',
  'studioConnectors.pricing.inputPrice': 'المدخلات، دولار لكل مليون رمز',
  'studioConnectors.pricing.outputPrice': 'المخرجات، دولار لكل مليون رمز',
  'studioConnectors.pricing.soundPrice': 'مع الصوت، دولار لكل ثانية (اختياري)',
  'studioConnectors.pricing.byResolution': 'سعر الثانية حسب الدقة (اختياري، يحل محل السعر أعلاه)',
  'studioConnectors.pricing.invalid': 'يجب أن تكون الأسعار أرقامًا لا تقل عن صفر.',
  'studioConnectors.pricing.usageTitle': 'أين يذكر الرد ما استُهلك',
  'studioConnectors.pricing.usageHint':
    'مسارات اختيارية داخل رد المزوّد، تُكتب مثل مسار النتيجة. إن وُجدت استُخدمت أعداد الرموز أو الثواني الفعلية بدل التقدير. اتركها فارغة إن لم تكن متأكدًا.',
  'studioConnectors.pricing.usageInput': 'مسار رموز المدخلات',
  'studioConnectors.pricing.usageOutput': 'مسار رموز المخرجات',
  'studioConnectors.pricing.usageSeconds': 'مسار الثواني',
  'studioConnectors.pricing.tag.SECOND': '/ث',
  'studioConnectors.pricing.tag.IMAGE': '/صورة',
  'studioConnectors.pricing.tag.CHARS_10K': '/10k حرف',
  'studioConnectors.pricing.tag.TOKENS_1M': ' لكل مليون رمز',
  'studioConnectors.pricing.tag.REQUEST': '/طلب',
  'studio.idea': 'فكرتك',
  'studio.ideaPlaceholder': 'صف ما تريد إنتاجه — كلما كانت الفكرة أوضح كان النص المُنتَج أدق.',
  'studio.attachments': 'الملفات المرجعية',
  'studio.uploading': 'جارٍ الرفع…',
  'studio.attachmentsCapped': 'باقتك تحدّد عدد الملفات المرجعية التي يحملها الطلب الواحد.',
  'studio.uploadFailed': 'تعذّر رفع هذا الملف',
  'studio.pasteScriptToggle': 'لديّ نصّ جاهز — دعني ألصقه',
  'studio.pastedScript': 'النصّ',
  'studio.pastedScriptPlaceholder': 'الصق النصّ هنا',
  'studio.generateScript': 'إنشاء النصّ',
  'studio.scriptResult': 'النصّ',
  'studio.audioSection': 'الصوت',
  'studio.generateAudio': 'إنشاء الصوت',
  'studio.audioResult': 'الصوت',
  'studio.videoSection': 'الفيديو أو الصورة',
  'studio.includeAudio': 'أدرج الصوت المُنتَج في الفيديو',
  'studio.generateVideo': 'إنشاء الفيديو',
  'studio.generateImage': 'إنشاء الصورة',
  'studio.videoResult': 'الفيديو',
  'studio.imageResult': 'الصورة',
  'studio.jobRunning': 'جارٍ العمل عليه…',
  'studio.jobFailed': 'تعذّر على الموصّل إنتاج هذا',
  'studio.previousFeedback': 'التعديل المطلوب: {text}',
  'studio.approve': 'اعتماد',
  'studio.reviseWithFeedback': 'طلب تعديل',
  'studio.sendToPublish': 'إرسال إلى النشر',
  'studio.copyLink': 'نسخ الرابط',
  'studio.download': 'تنزيل',
  'studio.linkReady': 'نُسخ الرابط — صالح لمدة 24 ساعة:',
  'studio.feedbackPlaceholder': 'ما الذي تريد تغييره؟',
  'studio.submitRevision': 'إرسال',
  'studio.libraryEmpty': 'لا شيء أُنشئ بعد — ابدأ من تبويب الإنشاء.',
  'studio.capability.SCRIPT': 'نصّ',
  'studio.capability.AUDIO': 'صوت',
  'studio.capability.VIDEO': 'فيديو',
  'studio.capability.IMAGE': 'صورة',
  'studio.revisionEntry': '{date} — {feedback}',
  /* مشروع واحد: الطلب وكل ما أُنتج منه. */
  'studio.project.savedNote': 'حُفظ هذا الطلب كمشروع في الأرشيف.',
  'studio.project.newRequest': 'طلب جديد',
  'studio.project.loadMore': 'عرض المزيد',
  'studio.project.inProgress': 'قيد التنفيذ',
  'studio.project.published': 'منشور',
  'studio.project.open': 'فتح',
  'studio.project.count.text': '{count} نص',
  'studio.project.count.image': '{count} صورة',
  'studio.project.count.audio': '{count} صوت',
  'studio.project.count.video': '{count} فيديو',
  'studio.project.back': 'العودة إلى الأرشيف',
  'studio.project.archive': 'أرشفة',
  'studio.project.archiveConfirm': 'أرشفة هذا المشروع؟ سيختفي من القائمة، ولن تُحذف الملفات نفسها.',
  'studio.project.request': 'الطلب',
  'studio.project.outputs': 'النتائج',
  'studio.project.noOutputs': 'لم يُنتج شيء لهذا الطلب بعد.',
  'studio.project.textHeading': 'النص',
  'studio.project.imageHeading': 'الصور',
  'studio.project.audioHeading': 'الصوت',
  'studio.project.videoHeading': 'مقاطع الفيديو',
  'studio.project.plansHeading': 'فيديوهات أطول',
  'studio.project.useText': 'استخدام هذا النص في المنشور',
  'studio.project.useMedia': 'تضمين هذا في المنشور',
  'studio.project.noText': 'بدون نص',
  'studio.project.audioNotSent': 'لا يمكن نشر الصوت وحده؛ يُستخدم داخل الفيديو.',
  'studio.project.earlierVersions': 'نسخ سابقة ({count})',
  'studio.project.readyTitle': 'ما سيُرسل',
  'studio.project.sendText': 'النص',
  'studio.project.sendMedia': 'الملفات',
  'studio.project.noMedia': 'بدون ملفات',
  'studio.project.kind.image': 'صورة',
  'studio.project.kind.video': 'فيديو',
  'studio.project.kind.audio': 'صوت',
  'studio.project.postRule': 'يحمل المنشور صورًا أو فيديو واحدًا، وليس الاثنين معًا. حدّد ما سيُنشر.',
  'studio.project.publishing': 'النشر',
  'studio.project.notPublished': 'لم يُنشر بعد.',
  'studio.project.mode.NEW_POST': 'منشور جديد',
  'studio.project.mode.ATTACHED': 'أُرفق بمنشور مجدول',
  'studio.project.seeInPosts': 'عرض المنشورات المجدولة',
  /* ابدأ من المنتج النهائي ثم اضبط الخيارات. */
  'studio.mode.goal': 'موجَّه',
  'studio.mode.free': 'وضع حر',
  'studio.mode.freeHint': 'الوضع الحر: اكتب وانطق وصوّر خطوة بخطوة دون تحديد هدف.',
  'studio.goal.title': 'ماذا تريد أن تُنتج؟',
  'studio.goal.POST': 'منشور',
  'studio.goal.POST.desc': 'نصّ جاهز للنشر على شبكاتك، ويمكن أن تصحبه صورة أو فيديو.',
  'studio.goal.IMAGE_PROMPT': 'برومت صورة',
  'studio.goal.IMAGE_PROMPT.desc': 'وصف دقيق يُسلَّم لنموذج توليد الصور.',
  'studio.goal.VIDEO_FROM_TEXT': 'سيناريو وبرومت فيديو من نص',
  'studio.goal.VIDEO_FROM_TEXT.desc': 'سيناريو ووصف للمشاهد لفيديو يُصنع من كلماتك وحدها.',
  'studio.goal.VIDEO_FROM_IMAGE': 'سيناريو وبرومت فيديو من صورة أو صور',
  'studio.goal.VIDEO_FROM_IMAGE.desc': 'سيناريو يُحيي الصورة أو الصور التي ترفقها.',
  'studio.goal.AUDIO_TEXT': 'نص للتحويل إلى صوت',
  'studio.goal.AUDIO_TEXT.desc': 'نصّ مكتوب ليُقرأ بصوت عالٍ، بتشكيل ولهجة تختارها.',
  'studio.followUp.label': 'اجعل معه',
  'studio.followUp.hint': 'تُخطَّط الصورة أو الفيديو في الطلب نفسه ومن الفكرة نفسها.',
  'studio.followUp.NONE': 'بلا',
  'studio.followUp.IMAGE': 'صورة',
  'studio.followUp.VIDEO_FROM_TEXT': 'فيديو من نص',
  'studio.followUp.VIDEO_FROM_IMAGE': 'فيديو من صورة',
  'studio.goal.requestLabel': 'طلبك',
  'studio.goal.requestPlaceholder': 'قل ما تريد ولمن هو موجَّه — كلما كان الطلب أوضح كانت النتيجة أدق.',
  'studio.att.title': 'المراجع',
  'studio.att.hint': 'ارفع ملفًا أو الصق رابط https عامًّا، وحدّد الغرض من كل مرجع.',
  'studio.att.upload': 'رفع ملف',
  'studio.att.urlPlaceholder': 'https://…',
  'studio.att.addUrl': 'إضافة الرابط',
  'studio.att.urlInvalid': 'يجب أن يكون الرابط عنوان https عامًّا.',
  'studio.att.role': 'ما غرضه؟',
  'studio.att.link': 'رابط',
  'studio.role.LOGO': 'شعار',
  'studio.role.CHARACTER': 'شخصية',
  'studio.role.PRODUCT': 'منتج',
  'studio.role.FIRST_FRAME': 'الإطار الأول',
  'studio.role.LAST_FRAME': 'الإطار الأخير',
  'studio.role.STYLE': 'أسلوب',
  'studio.role.KNOWLEDGE': 'معرفة خلفية',
  'studio.role.REFERENCE': 'مرجع عام',
  'studio.opt.title': 'الخيارات',
  'studio.opt.hint': 'كل خيار مضبوط مسبقًا على قيمة معقولة. غيّر ما يهمّك فقط.',
  'studio.opt.group.language': 'اللغة واللهجة والتعليمات',
  'studio.opt.group.textOnMedia': 'النص على الصورة أو الفيديو',
  'studio.opt.group.post': 'المنشور',
  'studio.opt.group.image': 'الصورة',
  'studio.opt.group.video': 'الفيديو',
  'studio.opt.group.audio': 'الصوت',
  'studio.opt.language': 'اللغة',
  'studio.opt.language.hint': 'لغة النص النهائي.',
  'studio.opt.language.ar': 'العربية',
  'studio.opt.language.en': 'الإنجليزية',
  'studio.opt.dialect': 'اللهجة',
  'studio.opt.dialect.hint': 'تنطبق على العربية. الفصحى رسمية، وغيرها يشبه كلام المنطقة.',
  'studio.opt.dialect.MSA': 'الفصحى',
  'studio.opt.dialect.SAUDI_NAJDI': 'سعودية (نجدية)',
  'studio.opt.dialect.SAUDI_HIJAZI': 'سعودية (حجازية)',
  'studio.opt.dialect.GULF': 'خليجية',
  'studio.opt.dialect.EGYPTIAN': 'مصرية',
  'studio.opt.dialect.LEVANTINE': 'شامية',
  'studio.opt.dialect.MAGHREBI': 'مغاربية',
  'studio.opt.diacritics': 'التشكيل',
  'studio.opt.diacritics.hint': 'على الكلمات التي ستُنطق، ليقرأها الصوت قراءة صحيحة.',
  'studio.opt.diacritics.NONE': 'بلا تشكيل',
  'studio.opt.diacritics.PARTIAL': 'جزئي — الكلمات الملتبسة فقط',
  'studio.opt.diacritics.FULL': 'كامل',
  'studio.opt.extra': 'تعليمات إضافية',
  'studio.opt.extra.hint': 'أي شيء آخر يلتزم به الكاتب: أسماء يتجنبها، شعار، قاعدة.',
  'studio.opt.textOnMedia': 'الكلمات داخل الصورة',
  'studio.opt.textOnMedia.hint':
    'هل تظهر كلمات داخل الصورة أو الفيديو نفسه. النماذج كثيرًا ما تخطئ في إملاء العربية، فحدّد ذلك صراحةً.',
  'studio.opt.textOnMedia.NONE': 'بلا نص في الصورة',
  'studio.opt.textOnMedia.EXACT': 'هذه الكلمات بالضبط',
  'studio.opt.textOnMedia.TITLE': 'عنوان قصير فقط',
  'studio.opt.textOnMedia.text': 'الكلمات',
  'studio.opt.textOnMedia.text.hint': 'تُكتب في الصورة كما هي تمامًا.',
  'studio.opt.post.length': 'الطول',
  'studio.opt.post.length.hint': 'كم يطول المنشور.',
  'studio.opt.post.length.SHORT': 'قصير',
  'studio.opt.post.length.MEDIUM': 'متوسط',
  'studio.opt.post.length.LONG': 'طويل',
  'studio.opt.post.tone': 'النبرة',
  'studio.opt.post.tone.hint': 'كيف يبدو المنشور للقارئ.',
  'studio.opt.post.tone.FORMAL': 'رسمية',
  'studio.opt.post.tone.FRIENDLY': 'ودّية',
  'studio.opt.post.tone.EXCITED': 'متحمسة',
  'studio.opt.post.tone.PERSUASIVE': 'إقناعية',
  'studio.opt.post.tone.INFORMATIVE': 'معلوماتية',
  'studio.opt.post.hashtags': 'إضافة وسوم',
  'studio.opt.post.hashtags.hint': 'بضعة وسوم مناسبة في النهاية.',
  'studio.opt.post.emojis': 'استخدام الرموز التعبيرية',
  'studio.opt.post.emojis.hint': 'بخفة، وليس في كل سطر.',
  'studio.opt.post.callToAction': 'ختم بدعوة لاتخاذ إجراء',
  'studio.opt.post.callToAction.hint': 'يُخبر القارئ بما يفعله بعد ذلك.',
  'studio.opt.aspect': 'الشكل (نسبة العرض إلى الارتفاع)',
  'studio.opt.aspect.hint': 'اختر شكل الشبكة التي ستنشر عليها.',
  'studio.opt.aspect.9:16': '9:16 — طولي (ريلز وقصص)',
  'studio.opt.aspect.16:9': '16:9 — عريض (يوتيوب والويب)',
  'studio.opt.aspect.1:1': '1:1 — مربّع',
  'studio.opt.aspect.4:5': '4:5 — طولي للخلاصة',
  'studio.opt.aspect.3:4': '3:4 — طولي',
  'studio.opt.aspect.4:3': '4:3 — عرضي',
  'studio.opt.quality': 'الجودة',
  'studio.opt.quality.hint': 'الجودة الأعلى أبطأ وأعلى كلفة.',
  'studio.opt.quality.DRAFT': 'مسودة — سريعة ورخيصة',
  'studio.opt.quality.STANDARD': 'قياسية',
  'studio.opt.quality.HIGH': 'عالية',
  'studio.opt.image.style': 'الأسلوب',
  'studio.opt.image.style.hint': 'مثلًا: واقعي كالصورة الفوتوغرافية، رسم مسطّح، دافئ ومشرق.',
  'studio.opt.video.duration': 'المدة (بالثواني)',
  'studio.opt.video.duration.hint': 'الطول الكلّي. الفيديو الأطول يُبنى من عدة مقاطع.',
  'studio.opt.video.scenes': 'عدد المشاهد',
  'studio.opt.video.scenes.hint': 'اتركه فارغًا ليقرر الكاتب.',
  'studio.opt.video.characterConsistency': 'ثبات الشخصيات',
  'studio.opt.video.characterConsistency.hint': 'تُوصَف كل شخصية مرة واحدة وتتكرر بوصفها في كل مشهد.',
  'studio.opt.video.sceneContinuity': 'تتابع المشاهد',
  'studio.opt.video.sceneContinuity.hint': 'يبدأ كل مشهد من حيث انتهى سابقه.',
  'studio.opt.video.transitions': 'الانتقالات',
  'studio.opt.video.transitions.hint': 'كيف ينتقل المشهد إلى الذي يليه.',
  'studio.opt.video.transitions.AUTO': 'تلقائية',
  'studio.opt.video.transitions.CUT': 'قطع مباشر',
  'studio.opt.video.transitions.FADE': 'تلاشٍ',
  'studio.opt.video.transitions.DISSOLVE': 'تداخل',
  'studio.opt.video.pace': 'الإيقاع',
  'studio.opt.video.pace.hint': 'سرعة تتابع المشاهد.',
  'studio.opt.video.pace.SLOW': 'بطيء',
  'studio.opt.video.pace.NORMAL': 'عادي',
  'studio.opt.video.pace.FAST': 'سريع',
  'studio.opt.video.keepScriptOrder': 'إبقاء ترتيب النص',
  'studio.opt.video.keepScriptOrder.hint': 'الجمل المنطوقة والنص على الشاشة يتبعان ترتيبك مشهدًا بمشهد.',
  'studio.opt.video.sound': 'الصوت',
  'studio.opt.video.sound.hint': 'الصوت الخلفي غير صوت المتحدث.',
  'studio.opt.video.sound.NONE': 'بلا',
  'studio.opt.video.sound.AMBIENT': 'أصوات المكان',
  'studio.opt.video.sound.MUSIC': 'موسيقى',
  'studio.opt.video.sound.AMBIENT_MUSIC': 'أصوات المكان وموسيقى',
  'studio.opt.audio.gender': 'الصوت',
  'studio.opt.audio.gender.hint': 'صوت رجل أو امرأة، أو أيّهما.',
  'studio.opt.audio.gender.ANY': 'أيّهما',
  'studio.opt.audio.gender.MALE': 'رجل',
  'studio.opt.audio.gender.FEMALE': 'امرأة',
  'studio.opt.audio.voice': 'معرّف الصوت (اختياري)',
  'studio.opt.audio.voice.hint': 'اسم الصوت لدى المزوّد. إن ملأته فهو يغلب الاختيار أعلاه.',
  'studio.opt.audio.speed': 'السرعة',
  'studio.opt.audio.speed.hint': '1 تعني كلامًا عاديًّا، وما دونه أبطأ.',
  'studio.opt.audio.tone': 'نبرة الصوت',
  'studio.opt.audio.tone.hint': 'الإحساس الذي يُلقى به النص.',
  'studio.opt.audio.tone.NEUTRAL': 'محايدة',
  'studio.opt.audio.tone.WARM': 'دافئة',
  'studio.opt.audio.tone.ENERGETIC': 'حيوية',
  'studio.opt.audio.tone.CALM': 'هادئة',
  'studio.opt.audio.tone.SERIOUS': 'جادّة',
  'studio.preset.label': 'الخيارات المحفوظة',
  'studio.preset.none': 'ابدأ من القيم الافتراضية',
  'studio.preset.save': 'حفظ هذه الخيارات',
  'studio.preset.namePrompt': 'اسم هذه المجموعة من الخيارات',
  'studio.preset.delete': 'حذف',
  'studio.preset.deleteConfirm': 'حذف هذه المجموعة المحفوظة من الخيارات؟',
  'studio.preset.saved': 'تم الحفظ.',
  'studio.route.title': 'ما الذي سيعمل',
  'studio.route.checking': 'جارٍ التحقق من الموصّلات التي ستُستخدم…',
  'studio.route.uses': 'سيُستخدم: {name}',
  'studio.route.missing': 'لا يوجد موصّل لهذه الخطوة — {reason}',
  'studio.route.missingNoReason': 'لم يُضبط أي موصّل لهذه الخطوة.',
  'studio.route.fix': 'فتح إعدادات الموصّلات',
  'studio.route.failed': 'تعذّر التحقق من الموصّلات. يمكنك البدء مع ذلك.',
  'studio.step.POST_TEXT': 'نص المنشور',
  'studio.step.IMAGE_PROMPT': 'برومت الصورة',
  'studio.step.VIDEO_SCRIPT': 'سيناريو وبرومت الفيديو',
  'studio.step.AUDIO_TEXT': 'النص المراد نطقه',
  'studio.step.IMAGE': 'توليد الصورة',
  'studio.step.VIDEO': 'توليد الفيديو',
  'studio.step.AUDIO': 'توليد الصوت',
  'studio.create.start': 'ابدأ',
  'studio.create.starting': 'جارٍ البدء…',
  'studio.problem.REQUEST_EMPTY': 'اكتب طلبك أولًا.',
  'studio.problem.UPLOADING': 'انتظر حتى ينتهي الرفع.',
  'studio.problem.BAD_URL': 'أحد الروابط ليس عنوان https صالحًا.',
  'studio.problem.NEEDS_IMAGE': 'هذا الهدف يحتاج صورة واحدة على الأقل: ارفع صورة أو الصق رابطًا.',
  'studio.problem.EXACT_TEXT_MISSING': 'اكتب الكلمات التي يجب أن تظهر في الصورة بالضبط.',
  'studio.project.goal': 'الهدف',
  'studio.project.postTextHeading': 'نص المنشور',
  'studio.project.promptHeading': 'البرومت الخاص بالوسائط',
  'studio.produce.title': 'الخطوة التالية',
  'studio.produce.hint': 'تُبنى من البرومت أعلاه. اعتمده أو اطلب تعديله أولًا إن لم يكن مناسبًا.',
  'studio.produce.IMAGE': 'إنشاء الصورة',
  'studio.produce.VIDEO': 'إنشاء الفيديو',
  'studio.produce.AUDIO': 'إنشاء الصوت',
  'studio.produce.connector': 'الموصّل',
  'studio.produce.auto': 'تلقائي — تختاره المنصة',
  'studio.produce.working': 'جارٍ البدء…',
  'studio.produce.videoCost': 'يحاسبك المزوّد على الفيديو بالثانية من المقطع.',
  'studio.produce.started': 'بدأ التنفيذ — ستظهر النتيجة ضمن النتائج أدناه.',
  /* أين يُستخدم الموصّل (الخانات). */
  'studioConnectors.slot.title': 'أين يُستخدم هذا الموصّل',
  'studioConnectors.slot.hint':
    'يمكن لعدة موصّلات من النوع نفسه أن يكون كلٌّ منها الافتراضي لاستخدام مختلف. اترك القائمة فارغة لتعني أي استخدام.',
  'studioConnectors.slot.purposes': 'أغراض الكتابة',
  'studioConnectors.slot.purpose.POST_TEXT': 'المنشورات',
  'studioConnectors.slot.purpose.IMAGE_PROMPT': 'برومتات الصور',
  'studioConnectors.slot.purpose.VIDEO_SCRIPT': 'سيناريوهات الفيديو',
  'studioConnectors.slot.purpose.AUDIO_TEXT': 'النص المنطوق',
  'studioConnectors.slot.videoInputs': 'مدخل الفيديو',
  'studioConnectors.slot.videoInput.TEXT': 'من نص',
  'studioConnectors.slot.videoInput.IMAGE': 'من صورة',
  'studioConnectors.slot.aspects': 'نسب العرض إلى الارتفاع',
  'studioConnectors.slot.quality': 'مستوى الجودة',
  'studioConnectors.slot.qualityAny': 'أي مستوى',
  'studioConnectors.slot.maxClip': 'أطول مقطع واحد (بالثواني)',
  'studioConnectors.slot.maxClipHint': 'اتركه فارغًا إن كان مجهولًا. الفيديو الأطول يُقسَّم إلى مقاطع بهذا الطول.',
  'studioConnectors.slot.extension': 'يمكنه تمديد مقطع سابق، فيكون الفيلم الأطول سلسلة',
  'studioConnectors.tag.maxClip': 'حتى {seconds} ث',
  'studioConnectors.tag.extension': 'يمدّد المقاطع',
  'studioConnectors.slot.none': 'أي استخدام',
  /* النشر من نتيجة أو مشروع: منشور جديد، أو إضافة إلى منشور مجدول. */
  'studio.publish.button': 'نشر',
  'studio.publish.newPost': 'منشور جديد',
  'studio.publish.newPostHint': 'يفتح المحرّر وقد مُلئ بالنص والملفات.',
  'studio.publish.attach': 'إرفاق بمنشور مجدول',
  'studio.publish.attachHint': 'أضف الملفات إلى منشور مجدول مسبقًا.',
  'studio.publish.attachNeedsMedia': 'يلزم صورة أو فيديو.',
  'studio.attach.title': 'اختر منشورًا مجدولًا',
  'studio.attach.videoRule': 'لا يُرفق الفيديو إلا بمنشور بلا ملفات أخرى، ويجب أن يكون الملف الوحيد.',
  'studio.attach.none': 'لا توجد منشورات مجدولة.',
  'studio.attach.mediaCount': '{count} ملف(ات)',
  'studio.attach.notEditable': 'هذا المنشور يعيد نشر منشور لغيرك ولا يمكن تعديله.',
  'studio.attach.block.NOTHING_SELECTED': 'لا شيء للإرفاق.',
  'studio.attach.block.POST_HAS_VIDEO': 'لهذا المنشور فيديو بالفعل؛ لا يمكن إضافة شيء.',
  'studio.attach.block.VIDEO_NEEDS_EMPTY_POST': 'الفيديو يحتاج منشورًا بلا ملفات أخرى.',
  'studio.attach.block.TOO_MANY': 'يتّسع المنشور لعشرة ملفات كحد أقصى.',
  'studio.attach.confirm': 'إضافة {count} ملف(ات) إلى هذا المنشور المجدول؟',
  'studio.attach.here': 'أرفق هنا',
  'studio.attach.done': 'أُضيفت الملفات إلى المنشور المجدول.',
  'studio.attach.notRecorded': 'أُضيفت الملفات، لكن تعذّر إبلاغ المشروع بذلك. لن يعرض المشروع هذا المنشور.',
  'studio.attach.openScheduled': 'فتح المنشورات المجدولة',
  'flow.projectRecordFailed': 'وُضع المنشور في الطابور، لكن تعذّر تحديث مشروع الاستوديو به.',
  'videoPlan.open': 'تخطيط فيديو أطول',
  'videoPlan.intro':
    'نماذج الفيديو تُنتج مقاطع من ثوانٍ قليلة. الفيديو الأطول يحتاج خطة: يُقسَّم النصّ إلى لقطات، تراجع وصف كل لقطة، ثم تُنتَج اللقطات واحدة تلو الأخرى — وكل لقطة طلب مدفوع.',
  'videoPlan.targetSeconds': 'المدة المطلوبة (بالثواني)',
  'videoPlan.shotEstimate': '≈ {count} لقطة',
  'videoPlan.pauseBetweenShots': 'توقّف بعد كل لقطة لأراجعها قبل المتابعة',
  'videoPlan.referenceNote': 'اللقطة الأولى تستخدم الصور المرجعية المرفقة ({count} من 3 كحدّ أقصى).',
  'videoPlan.referenceNoteNone': 'أرفق صورًا مرجعية في الأعلى إن أردت أن تبدأ اللقطة الأولى منها.',
  'videoPlan.create': 'كتابة قائمة اللقطات',
  'videoPlan.creating': 'جارٍ كتابة قائمة اللقطات… قد يستغرق ذلك نصف دقيقة.',
  'videoPlan.cancel': 'إلغاء',
  'videoPlan.title': 'خطة الفيديو',
  'videoPlan.summary': '{seconds} ثانية · {count} لقطة',
  'videoPlan.status.DRAFT': 'مسودة',
  'videoPlan.status.RUNNING': 'قيد التنفيذ',
  'videoPlan.status.PAUSED': 'متوقّفة للمراجعة',
  'videoPlan.status.DONE': 'مكتملة',
  'videoPlan.status.FAILED': 'فشلت',
  'videoPlan.shotStatus.PENDING': 'بانتظار الدور',
  'videoPlan.shotStatus.RUNNING': 'جارٍ الإنتاج',
  'videoPlan.shotStatus.DONE': 'جاهزة',
  'videoPlan.shotStatus.FAILED': 'فشلت',
  'videoPlan.shotStatus.APPROVED': 'جاهزة',
  'videoPlan.shot': 'اللقطة {number}',
  'videoPlan.timeRange': '{from}–{to} ث',
  'videoPlan.scriptExcerpt': 'سطور النصّ التي تغطّيها هذه اللقطة',
  'videoPlan.visualPrompt': 'ما تراه الكاميرا',
  'videoPlan.visualPromptHint':
    'بالإنجليزية عمدًا — نماذج الفيديو تفهم الإنجليزية أفضل. المكان، الشخصية، الحركة، الكاميرا، الإضاءة، الجوّ.',
  'videoPlan.editingLocked': 'الخطة قيد التنفيذ؛ يعود التعديل متاحًا عندما تتوقّف أو تكتمل.',
  'videoPlan.unsavedChanges': 'لديك تعديلات على اللقطات لم تُحفظ بعد.',
  'videoPlan.saveShots': 'حفظ اللقطات',
  'videoPlan.saveNote': 'تعديل لقطة يعيد إنتاجها هي وكل ما بعدها: كل لقطة لاحقة امتداد للتي قبلها.',
  'videoPlan.discardChanges': 'تجاهل التعديلات',
  'videoPlan.addShot': 'إضافة لقطة في النهاية',
  'videoPlan.removeShot': 'حذف هذه اللقطة',
  'videoPlan.approveAndStart': 'اعتماد وبدء الإنتاج',
  'videoPlan.confirmStart':
    'هل تبدأ الإنتاج؟ كل لقطة طلب مدفوع منفصل إلى موصّل الفيديو — عدد اللقطات في هذه الخطة: {count}. افتراضيًا يتوقّف بعد كل لقطة مكتملة لتراجعها.',
  'videoPlan.continue': 'متابعة إلى اللقطة التالية',
  'videoPlan.confirmContinue': 'هل تُنتج اللقطة التالية؟ هذا طلب مدفوع.',
  'videoPlan.runningNow': 'جارٍ إنتاج اللقطة {number} من {count}… قد يستغرق ذلك دقائق.',
  'videoPlan.pausedHint': 'شاهد الفيديو حتى الآن. تابع، أو أعد اللقطة الأخيرة بوصف جديد.',
  'videoPlan.doneHint': 'الفيديو كاملًا جاهز.',
  'videoPlan.videoSoFar': 'الفيديو حتى الآن',
  'videoPlan.finalVideo': 'الفيديو النهائي',
  'videoPlan.redoShot': 'إعادة هذه اللقطة',
  'videoPlan.redoHint': 'صف اللقطة من جديد؛ ستُنتَج مرة أخرى مع كل اللقطات التي بعدها.',
  'videoPlan.redoAndRun': 'إعادة وإنتاج',
  'videoPlan.fixAndRetry': 'تصحيح وإعادة المحاولة',
  'videoPlan.fixHint': 'عدّل وصف اللقطة التي فشلت ثم حاول مجددًا. هذا طلب مدفوع.',
  'videoPlan.retry': 'إعادة المحاولة',
  'videoPlan.noVideoYet': 'لا فيديو بعد — اعتمد الخطة للبدء.',
  'videoPlan.copyLink': 'نسخ الرابط',
  'videoPlan.libraryTitle': 'خطط الفيديو',
  'videoPlan.libraryEmpty': 'لا خطط فيديو بعد — خطّط واحدة من تبويب الإنشاء.',
  'videoPlan.openPlan': 'فتح',
  'videoPlan.backToList': 'العودة إلى القائمة',
  'automation.tab.studioConnectors': 'موصّلات الاستوديو',
  'studioConnectors.hint':
    'يُحفظ الموصّل كمسودة ولا يُستخدم إلا بعد أن يُنتج فحصٌ حقيقيٌّ نتيجة. ويختار الاستوديو من الموصّلات المفحوصة لكل نوع بحسب ما يعلنه كلٌّ منها (انظر «أين يُستخدم هذا الموصّل»)، ثم يرجع إلى المحدَّد كافتراضي.',
  'studioConnectors.name': 'الاسم',
  'studioConnectors.capability': 'القدرة',
  'studioConnectors.provider': 'مزوّد الخدمة',
  'studioConnectors.endpoint': 'رابط النقطة الطرفية',
  'studioConnectors.token': 'رمز الاعتماد',
  'studioConnectors.default': 'الافتراضي',
  'studioConnectors.active': 'مفعَّل',
  'studioConnectors.inactive': 'معطَّل',
  'studioConnectors.add': 'إضافة موصّل',
  'studioConnectors.tokenHint':
    'اتركه فارغًا عند التعديل للإبقاء على الرمز المخزَّن — يُعرض مرّة واحدة فقط ولا يُقرأ مجددًا.',
  'studioConnectors.setDefault': 'اجعله الافتراضي لهذه القدرة',
  'studioConnectors.saveConnector': 'حفظ الموصّل',
  'studioConnectors.capability.SCRIPT': 'نصّ',
  'studioConnectors.capability.AUDIO': 'صوت',
  'studioConnectors.capability.VIDEO': 'فيديو',
  'studioConnectors.capability.IMAGE': 'صورة',
  'studioConnectors.model': 'النموذج',
  'studioConnectors.modelHint':
    'معرّف النموذج كما يكتبه المزوّد تمامًا (وفي ElevenLabs هو معرّف الصوت). المزوّدون يوقفون معرّفات النماذج كثيرًا — إذا توقّف التوليد فجأة برسالة "not found" فضع المعرّف الحالي هنا.',
  'studioConnectors.preset': 'ابدأ من',
  'studioConnectors.presetCustom': 'مخصّص — ابدأ من الصفر',
  'studioConnectors.presetHint':
    'اختر مزوّدًا من القائمة وستُعبّأ كل الحقول تلقائيًا. لا تختر "مخصّص" إلا لمزوّد غير موجود في القائمة.',
  'studioConnectors.advanced': 'إعدادات متقدّمة — الطلب والاستجابة',
  'studioConnectors.advancedHint':
    'طريقة بناء الطلب ومكان قراءة النتيجة. القالب الجاهز يعبّئ هذا كلّه؛ لا تغيّره إلا إذا كانت وثائق المزوّد تقول غير ذلك.',
  'studioConnectors.authStyle': 'أين يوضع رمز الاعتماد',
  'studioConnectors.authStyle.BEARER': 'Authorization: Bearer',
  'studioConnectors.authStyle.HEADER': 'ترويسة مخصّصة (header)',
  'studioConnectors.authStyle.QUERY': 'مُعامِل في الرابط (query)',
  'studioConnectors.authStyle.NONE': 'بدون',
  'studioConnectors.headerName': 'اسم الترويسة (header)',
  'studioConnectors.queryName': 'اسم المُعامِل في الرابط',
  'studioConnectors.template': 'محتوى الطلب (JSON)',
  'studioConnectors.templateHint':
    'اتركه فارغًا لاستخدام العقد الخاص بهذه المنصّة. وإلا فهو الـ JSON الذي يُرسَل إلى المزوّد، مع العناصر البديلة أدناه.',
  'studioConnectors.templateInvalid': 'صيغة JSON غير صحيحة بعد: {error}',
  'studioConnectors.templateNotObject': 'يجب أن يكون محتوى الطلب كائن JSON بين قوسين: { … }',
  'studioConnectors.placeholders':
    'عناصر بديلة يمكنك استخدامها في محتوى الطلب وفي رابط النقطة الطرفية:',
  'studioConnectors.placeholder.prompt': 'الفكرة أو النص أو وصف الصورة/الفيديو',
  'studioConnectors.placeholder.model': 'قيمة حقل النموذج أعلاه',
  'studioConnectors.placeholder.feedback': 'ملاحظات التعديل (فارغة في المحاولة الأولى)',
  'studioConnectors.placeholder.audioUrl':
    'رابط الصوت المولَّد الذي يُدمج في الفيديو (قد يكون فارغًا)',
  'studioConnectors.placeholder.references': 'روابط الملفات المرجعية المرفقة، كقائمة',
  'studioConnectors.placeholder.item': 'المرجع الحالي، داخل كتلة $each فقط',
  'studioConnectors.placeholderEach': 'لإرسال عنصر لكل ملف مرجعي مرفق، استخدم $each — مثال:',
  'studioConnectors.placeholderEmpty': 'أي حقل تخرج قيمته فارغة يُحذف من الطلب بالكامل.',
  'studioConnectors.responseKind': 'ماذا يُرجع المزوّد',
  'studioConnectors.responseKind.TEXT': 'نص',
  'studioConnectors.responseKind.URL': 'رابط إلى الملف',
  'studioConnectors.responseKind.BASE64': 'الملف داخل الـ JSON (base64)',
  'studioConnectors.responseKind.BINARY': 'الملف نفسه (بدون JSON)',
  'studioConnectors.responseKind.legacy': 'العقد الخاص بهذه المنصّة',
  'studioConnectors.responsePath': 'مكان النتيجة في الاستجابة',
  'studioConnectors.responsePathHint':
    'مسار داخل ردّ الـ JSON، مثل candidates[0].content.parts[*].text أو data[0].b64_json أو [0].generated_text.',
  'studioConnectors.mimePath': 'مكان نوع الملف (اختياري)',
  'studioConnectors.mimePathHint':
    'مسار نوع الملف (MIME) في الردّ، مثل data[0].media_type. اتركه فارغًا إن لم يرسله المزوّد.',
  'studioConnectors.polling': 'نتيجة تستغرق وقتًا (متابعة الطلب)',
  'studioConnectors.pollingHint':
    'اترك هذه الحقول فارغة إلا إذا كان المزوّد يردّ بـ«بدأ التنفيذ» فقط ويحتاج أن نسأله مرارًا حتى تجهز النتيجة (Veo ومعظم نماذج الفيديو).',
  'studioConnectors.pollPath': 'مكان معرّف العملية في الردّ الأول',
  'studioConnectors.pollUrl': 'رابط المتابعة، ويحتوي {{operation}}',
  'studioConnectors.pollDonePath': 'مكان علامة الاكتمال في ردّ المتابعة',
  'studioConnectors.authOnResultFetch': 'أرسل المفتاح عند تنزيل النتيجة',
  'studioConnectors.pollDoneValue': 'كلمة الحالة التي تعني الاكتمال (اختياري)',
  'studioConnectors.pollFailedValues': 'كلمات الحالة التي تعني الفشل، مفصولة بفواصل',
  'studioConnectors.pollDoneValueHint':
    'اتركه فارغًا إذا كان مسار الاكتمال يصبح true (مثل Google). أما إذا كان حقل حالة مثل output.task_status في علي بابا فاكتب هنا SUCCEEDED وفي الحقل التالي FAILED,CANCELED,UNKNOWN.',
  'studioConnectors.extraHeaders': 'ترويسات إضافية',
  'studioConnectors.extraHeadersHint':
    'ترويسات ثابتة تُرسل مع كل طلب، بصيغة JSON. لا تضع مفتاح الاعتماد هنا؛ له حقله المشفّر الخاص.',
  'studioConnectors.extraHeadersInvalid': 'اكتب كائن JSON قيمه نصوص، أو اتركه فارغًا.',
  'studioConnectors.edit': 'تعديل الموصّل',
  'studioConnectors.editing': 'تعديل {name} — اترك رمز الاعتماد فارغًا ليبقى المحفوظ',
  'studioConnectors.verification': 'الفحص',
  'studioConnectors.verification.UNTESTED': 'لم يُفحص',
  'studioConnectors.verification.PASSED': 'ناجح',
  'studioConnectors.verification.FAILED': 'فشل',
  'studioConnectors.defaultNeedsTest': 'لا يمكن جعل الموصّل افتراضيًا إلا بعد نجاح فحصه.',
  'studioConnectors.draftHint':
    'يُحفظ كمسودة: لا يمكن استخدامه ولا جعله افتراضيًا حتى يُنتج فحصٌ حقيقيٌّ نتيجة. افحصه من صفّه بعد الحفظ.',
  'studioConnectors.test.button': 'فحص',
  'studioConnectors.test.title': 'فحص هذا الموصّل',
  'studioConnectors.test.hint':
    'يُنفّذ عملية توليد حقيقية واحدة عبر هذا الموصّل بطلب صغير ويعرض ما أجاب به المزوّد، لترى بالضبط ما تكتبه في مسار الاستجابة. ينجح الفحص فقط إذا عادت نتيجة (نص أو صورة أو فيديو أو صوت).',
  'studioConnectors.test.savedOnly': 'يستخدم الفحص الموصّل كما حُفظ آخر مرة — احفظ تعديلاتك أولًا.',
  'studioConnectors.test.prompt': 'نص الفحص (اختياري)',
  'studioConnectors.test.promptPlaceholder': 'اتركه فارغًا لاستخدام طلب قصير مدمج',
  'studioConnectors.test.reference': 'صورة مرجعية (اختياري، لموصّلات تحويل الصورة إلى فيديو أو تعديل الصور)',
  'studioConnectors.test.videoCost':
    'يحاسب المزوّد الفيديو بالثانية من المخرجات. سيُنتج هذا الفحص مقطعًا قصيرًا (بضع ثوانٍ) وستُحتسب تكلفته على حسابك لدى المزوّد. هل تريد المتابعة؟',
  'studioConnectors.test.videoCostNote':
    'موصّلات الفيديو تُحاسَب بالثانية. يطلب الفحص مقطعًا قصيرًا جدًا، لكنه يبقى استدعاءً حقيقيًا مدفوعًا.',
  'studioConnectors.test.run': 'تشغيل الفحص',
  'studioConnectors.test.running': 'جارٍ الفحص…',
  'studioConnectors.test.runningShort': 'جارٍ استدعاء المزوّد… يستغرق عادةً بضع ثوانٍ.',
  'studioConnectors.test.runningVideo': 'بانتظار أن يُنهي المزوّد إنتاج الفيديو — قد يستغرق ذلك عدة دقائق. يمكنك مغادرة الصفحة؛ ستجد النتيجة هنا عند الانتهاء.',
  'studioConnectors.test.lastTested': 'آخر فحص {when}',
  'studioConnectors.test.failed': 'فشل الفحص: {error}',
  'studioConnectors.test.passed': 'نجح الفحص — الموصّل جاهز للاستخدام الآن. هذا ما أنتجه:',
  'studioConnectors.test.resultImage': 'نتيجة الفحص',
  'studioConnectors.test.providerResponse': 'رد المزوّد',
  'studioConnectors.test.providerResponseHint':
    'ردّ المزوّد على آخر فحص، بعد إزالة المفاتيح واختصار القيم الطويلة. اكتب مسار الاستجابة اعتمادًا على ما تراه هنا.',
  'studioConnectors.test.providerRequest': 'الطلب المُرسل',
  'studioConnectors.test.providerRequestHint':
    'ما أرسله آخر فحص: العنوان بلا أي مفتاح، وأسماء الترويسات، والمحتوى. قارنه بتوثيق المزوّد.',
  'studioConnectors.repair.title': 'إصلاح تلقائي',
  'studioConnectors.repair.hint':
    'يقرأ نموذج نصي ما أرسله الفحص وما ردّ به المزوّد ثم يقترح تصحيحات. لا يتغيّر شيء حتى تطبّقها بنفسك. والمفتاح لا يُعرض عليه أبدًا.',
  'studioConnectors.repair.target': 'الموصّل الجاري إصلاحه: {name}',
  'studioConnectors.repair.usedModel': 'الاقتراح من الموصّل النصي "{name}"',
  'studioConnectors.repair.videoWarn':
    'هذا موصّل فيديو: كل إعادة فحص اتصال حقيقي يُحاسَب بالثانية. إعادة المحاولة التلقائية لا تتجاوز مرتين.',
  'studioConnectors.repair.videoConfirm':
    'كل إعادة فحص لموصّل الفيديو تُنتج مقطعًا وتُحتسب على حسابك لدى المزوّد. هل تريد المتابعة؟',
  'studioConnectors.repair.note': 'ملاحظة للنموذج (اختياري)',
  'studioConnectors.repair.notePlaceholder':
    'مثلًا: الرد يحتوي قائمة "images"، أو التوثيق يقول إن الترويسة x-api-key',
  'studioConnectors.repair.auto': 'طبّق تلقائيًا وأعد المحاولة حتى',
  'studioConnectors.repair.autoCount': 'عدد المحاولات التلقائية',
  'studioConnectors.repair.times': 'مرات',
  'studioConnectors.repair.run': 'إصلاح تلقائي',
  'studioConnectors.repair.again': 'جرّب إصلاحًا آخر',
  'studioConnectors.repair.round': 'المحاولة {n} من {max}',
  'studioConnectors.repair.asking': 'جارٍ سؤال النموذج النصي…',
  'studioConnectors.repair.applying': 'جارٍ حفظ التعديلات…',
  'studioConnectors.repair.retesting': 'جارٍ الفحص من جديد…',
  'studioConnectors.repair.wrong': 'ما الخطأ:',
  'studioConnectors.repair.confidence': 'درجة الثقة: {level}',
  'studioConnectors.repair.confidence.high': 'عالية',
  'studioConnectors.repair.confidence.medium': 'متوسطة',
  'studioConnectors.repair.confidence.low': 'منخفضة',
  'studioConnectors.repair.changes': 'التعديلات المقترحة',
  'studioConnectors.repair.dropped': 'تُركت (لم تُطبَّق)',
  'studioConnectors.repair.noChanges':
    'لم يجد النموذج ما يُسمح له بتغييره هنا. عدّل الإعدادات يدويًا، أو أضف ملاحظة وحاول مرة أخرى.',
  'studioConnectors.repair.apply': 'تطبيق وإعادة الفحص',
  'studioConnectors.repair.passed':
    'تم الإصلاح: نجح الفحص بعد {n} تعديل. الموصّل جاهز للاستخدام الآن.',
  'studioConnectors.repair.stopped': 'توقّف بعد {n} محاولة: ما زال الفحص يفشل. {error}',
  'studioConnectors.repair.failedAgain': 'ما زال الفحص يفشل: {error} يمكنك طلب إصلاح آخر.',
  'studioConnectors.repair.history': 'المحاولات السابقة ({n})',
  'studioConnectors.repair.attemptPassed': 'نجح الفحص بعد هذا التعديل.',
  'studioConnectors.repair.attemptFailed': 'ما زال الفحص يفشل: {error}',
  'studioConnectors.tree.title': 'أشِر إلى الحقل في الرد',
  'studioConnectors.tree.hint': 'اضغط على قيمة في الرد لاستخدام مسارها.',
  'studioConnectors.tree.anyItem': 'أي عنصر (اكتب [*] بدل الموضع)',
  'studioConnectors.tree.selected': 'المسار:',
  'studioConnectors.tree.useAs': 'استخدمه كـ:',
  'studioConnectors.tree.use.result': 'مسار النتيجة',
  'studioConnectors.tree.use.operation': 'مسار معرّف العملية',
  'studioConnectors.tree.use.done': 'مسار علامة الاكتمال',
  'studioConnectors.tree.use.mime': 'مسار نوع الملف (MIME)',
  'studioConnectors.tree.used': 'وُضع في النموذج: {path}. اضغط حفظ لتثبيته.',
  'studioConnectors.tree.readOnly': 'افتح تعديل هذا الموصّل لوضع المسار في نموذجه.',
  'studioConnectors.tree.cannotAddress':
    'لا يمكن كتابة هذا الاسم كمسار. اختر قيمة داخله، أو اكتب المسار يدويًا.',
  'studioConnectors.tree.noPosition': 'لا يوجد موضع في هذا المسار، فلا يغيّر "أي عنصر" شيئًا.',
  'studioConnectors.tree.notJson': 'الرد ليس JSON (أو قُطع)، فلا يوجد ما يمكن الإشارة إليه.',
  'studioConnectors.tree.items': '{n} عناصر',
  'studioConnectors.tree.fields': '{n} حقول',
  'studioConnectors.tree.expand': 'توسيع',
  'studioConnectors.tree.collapse': 'طيّ',
  'studioConnectors.tree.mark.result': 'النتيجة',
  'studioConnectors.tree.mark.operation': 'معرّف العملية',
  'studioConnectors.tree.mark.done': 'علامة الاكتمال',
  'studioConnectors.tree.mark.mime': 'MIME',
  'studioConnectors.catalogue.button': 'تحديث من الكتالوج',
  'studioConnectors.catalogue.hint':
    'يقارن هذا الموصّل بعنصر الكتالوج "{label}". علّم ما تريد أخذه؛ يُوضع في النموذج أدناه ولا يُحفظ إلا عند الضغط على حفظ (وهذا يعيد ضبط الفحص).',
  'studioConnectors.catalogue.same': 'هذا الموصّل مطابق لعنصر الكتالوج الخاص به.',
  'studioConnectors.catalogue.customised': 'يُغيَّر غالبًا عن قصد، لذلك لم يُعلَّم.',
  'studioConnectors.catalogue.apply': 'تطبيق {n} محدد على النموذج',
  'studioConnectors.catalogue.applied': 'طُبّق على النموذج. راجعه ثم اضغط حفظ.',
  'studioConnectors.field.requestTemplate': 'قالب الطلب',
  'studioConnectors.field.responsePath': 'مسار النتيجة',
  'studioConnectors.field.responseKind': 'نوع الرد',
  'studioConnectors.field.responseMimePath': 'مسار نوع الملف (MIME)',
  'studioConnectors.field.pollPath': 'مسار معرّف العملية',
  'studioConnectors.field.pollUrlTemplate': 'رابط المتابعة',
  'studioConnectors.field.pollDonePath': 'مسار علامة الاكتمال',
  'studioConnectors.field.pollDoneValue': 'كلمة الاكتمال',
  'studioConnectors.field.pollFailedValues': 'كلمات الفشل',
  'studioConnectors.field.authOnResultFetch': 'إرسال المفتاح عند جلب النتيجة',
  'studioConnectors.field.extraHeaders': 'ترويسات إضافية',
  'studioConnectors.field.authStyle': 'مكان وضع المفتاح',
  'studioConnectors.field.authParamName': 'اسم الترويسة أو المعامل',
  'studioConnectors.field.endpointUrl': 'رابط نقطة الاتصال',
  'studioConnectors.field.model': 'النموذج',
};

export const dictionaries = { en, ar } as const;
export type Language = keyof typeof dictionaries;
